/**
 * Arte pixel-art do escritório 2D — gerada por código, não carregada de ficheiros.
 *
 * Porquê desenhar em vez de importar sprites? Duas razões, e as duas importam:
 *  1. LEGAL — clean-room a sério. Os tilesets de escritório bonitos (Gather,
 *     WorkAdventure, LimeZu) são proprietários ou têm licenças com pegadilhas.
 *     Um pixel desenhado aqui é nosso, sem atribuição nem risco.
 *  2. LEVE — zero bytes de imagem no bundle e zero pedidos de rede. Cada textura
 *     nasce num `<canvas>` e entra no gestor de texturas do Phaser em runtime.
 *
 * O truque de pixel-art: autoro tudo numa grelha de "pixels de arte" pequena e
 * pinto cada pixel como um bloco S×S (S=2). Com `pixelArt: true` no Phaser
 * (nearest-neighbor, sem suavização) as bordas ficam nítidas como num 16 bits.
 *
 * As técnicas de qualidade aplicadas em TODA a arte (as que separam "objeto
 * pobre" de "objeto de jogo a sério"):
 *   • LUZ FIXA no canto superior-esquerdo — topos e faces esquerdas ganham um
 *     brilho, direitas e baixos ganham sombra. Consistente em tudo → coesão.
 *   • OUTLINE SELETIVO — contorno escuro em baixo/direita, ausente/claro em cima.
 *   • DITHERING — xadrez de 1px para suavizar rampas de sombra sem mais paleta.
 *   • PAREDES 2.5D — capa iluminada no topo + face vertical sombreada → o piso
 *     ganha altura e sensação de 3D sem sair do 2D.
 */

import type { Facing, FloorKind, Gender, PropKind } from "@greenvoice/shared";
import { TILE } from "@greenvoice/shared";
import type PhaserT from "phaser";
import { drawArcade } from "./officeArcade";
import { drawGameTable } from "./officeGameTable";
import { drawGravel, EDGE_COUNT } from "./officePath";
import { drawRug } from "./officeRug";

/** Tamanho do "pixel de arte" em pixels de ecrã. 16 art × 2 = 32 = um tile. */
const S = 2;

/** Direcções que um sprite conhece — as mesmas do protocolo (`Facing`). */
const DIRS = ["down", "up", "left", "right"] as const;

/** Camisola do avatar por pessoa — a mesma paleta determinística do resto da app. */
const SHIRTS = ["#22b364", "#2f9be0", "#8b5cf6", "#ec6aa1", "#f0a53a", "#e8574f", "#17b6a3", "#6d78e6"] as const;

/** Cores de cabelo, para dois colegas com a mesma camisola não serem gémeos. */
const HAIRS = ["#2b2018", "#4a3524", "#71512f", "#141418", "#8a6a3a", "#8a8f96", "#b5652f"] as const;

/** Tons de pele — variação humana, cada um com a sua sombra/brilho derivados. */
const SKINS = ["#f4c9a3", "#e6ad7d", "#cd8b57", "#a3663c", "#7a4a29"] as const;

/** Cores de calças — sóbrias, de escritório. */
const PANTS = ["#38414d", "#2b3a52", "#4a3a2c", "#3a3f47", "#503a4a"] as const;

// ── Pincel ──────────────────────────────────────────────────

interface Brush {
  /** Rectângulo em coordenadas de arte. */
  r(x: number, y: number, w: number, h: number, color: string): void;
  /** Xadrez de 1px entre duas cores — suaviza rampas de sombra (dithering). */
  dither(x: number, y: number, w: number, h: number, a: string, b: string): void;
}

/** Escurece (f<1) ou clareia (f>1) um hex `#rrggbb`, com clamp a 0–255. */
function shade(hex: string, f: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  const r = clamp(((n >> 16) & 0xff) * f);
  const g = clamp(((n >> 8) & 0xff) * f);
  const b = clamp((n & 0xff) * f);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/** Mistura linear de dois hex (t=0 → a, t=1 → b). */
function mix(a: string, b: string, t: number): string {
  const na = Number.parseInt(a.slice(1), 16);
  const nb = Number.parseInt(b.slice(1), 16);
  const lerp = (x: number, y: number) => Math.round(x + (y - x) * t);
  const r = lerp((na >> 16) & 0xff, (nb >> 16) & 0xff);
  const g = lerp((na >> 8) & 0xff, (nb >> 8) & 0xff);
  const bl = lerp(na & 0xff, nb & 0xff);
  return `#${((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1)}`;
}

/**
 * Cria um canvas, dá-lhe um pincel e regista a textura (idempotente).
 *
 * `detail` é a ALAVANCA DE RESOLUÇÃO: com detail=1 cada pixel de arte ocupa S×S
 * pixels de ecrã (o 16-bit clássico); com detail=2 a MESMA textura tem o dobro da
 * grelha de arte (`wArt*2 × hArt*2`) mas cada célula ocupa só S/2 — a PEGADA NO
 * ECRÃ é idêntica (`wArt*S × hArt*S`), muda só a densidade de pixels. É assim que
 * os "heróis" (secretária, CRT, plantas, avatares, candeeiros) ganham detalhe fino
 * sem mexer um único pixel na geometria/colisão testada de `space.ts`: o chão e as
 * paredes ficam em detail=1, os objetos que o olho fixa sobem a detail=2.
 */
function texture(
  scene: PhaserT.Scene,
  key: string,
  wArt: number,
  hArt: number,
  draw: (b: Brush) => void,
  detail = 1,
  mirror = false,
): void {
  if (scene.textures.exists(key)) return;
  const canvas = document.createElement("canvas");
  canvas.width = wArt * S;
  canvas.height = hArt * S;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  if (mirror) {
    // A vista da direita é a da esquerda ao espelho — autora-se uma, obtêm-se duas.
    ctx.translate(wArt * S, 0);
    ctx.scale(-1, 1);
  }
  const brush = makeBrush(ctx, S / detail);
  draw(brush);
  scene.textures.addCanvas(key, canvas);
}

/** Pincel que pinta em células de `px` pixels de ecrã (px=S no detalhe normal). */
function makeBrush(ctx: CanvasRenderingContext2D, px: number = S): Brush {
  return {
    r(x, y, w, h, color) {
      ctx.fillStyle = color;
      ctx.fillRect(x * px, y * px, w * px, h * px);
    },
    dither(x, y, w, h, a, b) {
      for (let j = 0; j < h; j += 1) {
        for (let i = 0; i < w; i += 1) {
          ctx.fillStyle = (i + j) & 1 ? a : b;
          ctx.fillRect((x + i) * px, (y + j) * px, px, px);
        }
      }
    },
  };
}

// ── Chão (um por tipo de sala) ──────────────────────────────

export function floorKey(kind: FloorKind, alt = false): string {
  return `floor:${kind}${alt ? ":b" : ""}`;
}

/** Cada tipo de piso: uma tábua de 16×16 desenhada com carácter próprio. */
function drawFloor(b: Brush, kind: FloorKind, alt: boolean): void {
  if (kind === "wood" || kind === "lounge") {
    // Madeira com MUITO mais contraste: tábua base, juntas fundas e escuras, aresta
    // de cima bem clara e veios em dois tons → a prancha "salta" do chão escurecido.
    const base = kind === "lounge" ? "#c39a63" : "#a8794e";
    const seam = shade(base, 0.62); // junta bem mais funda (era 0.8)
    const seamHi = shade(base, 1.24); // fio de luz por baixo da junta (bisel)
    const grain = shade(base, alt ? 0.84 : 1.14);
    const grainSoft = shade(base, alt ? 0.9 : 1.06);
    b.r(0, 0, 16, 16, alt ? shade(base, 0.9) : base);
    for (let y = 0; y < 16; y += 4) {
      b.r(0, y, 16, 1, seam); // juntas das tábuas (fundas)
      b.r(0, y + 1, 16, 1, seamHi); // bisel iluminado logo abaixo → relevo
    }
    b.r(0, 0, 16, 1, shade(base, 1.3)); // luz forte na aresta de cima
    b.r(alt ? 9 : 3, 1, 6, 1, grain); // veios (alto contraste)
    b.r(alt ? 2 : 10, 5, 5, 1, grainSoft);
    b.r(5, 6, 7, 1, grain);
    b.r(6, 9, 5, 1, grainSoft);
    b.r(alt ? 11 : 1, 10, 4, 1, grain);
    b.r(alt ? 3 : 8, 13, 5, 1, grainSoft);
    b.r(2, 14, 3, 1, shade(base, 0.72)); // nó escuro da madeira
    return;
  }
  if (kind === "carpet") {
    const base = alt ? "#354a5a" : "#3e5666";
    b.r(0, 0, 16, 16, base);
    b.dither(0, 0, 16, 16, shade(base, 1.12), shade(base, 0.9)); // trama contrastada
    b.r(0, 0, 16, 1, shade(base, 1.24)); // luz de cima
    b.r(0, 0, 1, 16, shade(base, 1.16)); // luz da esquerda
    b.r(0, 15, 16, 1, shade(base, 0.74)); // sombra em baixo → borda de tufo
    b.r(15, 0, 1, 16, shade(base, 0.8));
    return;
  }
  if (kind === "tile") {
    // Cerâmica em xadrez: a variante 'alt' troca a cor de arranque → tabuleiro.
    const light = "#d7dbe0";
    const dark = "#c3c9d0";
    const grout = "#a9b0b8";
    b.r(0, 0, 16, 16, alt ? dark : light);
    b.r(0, 0, 16, 1, "#e7eaed"); // brilho de cima
    b.r(0, 15, 16, 1, grout); // junta em baixo
    b.r(15, 0, 1, 16, grout); // junta à direita
    b.r(2, 2, 2, 2, alt ? "#cdd2d8" : "#e1e4e8"); // reflexo do vidrado
    return;
  }
  if (kind === "asphalt") {
    // Alcatrão do parque de estacionamento: cinza-carvão escuro, grão irregular de
    // gravilha e um leve pontilhado mais claro → superfície fosca, nunca lisa. As
    // linhas brancas NÃO se pintam aqui (são um prop `parkingLines` transparente por
    // cima), para o alcatrão ladrilhar limpo por baixo dos carros e das casas.
    const base = alt ? "#2a2e33" : "#2c3035";
    b.r(0, 0, 16, 16, base);
    b.dither(0, 0, 16, 16, shade(base, 1.12), shade(base, 0.86)); // grão de gravilha
    b.r(0, 0, 16, 1, shade(base, 1.14)); // aresta de cima levemente iluminada
    b.r(0, 15, 16, 1, shade(base, 0.82)); // sombra em baixo
    b.r(alt ? 4 : 11, 3, 2, 1, shade(base, 1.2)); // pedrinhas claras dispersas
    b.r(alt ? 9 : 2, 9, 1, 1, shade(base, 1.24));
    b.r(6, 12, 2, 1, shade(base, 0.72)); // mancha de asfalto mais escura
    return;
  }
  if (kind === "concrete") {
    // Betão em bruto do bunker. É o `lobby` ao contrário de propósito: lá é
    // mármore polido e claro para a recepção parecer cara, aqui é laje cinzenta
    // e fria para o subsolo parecer um sítio a que se DESCE. Se fosse o mesmo
    // chão, entrar pela escotilha não mudava nada — e a sala inteira existe para
    // essa mudança se sentir.
    //
    // Três coisas fazem o betão ler como betão e não como cinzento: a junta de
    // dilatação (o quadriculado grande, dado pela sombra em dois lados do tile),
    // o grão a dois tons muito próximos (poeira, não textura) e a AUSÊNCIA de
    // brilho — o único fio claro é a aresta de cima, e é fraco.
    const base = alt ? "#4a4f56" : "#4f545b";
    b.r(0, 0, 16, 16, base);
    b.dither(0, 0, 16, 16, shade(base, 1.07), shade(base, 0.94)); // grão de poeira
    b.r(0, 0, 16, 1, shade(base, 1.16)); // aresta de cima (a única luz)
    b.r(0, 15, 16, 1, shade(base, 0.74)); // junta de dilatação (baixo)
    b.r(15, 0, 1, 16, shade(base, 0.78)); // junta de dilatação (direita)
    b.r(alt ? 4 : 10, 4, 3, 1, shade(base, 0.84)); // manchas de humidade
    b.r(alt ? 9 : 3, 11, 2, 1, shade(base, 0.88));
    b.r(alt ? 2 : 12, 7, 1, 1, shade(base, 1.14)); // gravilha à vista
    return;
  }
  // lobby — mármore/betão polido com veios e grandes lajes
  const base = alt ? "#a7adb6" : "#b0b6bf";
  b.r(0, 0, 16, 16, base);
  b.r(0, 0, 16, 1, shade(base, 1.08));
  b.r(0, 0, 1, 16, shade(base, 1.05));
  b.r(0, 15, 16, 1, shade(base, 0.9)); // junta das lajes
  b.r(15, 0, 1, 16, shade(base, 0.9));
  b.r(alt ? 3 : 9, 2, 5, 1, shade(base, 1.06)); // veio de mármore
  b.r(alt ? 8 : 2, 10, 4, 1, shade(base, 0.94));
}

// ── Paredes (2.5D) ──────────────────────────────────────────

export const WALL_TOP = "wall:top";
export const WALL_FACE = "wall:face";
export const WALL_TOP_SLIM = "wall:top-slim";

/** Capa de topo de uma parede vista de cima (paredes sem sala em baixo). */
function drawWallTop(b: Brush): void {
  const base = "#5b6675";
  b.r(0, 0, 16, 16, base);
  b.r(0, 0, 16, 1, shade(base, 1.28)); // aresta iluminada (cima)
  b.r(0, 0, 1, 16, shade(base, 1.16)); // aresta iluminada (esq)
  b.r(0, 15, 16, 1, shade(base, 0.7)); // sombra (baixo)
  b.r(15, 0, 1, 16, shade(base, 0.78)); // sombra (dir)
  b.dither(3, 4, 10, 8, shade(base, 1.04), base); // grão de betão
}

/**
 * A MESMA capa, mas de uma parede FINA — a que se vê num tramo vertical.
 *
 * *"todas as paredes do office 2d que estão na vertical, vamos deixar elas mais
 * finas, pois estão muito grossas"*. E estavam: vista de cima, uma parede vertical
 * enchia o tile de lado a lado, 32px de betão a cortar o escritório ao meio. Uma
 * divisória a sério tem metade disso.
 *
 * SEGUNDA VOLTA — *"as paredes verticais ainda estão muito grossas"*. A primeira
 * encolheu a pedra de 16 para 10 art e pintou o resto do tile de quase-preto opaco,
 * a fingir de sombra. Não afinou NADA, e a folha de prova diz porquê: uma mancha
 * escura chapada não se lê como sombra, lê-se como MAIS PAREDE. Com margem opaca,
 * a pedra a 10, a 8 ou a 6 art dá exactamente a mesma barra de 32px ao olho — o que
 * define a grossura percebida é a largura do que é opaco, não a da pedra.
 *
 * Por isso a sombra passou a ser TRANSLÚCIDA, e o varrimento das paredes pinta por
 * baixo destes tiles o chão dos vizinhos (`OfficeSpace`, meia tile de cada lado).
 * A madeira e o alcatifa continuam a ver-se por baixo, escurecidos: aí sim é sombra,
 * e a parede lê-se com os 12px de pedra que tem. O que a `Brush` já permitia sem
 * mudar nada — o `fillStyle` do canvas aceita `rgba()` como aceita `#rrggbb`.
 *
 * A colisão continua a ser o tile inteiro, como em qualquer grelha. É por isso que
 * o degradê arranca FUNDO encostado à pedra: a faixa mais escura é oclusão de canto,
 * e um jogador que encoste ali lê "estou colado ao rodapé", não "há chão livre".
 *
 * As duas margens não são iguais. A luz desta arte vem de cima-esquerda (é o que a
 * `drawWallTop` diz nas suas arestas), logo a sombra projectada cai para a DIREITA:
 * seis passos desse lado, quatro do lado do contacto. Cada uma esbate para fora —
 * o mais escuro encosta à pedra — senão a parede ficava com um contorno à régua.
 */
function drawWallTopSlim(b: Brush): void {
  const base = "#5b6675";
  // 6 art de pedra ao meio (12px de 32) → colunas 5..10; o resto é sombra sobre o
  // chão que o varrimento deixou pintado por baixo.
  const SOMBRA_ESQ = [0.5, 0.3, 0.14, 0.05]; // do encosto para fora
  const SOMBRA_DIR = [0.62, 0.46, 0.32, 0.18, 0.08]; // lado da projecção — vai até à borda
  for (const [i, a] of SOMBRA_ESQ.entries()) b.r(4 - i, 0, 1, 16, `rgba(10,12,17,${a})`);
  for (const [i, a] of SOMBRA_DIR.entries()) b.r(11 + i, 0, 1, 16, `rgba(8,10,14,${a})`);
  // O vulto da parede: mesma pedra e mesmas arestas da capa larga, em 6 art.
  b.r(5, 0, 6, 16, base);
  b.r(5, 0, 6, 1, shade(base, 1.1)); // junta da fiada (cima)
  b.r(5, 0, 1, 16, shade(base, 1.16)); // aresta iluminada (esq)
  b.r(10, 0, 1, 16, shade(base, 0.78)); // aresta sombreada (dir)
  b.r(5, 15, 6, 1, shade(base, 0.86)); // junta da fiada (baixo)
  b.dither(6, 4, 4, 8, shade(base, 1.04), base); // grão de betão
}

/**
 * Parede virada ao observador: capa iluminada em cima (poka 4 art acima do tile)
 * + face vertical em degradê + rodapé. Ancorada em baixo no tile → dá altura.
 * Arte 16 wide × 20 tall (4 art de capa + 16 art de face).
 */
function drawWallFace(b: Brush): void {
  const capTop = "#97a2b0";
  const capBase = "#6b7684";
  // capa (topo da parede, bem iluminada → contraste com a face)
  b.r(0, 0, 16, 4, capBase);
  b.r(0, 0, 16, 1, "#b6c0cc");
  b.r(0, 1, 16, 1, capTop);
  b.r(0, 3, 16, 1, "#2f363f"); // linha funda onde a capa encontra a face
  // face vertical — degradê mais AMPLO: topo claro, base bem escura → altura
  for (let y = 0; y < 15; y += 1) {
    const t = y / 15;
    b.r(0, 4 + y, 16, 1, mix("#697488", "#2c333d", t));
  }
  b.r(0, 4, 1, 15, "#7a8698"); // aresta esquerda iluminada
  b.r(1, 4, 1, 15, "#5b6675");
  b.r(15, 4, 1, 15, "#252b33"); // aresta direita bem sombreada
  b.r(0, 18, 16, 1, "#1c2129"); // rodapé (2 fios → base pesada)
  b.r(0, 19, 16, 1, "#33475a"); // frisо de luz do rodapé (azulado, da sala)
  b.dither(2, 8, 12, 7, "#54606e", "#3f4954"); // textura da face (mais contraste)
}

export const WALL_WINDOW = "wall:window";

/**
 * Igual à WALL_FACE mas com uma JANELA recortada: moldura, vidro em degradê de céu,
 * caixilho em cruz, reflexo no canto e parapeito saliente. É o que tira ao prédio o
 * ar de "caixa fechada / prisão" — a fachada norte passa a respirar.
 */
function drawWallWindow(b: Brush): void {
  const capTop = "#97a2b0";
  const capBase = "#6b7684";
  b.r(0, 0, 16, 4, capBase);
  b.r(0, 0, 16, 1, "#b6c0cc");
  b.r(0, 1, 16, 1, capTop);
  b.r(0, 3, 16, 1, "#2f363f");
  for (let y = 0; y < 15; y += 1) b.r(0, 4 + y, 16, 1, mix("#697488", "#2c333d", y / 15));
  b.r(0, 4, 1, 15, "#7a8698");
  b.r(15, 4, 1, 15, "#252b33");
  b.r(0, 18, 16, 1, "#1c2129");
  b.r(0, 19, 16, 1, "#33475a");
  // ── Janela (moldura + vidro + travessas + reflexo + parapeito).
  const frame = "#20252c";
  b.r(3, 6, 10, 10, frame);
  const skyTop = "#7fb4d8";
  const skyBot = "#cfe6f2";
  for (let y = 0; y < 8; y += 1) b.r(4, 7 + y, 8, 1, mix(skyTop, skyBot, y / 8));
  b.r(4, 7, 4, 4, mix(skyTop, "#ffffff", 0.35)); // reflexo canto sup-esq
  b.r(7, 7, 1, 8, frame); // travessa vertical
  b.r(4, 10, 8, 1, frame); // travessa horizontal
  b.r(3, 6, 10, 1, "#3a424c"); // luz no topo da moldura
  b.r(3, 15, 10, 1, "#12161b"); // peitoril na sombra
  b.r(2, 16, 12, 1, "#8792a0"); // parapeito saliente
}

export const WALL_DOOR = "wall:door";
export const DOOR_EXT_SLIDE = "door:extSlide";

/**
 * Vão da porta exterior INTEIRA (os dois tiles do par), em px de ecrã medidos a
 * partir do canto do PRIMEIRO tile — o que está em `EXTERIOR_DOOR_SPANS`. Estes
 * números são os do par HORIZONTAL; no vertical trocam-se `x↔y` e `w↔h`, porque a
 * peça é a mesma rodada um quarto de volta.
 *
 * 56 de largura contra os 20 de antes: o vão de uma sala tem dois tiles e este
 * passou a ter os mesmos. Sobram 4px de ombreira de pedra em cada ponta, que é o
 * que impede a porta de ler como um buraco na parede.
 */
export const EXT_DOOR_GAP = { x: 4, y: 3, w: 56, h: 26 } as const;

/**
 * BATENTE da porta exterior — a moldura FIXA cravada na parede (ombreira de pedra +
 * vão escuro + soleira). As folhas NÃO se pintam aqui: são dois sprites à parte
 * (`DOOR_EXT_SLIDE`) que a cena faz correr, e o vão escuro é exactamente o que fica
 * à vista quando elas se recolhem. Casa com a capa de topo das paredes vizinhas
 * (base cinza) porque as portas exteriores ficam todas em paredes-cap.
 *
 * Este sprite é METADE do batente, não o batente todo: leva o montante de pedra na
 * ponta ESQUERDA e o vão aberto até à borda direita, para o tile seguinte ser esta
 * mesma textura com `setFlipX(true)`. Duas metades espelhadas dão uma ombreira em U
 * simétrica com 64px de frente — a mesma pegada da porta de uma sala, que também é
 * um sprite de moldura por tile. Nas paredes verticais a cena roda o par 90°: a
 * ombreira que aqui é o montante esquerdo passa a ser a padieira de cima.
 *
 * `EXT_DOOR_GAP` é contrato com a cena: é ela que recorta as folhas (máscara) e que
 * mede o curso de cada uma. Mexer aqui sem mexer lá deixa folha a pintar por cima
 * da pedra. Arte 16×16 a detail=2 (grelha 32×32) → 1 art-cell = 1px de ecrã.
 */
function drawWallDoor(b: Brush): void {
  const gap = EXT_DOOR_GAP;
  const base = "#5b6675";
  b.r(0, 0, 32, 32, base);
  b.r(0, 0, 32, 2, shade(base, 1.28)); // arestas iluminadas da capa
  b.r(0, 0, 2, 32, shade(base, 1.16));
  b.r(0, 30, 32, 2, shade(base, 0.7));
  // Ombreira de pedra: o montante desta ponta + a padieira e a soleira, que correm
  // o tile todo e continuam no tile espelhado ao lado.
  const jamb = "#8b95a3";
  const jambHi = "#aeb7c3";
  const jambSh = "#5c6572";
  b.r(0, 0, gap.x, 32, jamb); // montante da ponta
  b.r(0, 0, 2, 32, jambHi); // aresta iluminada do montante
  b.r(gap.x - 1, 0, 1, 32, jambSh); // sombra onde o montante encontra o vão
  b.r(gap.x, 0, 32 - gap.x, gap.y, jamb); // padieira
  b.r(gap.x, 0, 32 - gap.x, 1, jambHi);
  b.r(gap.x, gap.y - 1, 32 - gap.x, 1, jambSh);
  // Vão — a passagem escura para fora/dentro (o que as folhas recolhidas revelam).
  b.r(gap.x, gap.y, 32 - gap.x, gap.h, "#12171c");
  b.r(gap.x, gap.y, 32 - gap.x, 1, "#0b0e12"); // topo do vão mais fundo
  // Calha por onde as folhas correm, cravada na padieira do vão.
  b.r(gap.x, gap.y + 1, 32 - gap.x, 1, "#2b323b");
  // Luz quente a subir do fundo do vão — dá profundidade ao interior.
  const glowTop = gap.y + gap.h - 8;
  for (let y = 0; y < 8; y += 1) {
    b.r(gap.x, glowTop + y, 32 - gap.x, 1, mix("#12171c", "#d8ab5c", y / 8));
  }
  // Soleira/limiar saliente por baixo.
  b.r(gap.x, gap.y + gap.h, 32 - gap.x, 2, "#9aa4b1");
  b.r(gap.x, gap.y + gap.h + 2, 32 - gap.x, 1, "#c8a05a");
}

/**
 * ALMOFADA rebaixada — o rectângulo afundado que faz uma tábua ler como PORTA.
 * A luz desta cena vem de cima e da esquerda, por isso o rebordo de cima e o da
 * esquerda ficam em sombra (a moldura tapa-lhes a luz) e os de baixo/direita
 * apanham-na. Invertido, o painel salta para fora em vez de afundar.
 */
function drawDoorPanel(b: Brush, x: number, y: number, w: number, h: number): void {
  b.r(x, y, w, h, "#3e2707");
  b.dither(x + 1, y + 1, w - 2, h - 2, "#3e2707", "#3a2107");
  b.r(x, y, w, 1, "#241505"); // aresta de cima, à sombra da moldura
  b.r(x, y, 1, h, "#2b1a06");
  b.r(x, y + h - 1, w, 1, "#6b4310"); // aresta de baixo, iluminada
  b.r(x + w - 1, y, 1, h, "#5c3a0d");
}

/**
 * MEIA-FOLHA da porta exterior — madeira maciça com duas almofadas, no tom das
 * portas do campus 2D. O vão exterior leva DUAS destas: correm para lados opostos
 * e recolhem-se para dentro da ombreira, como as de correr das salas — em vez da
 * folha única que rodava pela dobradiça (uma porta que rodasse para fora ia bater
 * no jardim e para dentro invadia o corredor).
 *
 * ESTA é a porta que o pedido mandou castanha: "as portas do office 2d, essas que
 * são VERDES, ponha as castanhas do campus". Era a única verde do mapa — as das
 * salas são de vidro fosco e ficam de vidro. À primeira tentativa foram trocadas
 * uma pela outra, e este parágrafo existe para isso não se repetir.
 *
 * PORQUÊ REDESENHADA E NÃO O PNG DO CAMPUS: `office-door-bottom-v1.png` tem 81×88
 * e traz o vão INTEIRO (padieira + ombreiras + soleira) num formato quase
 * quadrado; aqui a folha é só a tábua que corre, porque a ombreira já é parte da
 * parede (`drawWallDoor`). Esticar aquele ficheiro para esta fatia esmagava-o. O
 * que veio de lá foi a PALETA, amostrada pixel a pixel do ficheiro: corpo
 * `#4c2c09`/`#795011`, almofada `#3e2707`, contorno `#1b1003`, latão `#cf9919`.
 *
 * 28×26 px = metade exacta do vão de 56 (`EXT_DOOR_GAP.w`), por isso fechadas
 * encostam ao milímetro e o curso de abertura é a própria largura da folha. Era
 * 10×24 e ficou quase três vezes mais larga: o pedido foi pô-la do tamanho das
 * portas das salas, e essas ocupam dois tiles. Com a largura a triplicar as
 * almofadas deixam de ser dois riscos e passam a ler-se como almofadas.
 *
 * O puxador está no bordo DIREITO — este é o desenho da folha ESQUERDA. A folha
 * direita é a MESMA textura com `setFlipX(true)`: o par fica simétrico, os dois
 * puxadores encontram-se a meio do vão e o jogo carrega uma textura em vez de duas.
 * Nas paredes verticais a cena roda as duas 90°, e como a folha é uma tábua com
 * veio próprio isso é o que se quer: de cima, a madeira de uma porta de flanco
 * corre mesmo no outro sentido. detail=2 → 28×26 art-cells nítidas.
 */
function drawDoorExtSlide(b: Brush): void {
  const escuro = "#331e08";
  const claro = "#8a5911";
  const contorno = "#1b1003";
  // ── Corpo: caramelo com degradê, mais aceso em baixo (é onde bate a luz que o
  // vão atira para o chão). O bordo ESQUERDO é o que recolhe para dentro da
  // ombreira; o DIREITO é o bordo livre, o que encosta na outra folha.
  for (let y = 0; y < 26; y += 1) {
    b.r(0, y, 28, 1, mix("#4c2c09", "#795011", y / 26));
  }
  b.dither(1, 1, 26, 24, "#4c2c09", "#492d0b"); // grão da madeira
  // Veio vertical DETERMINÍSTICO: a folha direita é esta mesma com flipX, e um
  // grão aleatório fazia o par ler como duas madeiras diferentes.
  for (let x = 2; x < 27; x += 4) {
    b.r(x, 1, 1, 24, mix("#4c2c09", claro, (((x * 7) % 5) + 1) / 12));
  }
  // ── Almofadas: alta em cima, baixa em baixo. Encostam ao bordo que recolhe
  // para deixarem livre a faixa do puxador, que é do outro lado.
  drawDoorPanel(b, 3, 4, 20, 10);
  drawDoorPanel(b, 3, 17, 20, 5);
  // ── Travessas: risco de luz no topo de cada uma, sombra no pé (luz de cima).
  b.r(0, 0, 28, 1, "#966b1c");
  b.r(0, 3, 28, 1, escuro); // pé da travessa de cima
  b.r(0, 14, 28, 1, claro); // topo da travessa do meio
  b.r(0, 16, 28, 1, escuro);
  b.r(0, 22, 28, 1, claro); // topo do rodapé
  b.r(0, 25, 28, 1, contorno);
  // ── Montantes.
  b.r(0, 0, 1, 26, "#684612"); // bordo que recolhe para dentro da ombreira
  b.r(2, 4, 1, 18, escuro); // sombra interior do montante esquerdo
  b.r(23, 4, 1, 18, claro);
  b.r(27, 0, 1, 26, contorno); // bordo livre: a junta a meio do vão
  // ── Puxador vertical de latão, no bordo livre.
  b.r(24, 9, 3, 9, "#cf9919");
  b.r(24, 9, 1, 9, "#f7b929"); // aresta que apanha a luz
  b.r(26, 9, 1, 9, claro);
  b.r(24, 8, 3, 1, "#af8524"); // fixação de cima
  b.r(24, 18, 3, 1, "#70460a"); // fixação de baixo
  // ── Sapatas do carril: os roletes que penduram a folha na calha da padieira.
  b.r(5, 0, 4, 1, "#684612");
  b.r(17, 0, 4, 1, "#684612");
}

// ── Porta de correr interior (o vão de cada sala) ───────────
//
// Um vão de sala são DOIS tiles lado a lado (garantido por teste em space.test.ts)
// e leva UMA porta dupla automática: duas folhas que correm para lados opostos e
// recolhem para dentro da parede. Três peças, porque só uma delas se mexe:
//   • DOOR_FRAME  — a moldura FIXA, um sprite por tile: padieira, calha e o vão
//                   escuro que fica à vista quando as folhas se afastam;
//   • DOOR_SLIDE  — a folha, o único sprite animado (desliza um tile para o lado);
//   • DOOR_SIGN   — o cadeado que só acende quando a sala está trancada.
// Se a padieira fosse pintada na folha, abrir a porta levava a parede com ela.

export const DOOR_FRAME = "door:frame";
export const DOOR_SLIDE = "door:slide";
export const DOOR_SIGN = "door:sign";

/**
 * MOLDURA do vão — a parte que nunca se mexe. Ancorada pela base (origin 0.5,1)
 * no fundo do tile, com a mesma capa de pedra da `WALL_FACE` vizinha para o vão
 * ler como um buraco ABERTO na divisória e não como um autocolante por cima dela.
 *
 * Por baixo da capa corre a calha de alumínio onde as folhas penduram — é o que
 * explica ao olho, sem uma linha de texto, porque é que aquela porta corre para o
 * lado em vez de rodar. O interior é o vão escuro com a luz do chão da sala a
 * subir do fundo: é exactamente o que se vê quando a porta abre.
 *
 * Arte 16×20 a detail=2 (grelha 32×40) — pegada idêntica à de uma parede-face.
 */
function drawDoorFrame(b: Brush): void {
  // ── Capa de pedra (idêntica à WALL_FACE, para casar tile a tile).
  b.r(0, 0, 32, 8, "#6b7684");
  b.r(0, 0, 32, 2, "#b6c0cc");
  b.r(0, 2, 32, 2, "#97a2b0");
  b.r(0, 6, 32, 2, "#2f363f"); // linha funda onde a capa encontra o vão
  // ── Vão: a passagem escura para a sala.
  b.r(0, 8, 32, 32, "#141a20");
  b.r(0, 8, 32, 2, "#0a0e12"); // topo do vão mais fundo (sombra da padieira)
  // Luz do chão da sala a subir do fundo do vão → profundidade, não um buraco liso.
  for (let y = 0; y < 12; y += 1) {
    b.r(0, 28 + y, 32, 1, mix("#141a20", "#8d9aa6", y / 12));
  }
  b.r(0, 8, 2, 32, "#1d242c"); // ombreiras interiores (o rasgo onde a folha entra)
  b.r(30, 8, 2, 32, "#0e1319");
  // ── Calha de alumínio: barra + carril + sombra projectada no vão.
  b.r(0, 8, 32, 3, "#7d8794");
  b.r(0, 8, 32, 1, "#c3ccd6"); // brilho no cimo da calha
  b.r(0, 10, 32, 1, "#454e59"); // rasgo do carril (fio escuro)
  b.r(0, 11, 32, 2, "#39414b"); // sombra que a calha atira para dentro do vão
  // ── Soleira: o trilho de baixo, gasto ao meio pela passagem.
  b.r(0, 38, 32, 2, "#8b95a3");
  b.r(0, 38, 32, 1, "#aab4c0");
  b.r(10, 38, 12, 1, "#7c8693"); // desgaste onde toda a gente pisa
}

/**
 * FOLHA de correr — vidro fosco num caixilho de alumínio, o painel de escritório
 * moderno. É o ÚNICO sprite que a cena anima: um tween de `x` de exactamente um
 * tile, para a folha desaparecer atrás da parede do lado (a profundidade trata do
 * resto). Nada de rodar: uma folha que rodasse invadia o corredor e a sala.
 *
 * NÃO É DE MADEIRA, e não é por falta de tentativa: já foi castanha, no tom do
 * campus, e o pedido que a mandou castanha era outro — era a porta EXTERIOR, a
 * verde. As de vidro são as das salas e é assim que devem ficar. Fica escrito
 * aqui porque a única forma de não voltar a trocá-las é saber o que cada uma é.
 *
 * O puxador está no bordo DIREITO — este é o desenho da folha ESQUERDA. A folha
 * direita é a mesma textura com `setFlipX(true)`: o par fica simétrico e o jogo
 * carrega uma textura em vez de duas.
 *
 * Arte 16×16 a detail=2 (grelha 32×32) — cobre exactamente o tile do vão.
 */
function drawDoorSlide(b: Brush): void {
  const alu = "#aeb8c4";
  const aluHi = "#dde4ec";
  const aluSh = "#6d7784";
  const aluDk = "#3f4854";
  // ── Vidro fosco esverdeado: degradê vertical + bandas de vidro canelado.
  for (let y = 0; y < 32; y += 1) {
    b.r(0, y, 32, 1, mix("#b9d4cb", "#6f9a8e", y / 32));
  }
  for (let y = 2; y < 30; y += 4) {
    b.r(2, y, 28, 1, "#cfe3dc"); // relevo canelado (o que torna o vidro OPACO ao olho)
  }
  b.dither(3, 3, 26, 26, "#a9c8bf", "#9dbeb4"); // grão do fosco
  // Reflexo diagonal da luz do tecto — a única coisa que faz vidro parecer vidro.
  b.r(5, 6, 7, 2, "#e8f4ef");
  b.r(7, 8, 6, 2, "#dcece6");
  b.r(9, 10, 4, 2, "#d2e6df");
  b.r(20, 20, 5, 2, "#c6ded6"); // segundo reflexo, mais fraco, em baixo
  // ── Caixilho de alumínio a toda a volta.
  b.r(0, 0, 32, 3, alu); // travessa de cima
  b.r(0, 0, 32, 1, aluHi);
  b.r(0, 2, 32, 1, aluSh);
  b.r(0, 29, 32, 3, alu); // travessa de baixo
  b.r(0, 29, 32, 1, aluHi);
  b.r(0, 31, 32, 1, aluDk);
  b.r(0, 0, 2, 32, alu); // montante do bordo que recolhe
  b.r(0, 0, 1, 32, aluHi);
  b.r(28, 0, 4, 32, alu); // montante do bordo livre (mais largo — leva o puxador)
  b.r(28, 0, 1, 32, aluHi);
  b.r(31, 0, 1, 32, aluDk);
  // ── Puxador vertical embutido no bordo livre.
  b.r(29, 11, 2, 10, "#2b323b");
  b.r(29, 11, 2, 1, "#59636f");
  b.r(29, 20, 2, 1, "#161a20");
  // ── Sapatas do carril: os dois roletes que penduram a folha na calha.
  b.r(6, 0, 4, 2, aluSh);
  b.r(22, 0, 4, 2, aluSh);
}

/**
 * SINAL DE TRANCADA — o cadeado que aparece a meio da porta enquanto a sala está
 * fechada. É a resposta à pergunta que uma porta automática levanta sozinha: uma
 * folha fechada pode ser "não está lá ninguém" ou "está trancada", e do outro lado
 * do corredor não se distingue. O cadeado (com o LED vermelho da cena ao lado)
 * distingue — a tranca passa a ver-se à distância, sem abrir menu nenhum.
 *
 * Arte 8×10 a detail=2 (grelha 16×20) — pequeno de propósito, é um sinal.
 */
function drawDoorSign(b: Brush): void {
  const steel = "#aab3c0";
  const steelSh = "#5d6672";
  const gold = "#e0b53c";
  // Arco de aço.
  b.r(5, 0, 6, 2, steel);
  b.r(5, 0, 6, 1, "#d6dce5");
  b.r(4, 1, 2, 6, steel);
  b.r(10, 1, 2, 6, steelSh);
  // Corpo do cadeado.
  b.r(2, 6, 12, 12, gold);
  b.r(2, 6, 12, 1, "#ffe58a"); // luz de cima
  b.r(2, 6, 1, 12, "#f5d97a"); // aresta esquerda iluminada
  b.r(13, 6, 1, 12, "#8a6a14"); // aresta direita em sombra
  b.r(2, 17, 12, 1, "#6d5310");
  b.r(3, 18, 11, 1, "#00000055"); // sombra pousada por baixo
  // Buraco da fechadura.
  b.r(7, 10, 2, 2, "#3a2c06");
  b.r(7, 12, 2, 3, "#3a2c06");
}

// ── Candeeiro de parede (aplique quente) ────────────────────

export const WALL_LAMP = "wall:lamp";

/**
 * Aplique de parede no estilo da referência: braço de ferro preto + lampião de
 * vidro âmbar com brasa quente lá dentro. Autorado a detail=2 (grelha 24×36) para
 * a moldura de ferro e o filamento ficarem nítidos. A auréola de luz quente que se
 * espalha pelo chão é adicionada na cena (GLOW_WARM, aditivo) — aqui é só o objeto.
 */
function drawWallLamp(b: Brush): void {
  const iron = "#1b1b20";
  const ironHi = "#3c3c46";
  const ironDk = "#0c0c10";
  const glass = "#ffcf7a"; // vidro quente
  const glassHi = "#ffe8b8";
  const core = "#fff3d0"; // núcleo incandescente
  const ember = "#ff9a3c"; // brasa
  const emberHi = "#ffd98a";
  // chapa de fixação + haste
  b.r(9, 1, 6, 3, iron);
  b.r(9, 1, 6, 1, ironHi);
  b.r(9, 3, 6, 1, ironDk);
  b.r(11, 4, 2, 6, iron);
  b.r(11, 4, 1, 6, ironHi); // fio de luz na haste
  // chapéu do lampião + pináculo
  b.r(11, 7, 2, 1, iron); // pináculo
  b.r(8, 8, 8, 1, iron);
  b.r(6, 9, 12, 2, iron);
  b.r(6, 9, 12, 1, ironHi);
  // corpo de vidro (ligeiro trapézio que alarga a meio)
  b.r(7, 11, 10, 16, glass);
  b.r(6, 14, 12, 10, glass);
  b.dither(7, 12, 10, 13, glassHi, glass); // cintilação do vidro
  b.r(9, 14, 6, 9, core); // núcleo quente
  b.r(10, 15, 4, 7, ember); // brasa/filamento
  b.r(11, 16, 2, 5, emberHi);
  b.r(7, 12, 8, 1, glassHi); // reflexo do topo do vidro
  // moldura de ferro (montantes + travessas + montante central)
  b.r(6, 11, 1, 16, iron);
  b.r(17, 11, 1, 16, iron);
  b.r(6, 11, 12, 1, iron);
  b.r(6, 26, 12, 1, iron);
  b.r(11, 11, 1, 16, ironHi); // montante central (aresta de luz)
  b.r(6, 18, 12, 1, ironHi); // travessa do meio
  // base / bico inferior com pinga de luz
  b.r(9, 27, 6, 2, iron);
  b.r(9, 27, 6, 1, ironHi);
  b.r(11, 29, 2, 2, iron);
  b.r(11, 30, 2, 1, glass);
}

// ── Móveis ──────────────────────────────────────────────────

export function propKey(kind: PropKind): string {
  return `prop:${kind}`;
}

/**
 * As cadeiras desenham-se em DUAS CAMADAS, e a razão é de desenho, não de código.
 *
 * Uma cadeira não é um cartaz atrás do corpo: parte dela fica à FRENTE de quem lá
 * está. Com uma só imagem por móvel, o boneco sentado tinha de ficar por cima —
 * e as costas de quem se senta de frente para a secretária apareciam INTEIRAS
 * coladas sobre o encosto, como se a cadeira fosse um autocolante no chão.
 *
 * A correcção é a ordem natural das três camadas: móvel → corpo → encosto.
 *   • `base`  — o que fica ATRÁS do corpo (assento, coluna, pés, apoio de cabeça).
 *   • `front` — o que TAPA o corpo (o encosto e os braços, quando estão do lado
 *     da câmara). Desenha-se logo acima da profundidade de quem está sentado.
 * As duas camadas juntas são SEMPRE a cadeira completa — uma cadeira vazia fica
 * exactamente igual, seja qual for a direcção.
 *
 * E qual das peças passa à frente depende da DIRECÇÃO em que ali se senta (a que
 * `space.ts` lê da planta), porque é isso que decide o que está entre a câmara e
 * o corpo:
 *   • `up`    — de costas para nós: o encosto está à frente ⇒ tapa as costas. O
 *     apoio de cabeça fica na base, senão comia a cara (e, com alguém sentado, o
 *     que se vê ali é a nuca — o apoio de cabeça só aparece na cadeira vazia).
 *   • `down`  — de frente: o corpo está à frente de tudo ⇒ camada da frente vazia.
 *   • `left`/`right` — de perfil: o encosto está AO LADO das costas, não à frente
 *     delas; o que atravessa por diante do corpo é o apoio de braço deste lado —
 *     e numa cadeira SEM braços não atravessa nada, a camada da frente fica vazia.
 */
export type SeatLayer = "base" | "front";

const SEAT_LAYERS = ["base", "front"] as const;

/** Desenhador de um assento: sabe-se pintar em qualquer vista e qualquer camada. */
type SeatDrawer = (b: Brush, facing: Facing, layer: SeatLayer) => void;

/** Chave da textura de um assento, por direcção e camada. */
export function seatPropKey(kind: PropKind, facing: Facing, layer: SeatLayer): string {
  return `prop:${kind}:${facing}:${layer}`;
}

/**
 * Posto do SUPERVISOR da TI — PLANO B da arte do pacote
 * (`ti-supervisor-desk-v1.png`): consola larga de madeira com três ecrãs, torre
 * de vidro, telefone, teclado inteiro e um painel de macros. A CENA acende por
 * cima o NOME DO CARGO (a mesma sala que dispara a call).
 *
 * Já foi um CRT bege de terminal numa grelha de 64×56, e a mudança não foi de
 * gosto: 64×56 é a proporção 1,143 e o render do pacote tem 1,7985 — a mesa
 * estava ACHATADA, e via-se assim que a cadeira nova (32×40) foi para debaixo
 * dela. A 96×54 a proporção é a verdadeira (desvio de -1,15%) e o supervisor
 * deixa de ter a menor secretária da sala (as consolas dos funcionários têm 90).
 *
 * A grelha é 96×54, a MESMA medida final do PNG, pela razão que o `serverRack`
 * já documenta: se os dois planos medissem diferente, a mesma secretária mudava
 * de tamanho consoante o ficheiro tivesse carregado. E, ao contrário do CRT, não
 * dava para reaproveitar o desenho antigo com um deslocamento do pincel — a
 * altura ENCOLHEU de 56 para 54, e o que não cabe não se resolve a empurrar.
 *
 * As bandas saem medidas do PNG (mapa de cores do ficheiro final, não a olho):
 *
 *   y  0..29  torre de vidro x 72..93 · ecrã grande x 22..55 · laterais x 3..20 e 56..69
 *   y 21..41  tampo em escorço, aresta de trás curta (x 3..92) e a da frente inteira
 *   y 25..36  telefone x 6..17 · teclado x 23..51 · rato x 53..58 · macros x 60..71
 *   y 42..43  avental verde com o puxador da gaveta central
 *   y 44..53  os dois blocos de gavetas que servem de perna, x 2..22 e x 73..93
 */
function drawDeskPC(b: Brush): void {
  // Autorado a DETAIL=2 → grelha 96×54, pegada de ecrã idêntica (96×54 px, w3).
  const wood = "#733d18";
  const woodHi = "#a2794a";
  const woodMid = "#8c4f21";
  const woodSh = "#522c12";
  const woodDk = "#22150b";
  const shell = "#1f2223";
  const shellHi = "#3a3e3f";
  const shellDk = "#0d1316";
  const base = "#171a1b";
  const glass = "#06171b";
  const ph = "#05a466";
  const phHi = "#3ce49a";
  const cy = "#037c93";
  const cyHi = "#4fd2e8";
  const brass = "#caa863";

  // ── Tampo em escorço: a aresta de trás é mais curta do que a da frente, e é
  // isso que dá a sensação de olhar a mesa de cima e de lado. UM degrau, de 3 px:
  // com quatro (1+1+2+3) a ponta esquerda saía uma escada e lia-se como um
  // lascado, não como perspectiva — o degrau é grande de mais para a altura que
  // tem para o esconder.
  b.r(3, 21, 90, 11, wood);
  b.r(0, 32, 96, 10, wood);
  b.r(3, 21, 90, 1, woodMid); // aresta de trás iluminada
  // O veio é um `dither` de duas madeiras VIZINHAS. Com `woodMid` cru contra
  // `wood` a trama de 1 px lê-se como xadrez — o tampo ficava um tabuleiro em vez
  // de madeira (visto ao ampliar o plano B ao lado do PNG, não adivinhado).
  b.dither(4, 23, 88, 15, mix(wood, woodMid, 0.3), wood);
  b.r(8, 38, 80, 1, mix(woodMid, woodHi, 0.5)); // reflexo comprido de verniz
  b.r(0, 40, 96, 1, woodSh); // bisel da frente
  b.r(0, 41, 96, 1, woodDk); // sombra funda sob o tampo

  // ── Avental verde da frente + puxador de latão da gaveta central.
  const skirt = "#042612";
  b.r(2, 42, 92, 2, skirt);
  b.r(2, 42, 92, 1, mix(skirt, "#ffffff", 0.16));
  b.r(42, 41, 10, 3, woodMid);
  b.r(42, 41, 10, 1, brass);
  b.r(42, 43, 10, 1, woodDk);

  // ── Dois blocos de gavetas a fazer de perna, um debaixo de cada ponta.
  for (const gx of [2, 73]) {
    b.r(gx, 44, 21, 10, woodSh);
    b.r(gx, 44, 21, 1, woodMid); // luz de cima do bloco
    b.r(gx, 44, 1, 10, woodMid);
    b.r(gx + 20, 44, 1, 10, woodDk);
    for (const gy of [46, 50]) {
      b.r(gx + 2, gy, 17, 3, wood);
      b.r(gx + 2, gy, 17, 1, woodHi);
      b.r(gx + 2, gy + 2, 17, 1, woodDk);
      b.r(gx + 7, gy + 1, 7, 1, brass); // puxador de latão
    }
    b.r(gx, 53, 21, 1, woodDk); // pé de contacto, por cima das gavetas
  }

  // ── Torre de vidro temperado à direita: três ventoinhas acesas e fita de LEDs.
  b.r(72, 0, 22, 30, shell);
  b.r(72, 0, 22, 1, shellHi);
  b.r(72, 0, 1, 30, shellHi);
  b.r(93, 0, 1, 30, shellDk);
  b.r(74, 2, 18, 26, "#0a1417"); // painel lateral de vidro
  b.dither(74, 2, 18, 26, "#0d1a1e", "#081013");
  for (const fy of [6, 14, 22]) {
    // Anel chanfrado nos quatro cantos: a 8×6 px um rectângulo lê-se como uma
    // gaveta de rack, não como uma ventoinha. Tirar o canto chega para o olho
    // fechar o círculo.
    b.r(84, fy, 6, 1, ph);
    b.r(84, fy + 5, 6, 1, ph);
    b.r(83, fy + 1, 1, 4, ph);
    b.r(90, fy + 1, 1, 4, ph);
    b.r(86, fy + 2, 2, 2, phHi); // centro da ventoinha, mais aceso
  }
  for (let vy = 4; vy <= 25; vy += 3) b.r(76, vy, 4, 1, "#0d5f46"); // fita de LEDs

  // ── Ecrã grande do meio: o diagrama de rede que o supervisor tem sempre aberto.
  b.r(22, 0, 34, 20, shell);
  b.r(22, 0, 34, 1, shellHi);
  b.r(22, 0, 1, 20, shellHi);
  b.r(55, 0, 1, 20, shellDk);
  b.r(24, 2, 30, 16, glass);
  b.dither(24, 2, 30, 16, "#0a2027", glass); // brilho do painel
  b.r(26, 5, 26, 1, cy); // barramento de cima
  b.r(26, 11, 26, 1, cy); // barramento de baixo
  for (const nx of [27, 33, 39, 45]) {
    b.r(nx, 3, 3, 3, ph); // nó de cima
    b.r(nx + 1, 4, 1, 1, phHi);
    b.r(nx + 1, 6, 1, 5, cy); // ligação entre barramentos
    b.r(nx, 12, 3, 3, ph); // nó de baixo
    b.r(nx + 1, 13, 1, 1, phHi);
  }
  for (let bx = 0; bx < 5; bx += 1) {
    const alt = 2 + ((bx * 3) % 7);
    b.r(49 + bx, 16 - alt, 1, alt, bx % 2 ? ph : cy); // barras de carga
  }
  b.r(24, 2, 12, 1, "#123c46"); // reflexo do vidro (canto sup-esq)
  b.r(24, 2, 1, 6, "#123c46");
  b.r(37, 20, 4, 2, shellDk); // pescoço
  b.r(33, 22, 12, 2, base); // base inclinável
  b.r(33, 22, 12, 1, shell);

  // ── Ecrã da esquerda, virado para dentro: painel de métricas.
  b.r(4, 5, 17, 15, shell);
  b.r(4, 5, 17, 1, shellHi);
  b.r(3, 7, 1, 12, shellHi); // canto chanfrado — o ecrã está de esguelha
  b.r(20, 5, 1, 15, shellDk);
  b.r(6, 7, 13, 11, glass);
  for (let ly = 8; ly <= 16; ly += 2) b.r(7, ly, 5 + ((ly * 3) % 7), 1, ly % 4 === 0 ? ph : cy);
  b.r(7, 8, 11, 1, cyHi); // linha do topo, em destaque
  b.r(9, 20, 6, 2, shellDk);
  b.r(7, 22, 10, 1, base);

  // ── Ecrã da direita, também de esguelha: consola de logs a correr.
  b.r(56, 5, 14, 15, shell);
  b.r(56, 5, 14, 1, shellHi);
  b.r(56, 5, 1, 15, shellHi);
  b.r(69, 5, 1, 15, shellDk);
  b.r(58, 7, 10, 11, glass);
  for (let ly = 8; ly <= 16; ly += 2) b.r(59, ly, 3 + ((ly * 5) % 6), 1, ly % 4 === 0 ? cy : ph);
  b.r(59, 17, 2, 1, phHi); // cursor aceso na última linha
  b.r(62, 20, 4, 2, shellDk);
  b.r(60, 22, 8, 1, base);

  // ── Telefone de secretária: LCD verde, teclado e auscultador pousado.
  b.r(6, 25, 12, 11, "#1c2026");
  b.r(6, 25, 12, 1, "#2f353d");
  b.r(17, 25, 1, 11, "#0e1114");
  b.r(12, 26, 5, 4, "#0a2a1c");
  b.r(12, 26, 5, 1, ph);
  b.r(13, 28, 3, 1, shade(ph, 0.7));
  for (let ky = 31; ky <= 34; ky += 1) {
    for (let kx = 12; kx <= 16; kx += 2) b.r(kx, ky, 1, 1, "#575e66");
  }
  b.r(7, 26, 4, 9, "#0f1216");
  b.r(7, 26, 4, 1, "#2b3138");
  b.r(7, 34, 4, 1, "#05080a");

  // ── Teclado mecânico de tamanho inteiro, ao centro do tampo.
  b.r(23, 27, 29, 9, "#2a2e34");
  b.r(23, 27, 29, 1, "#3d434b");
  b.r(24, 28, 27, 7, "#c9c2a4");
  b.r(24, 28, 27, 1, "#e6e0c6"); // brilho de cima
  for (let ky = 29; ky <= 32; ky += 1) {
    for (let kx = 25; kx <= 49; kx += 2) b.r(kx, ky, 1, 1, ky === 32 ? "#a9a184" : "#ded7ba");
  }
  b.r(31, 34, 13, 1, "#a49d82"); // barra de espaço
  b.r(45, 29, 5, 1, brass); // fila de macros iluminada
  b.r(22, 30, 2, 1, "#15181d"); // cabo a sair para a esquerda

  // ── Rato, com a roda acesa.
  b.r(53, 29, 6, 6, "#20242a");
  b.r(53, 29, 6, 1, "#31373f");
  b.r(55, 30, 2, 2, ph);
  b.r(53, 34, 6, 1, "#0e1114");

  // ── Painel de macros do supervisor: seis botões grandes e coloridos.
  b.r(60, 25, 12, 12, "#1a1e24");
  b.r(60, 25, 12, 1, "#2d333b");
  b.r(71, 25, 1, 12, "#0d1014");
  const botoes = ["#c8443c", "#e0a53a", "#2f9e5e", "#2f8fbe", "#8b5cf6", brass] as const;
  botoes.forEach((cor, i) => {
    const bx = 62 + (i % 3) * 3;
    const by = 27 + Math.floor(i / 3) * 4;
    b.r(bx, by, 2, 3, cor);
    b.r(bx, by, 2, 1, mix(cor, "#ffffff", 0.4)); // luz de cima da tecla
  });
  b.r(62, 35, 8, 1, "#3a4149"); // roda de volume, em baixo
}

/**
 * Secretária de Vendas — PLANO B da arte do pacote (`sales-desk-v1.png`).
 *
 * A grelha é 64×46 (detail=2), a MESMA medida final do PNG, pela razão que o
 * `serverRack` já documenta: se os dois planos medissem diferente, a mesma
 * secretária mudava de tamanho consoante o ficheiro tivesse carregado.
 *
 * Já foi um invólucro de quatro linhas que deslocava o pincel e deixava o
 * `drawDeskPC` pintar-se lá dentro — e funcionava enquanto a caixa era 96×70,
 * porque sobrava espaço. Não sobrevive a 64×46: o `drawDeskPC` pinta de y=4 a
 * y=54, são 50 px de altura que não entram numa caixa de 46, e o que a mesa
 * perde em ecrã não se recupera a espremer o desenho (esticar um dos planos
 * fazia a mesma secretária mudar de forma consoante o PNG carregasse).
 *
 * É por isso um desenho próprio, e é OUTRA secretária: o `drawDeskPC` é um CRT
 * bege de terminal, este é o ecrã largo com o painel de gestão. As bandas saem
 * medidas do PNG (mapa de cores do ficheiro final, não a olho):
 *
 *   y 0..18  monitor, x 18..46 — ecrã x 21..43, y 2..16
 *   y 19..30 tampo, de parede a parede — pescoço e base do monitor por cima
 *   y 23..28 teclado x 20..44 · rato x 46..51 · telefone x 4..15, y 14..22
 *   y 31..45 gavetas à esquerda e à direita, torre encaixada no vão da direita
 *
 * O vão entre os dois blocos de gavetas fica VAZIO de propósito — é o buraco por
 * onde se vêem as pernas de quem lá está sentado, e no PNG é transparente.
 */
function drawSalesDesk(b: Brush): void {
  const wood = "#96501f";
  const woodHi = "#be692d";
  const woodMid = "#a85c26";
  const woodSh = "#5f3014";
  const woodDk = "#33190a";
  const bezel = "#e6e1d2";
  const bezelSh = "#b4ac9e";
  const metal = "#464a50";
  const black = "#1e2024";
  const scr = "#103c2a";
  const ph = "#2f9e5e";
  const phHi = "#5ee08a";

  // ── Tampo: prancha de parede a parede, aresta de trás na sombra, veio e
  //    reflexo comprido. Pintado PRIMEIRO — a base do monitor pousa-lhe em cima.
  b.r(3, 19, 58, 2, woodSh);
  b.r(2, 21, 60, 8, wood);
  b.r(2, 21, 60, 1, woodHi); // aresta iluminada
  b.dither(3, 22, 58, 6, woodMid, wood); // veio da madeira
  b.r(6, 24, 40, 1, mix(woodHi, wood, 0.35)); // reflexo de verniz
  b.r(1, 29, 62, 2, woodSh); // avental frontal
  b.r(1, 29, 62, 1, woodMid);
  b.r(1, 31, 62, 1, woodDk); // sombra funda sob o tampo

  // ── Blocos de gavetas, um de cada lado, com puxadores verdes (os do ficheiro).
  for (const bx of [2, 47]) {
    b.r(bx, 31, 15, 14, wood);
    b.r(bx, 31, 15, 1, woodMid);
    b.r(bx, 31, 1, 14, woodMid);
    b.r(bx + 14, 31, 1, 14, woodDk);
    b.r(bx, 44, 15, 1, woodDk); // pé de contacto
    for (const gy of [33, 38]) {
      b.r(bx + 2, gy, 11, 4, woodHi);
      b.r(bx + 2, gy, 11, 1, mix(woodHi, bezel, 0.25));
      b.r(bx + 2, gy + 3, 11, 1, woodSh);
      b.r(bx + 5, gy + 1, 5, 1, ph); // puxador
    }
  }

  // ── Torre encaixada no vão direito, encostada ao bloco de gavetas.
  b.r(37, 30, 10, 15, black);
  b.r(37, 30, 10, 1, metal);
  b.r(37, 30, 1, 15, "#2a2d33");
  b.r(46, 30, 1, 15, "#101216");
  b.r(39, 33, 6, 1, metal); // ranhura óptica
  b.r(40, 36, 3, 3, ph); // logótipo aceso
  b.r(41, 37, 1, 1, phHi);
  b.r(39, 41, 6, 1, "#2a2d33"); // grelha de ventilação

  // ── Monitor de ecrã largo: caixa metálica, moldura clara, bisel preto.
  b.r(18, 0, 29, 19, metal);
  b.r(19, 1, 27, 17, bezel);
  b.r(19, 1, 27, 1, "#f2eee2"); // topo iluminado
  b.r(46, 1, 1, 17, bezelSh); // direita sombreada
  b.r(20, 2, 25, 15, black);
  b.r(21, 2, 23, 15, scr);
  b.dither(21, 2, 23, 15, "#17553a", scr); // brilho do painel

  // Painel de gestão: cartão da casa à esquerda, coluna de barras à direita.
  b.r(22, 3, 4, 1, phHi); // cabeçalho esquerdo
  b.r(34, 3, 4, 1, phHi); // cabeçalho direito
  b.r(22, 5, 10, 9, "#0d3324");
  b.r(22, 5, 10, 1, ph);
  b.r(24, 7, 6, 4, "#0a2a1d");
  b.r(24, 7, 6, 1, phHi); // linha do telhado
  b.r(26, 9, 2, 2, ph); // porta
  for (const by of [5, 7, 9, 11]) {
    b.r(34, by, 6, 1, ph);
    b.r(41, by, 2, 1, "#caa863"); // marcador âmbar
  }
  b.r(34, 13, 4, 1, shade(ph, 0.8));

  // ── Pescoço e base do monitor, pousados no tampo.
  b.r(30, 17, 5, 3, bezelSh);
  b.r(30, 17, 5, 1, bezel);
  b.r(26, 20, 13, 2, bezelSh);
  b.r(26, 20, 13, 1, bezel);
  b.r(30, 20, 5, 1, ph); // faixa verde da base

  // ── Teclado: base, placa e grelha de teclas + barra de espaço.
  b.r(20, 23, 25, 6, "#8f8778");
  b.r(20, 23, 25, 1, bezelSh);
  b.r(21, 24, 23, 4, "#cfc9b8");
  b.r(21, 24, 23, 1, bezel);
  for (let ky = 24; ky < 27; ky += 1) {
    for (let kx = 22; kx < 44; kx += 2) b.r(kx, ky, 1, 1, ky === 26 ? "#b0a897" : "#e3ddcd");
  }
  b.r(28, 27, 10, 1, "#a49c8b"); // barra de espaço

  // ── Rato com divisória de botões e cabo a subir para o monitor.
  b.r(46, 24, 6, 4, "#cfc9b8");
  b.r(46, 24, 6, 1, bezel);
  b.r(48, 24, 1, 2, "#8f8778");
  b.r(49, 22, 1, 2, black);

  // ── Telefone de secretária à esquerda, auscultador pousado.
  b.r(5, 16, 10, 7, black);
  b.r(5, 16, 10, 1, metal);
  b.r(4, 14, 8, 3, black);
  b.r(4, 14, 8, 1, metal);
  b.r(6, 18, 7, 4, metal); // teclas
  for (let ty = 19; ty < 22; ty += 1) {
    for (let tx = 7; tx < 12; tx += 2) b.r(tx, ty, 1, 1, "#8c9199");
  }
  b.r(13, 17, 1, 2, ph); // LED de linha
}

/**
 * Consola de seis monitores — a bancada da sala de TI.
 *
 * Esta função é o PLANO B: quando o PNG do pacote (`computer-console-v1.png`)
 * está lá, é ele que ganha a chave da textura e isto nunca chega a correr. Existe
 * porque um 404 no `load.image` NÃO dá erro — dava um quadrado preto no meio da
 * sala. Um desenho a sério em vez de um buraco.
 *
 * A grelha é 90×52 (detail=2), a MESMA medida final da arte do pacote: os dois
 * planos têm de ser intermutáveis, senão a sala muda de composição consoante o
 * PNG carregar ou não. Os dois postos ficam simétricos em torno de x=45, e é o do
 * meio dos três monitores que marca o centro — que é onde cai o único lugar
 * sentável (a pegada tem largura ÍMPAR por isso — ver `PROP_SPECS.deskDual`).
 *
 * As 4 linhas que a bancada ganhou («aumente um poouquinho … bem poucio») não se
 * empurraram todas para o mesmo sítio: 2 foram para os monitores (chassis 17→19,
 * vidro 14→16) e 2 para as pernas (9→11), que é o que mantém o tampo à mesma
 * altura RELATIVA. Empurrar o desenho inteiro para baixo punha a bancada a
 * levitar — a âncora é o pé (`setOrigin(0.5, 1)`), não o topo.
 */
function drawDeskDual(b: Brush): void {
  const top = "#3b434c";
  const topHi = "#5b6570";
  const topMid = "#48515b";
  const topDk = "#1e2329";
  const apron = "#2f363d";
  const metal = "#59626c";
  const metalHi = "#8b949e";
  const metalDk = "#161a1f";

  // ── Estrutura primeiro: o que fica POR BAIXO desenha-se antes do tampo, e o
  // tampo antes do que assenta nele (teclados, pés dos monitores).
  // Três cavaletes: pontas e meio — a bancada é comprida, precisa do do meio.
  for (const lx of [4, 43, 82]) {
    b.r(lx, 39, 4, 11, metal);
    b.r(lx, 39, 1, 11, metalHi);
    b.r(lx + 3, 39, 1, 11, metalDk);
    b.r(lx - 1, 49, 6, 2, metalDk); // pé de contacto
  }
  b.r(8, 41, 74, 2, metal); // travessa que une os cavaletes
  b.r(8, 41, 74, 1, metalHi);

  // Torres por baixo, uma por posto (é uma sala de TI, não um escritório).
  for (const tx of [17, 61]) {
    b.r(tx, 39, 7, 12, "#22272d");
    b.r(tx, 39, 7, 1, "#3a424b");
    b.r(tx, 39, 1, 12, "#333b44");
    b.r(tx + 6, 39, 1, 12, "#12151a");
    b.r(tx + 2, 41, 4, 1, "#4a535d"); // grelha frontal
    b.r(tx + 2, 43, 4, 1, "#4a535d");
    b.r(tx + 2, 48, 2, 2, "#33cf6e"); // LED aceso
    b.r(tx + 2, 48, 1, 1, "#7dfcae");
  }

  // ── Tampo corrido: uma prancha só a ligar os dois postos, e o seu avental.
  b.r(1, 27, 88, 7, top);
  b.r(1, 27, 88, 1, topHi); // aresta de cima iluminada
  b.r(1, 28, 88, 1, mix(top, topHi, 0.45));
  b.dither(3, 29, 84, 4, topMid, top);
  b.r(1, 34, 88, 1, topDk); // sombra funda sob o tampo
  b.r(3, 35, 84, 4, apron);
  b.r(3, 35, 84, 1, mix(apron, topHi, 0.35));
  b.r(3, 38, 84, 1, metalDk);

  // ── Seis monitores: três por posto, os postos centrados em x=23 e x=67.
  for (const cx of [23, 67]) {
    for (const [i, mx] of [cx - 20, cx - 6, cx + 8].entries()) {
      // Chassis + bisel: o do meio é um pouco mais alto (é o ecrã principal).
      const alto = i === 1 ? 2 : 0;
      const y0 = 4 - alto;
      b.r(mx, y0, 13, 19 + alto, "#20252b");
      b.r(mx, y0, 13, 1, "#39414a");
      b.r(mx, y0, 1, 19 + alto, "#333b43");
      b.r(mx + 12, y0, 1, 19 + alto, "#0e1116");

      // Vidro: fósforo verde nos laterais, um "dashboard" no central.
      const scr = "#08160e";
      b.r(mx + 1, y0 + 1, 11, 16 + alto, scr);
      b.dither(mx + 1, y0 + 1, 11, 16 + alto, "#0d2016", scr);
      // Scanlines ANTES do conteúdo. Ao contrário: estavam a correr no fim, de
      // duas em duas a partir de `y0+1`, que é exactamente a paridade em que as
      // linhas de texto dos monitores laterais são escritas — apagava-as todas e
      // os quatro ecrãs de fora ficavam vazios. Só se viu a render o plano B e a
      // OLHAR para ele; nenhum teste mede o que um pincel escreve por cima.
      for (let sy = y0 + 1; sy < y0 + 17 + alto; sy += 2) b.r(mx + 1, sy, 11, 1, "#0b1a11");
      if (i === 1) {
        // Gráfico de barras + linha de tendência — o painel que se olha de longe.
        b.r(mx + 2, y0 + 2, 9, 1, "#2f9e5e");
        for (const [j, alt] of [3, 6, 4, 8, 5].entries()) {
          b.r(mx + 2 + j * 2, y0 + 15 - alt, 1, alt, j % 2 ? "#2f9e5e" : "#5ee08a");
        }
        b.r(mx + 2, y0 + 16, 9, 1, "#1f5236");
      } else {
        b.r(mx + 2, y0 + 3, 2, 1, "#5ee08a"); // prompt
        b.r(mx + 5, y0 + 3, 5, 1, "#2f9e5e");
        b.r(mx + 2, y0 + 5, 8, 1, "#2f9e5e");
        b.r(mx + 2, y0 + 7, 4, 1, shade("#2f9e5e", 0.8));
        b.r(mx + 2, y0 + 9, 9, 1, "#2f9e5e");
        b.r(mx + 2, y0 + 11, 3, 1, shade("#2f9e5e", 0.8));
        b.r(mx + 2, y0 + 13, 7, 1, "#2f9e5e");
        b.r(mx + 2, y0 + 15, 2, 1, "#5ee08a"); // cursor
      }
      b.r(mx + 1, y0 + 1, 4, 1, "#1f5236"); // reflexo do vidro
      b.r(mx + 1, y0 + 1, 1, 3, "#1f5236");

      // Pé: pescoço fino e placa de apoio POUSADA no tampo (daí o y=27).
      b.r(mx + 5, 23, 3, 4, metal);
      b.r(mx + 5, 23, 1, 4, metalHi);
      b.r(mx + 3, 27, 7, 1, metalDk);
    }

    // Teclado e rato de cada posto, na frente do tampo.
    b.r(cx - 10, 29, 18, 4, "#1e2229");
    b.r(cx - 10, 29, 18, 1, "#333a45");
    b.r(cx - 9, 30, 16, 2, "#c7cbd1");
    b.r(cx - 9, 30, 16, 1, "#e9ecef");
    for (let kx = cx - 8; kx < cx + 6; kx += 2) b.r(kx, 30, 1, 1, "#dadee2");
    b.r(cx - 5, 31, 7, 1, "#9aa0a7"); // barra de espaço
    b.r(cx + 9, 30, 4, 3, "#cfd3d8"); // rato
    b.r(cx + 9, 30, 4, 1, "#eceff2");
    b.r(cx + 10, 30, 1, 2, "#9aa0a7");
  }
}

/**
 * Cadeira de escritório ergonómica de malha, nas quatro vistas. detail=2 → 32×40.
 *
 * A malha só se vê por TRÁS (é o que a câmara apanha quando alguém está sentado
 * de costas); de frente vê-se o estofo. De perfil o encosto é um painel a cair
 * para trás com o apoio de cabeça em cima, e o braço deste lado atravessa por
 * diante do corpo — daí ser a única peça na camada da frente numa vista lateral.
 */
function drawOfficeChair(b: Brush, facing: Facing, layer: SeatLayer): void {
  const body = "#2c343f";
  const hi = "#4c5866";
  const sh = "#1b212a";
  const mesh = "#3b4552";
  const meshHi = "#556372";
  const metal = "#20262e";
  const metalHi = "#3c444e";
  const front = layer === "front";

  /** Apoio de cabeça almofadado (de frente/de trás). */
  const headrest = (): void => {
    b.r(9, 2, 14, 6, body);
    b.r(9, 2, 14, 1, hi);
    b.r(9, 3, 14, 1, mix(body, hi, 0.4));
    b.r(9, 7, 14, 1, sh);
    b.r(11, 4, 10, 2, mesh);
    b.r(14, 8, 4, 2, sh); // pescoço que liga ao encosto
  };

  /** Encosto com curva lombar: `weave` = malha (visto de trás) ou estofo (frente). */
  const backrest = (weave: boolean): void => {
    b.r(6, 9, 20, 18, body);
    b.r(6, 9, 20, 1, hi); // luz de cima
    b.r(6, 9, 1, 18, hi); // aresta esquerda iluminada
    b.r(25, 9, 1, 18, sh); // aresta direita sombreada
    b.r(6, 26, 20, 1, sh);
    if (weave) {
      b.r(9, 11, 14, 13, mesh); // malha central
      b.dither(9, 11, 14, 13, meshHi, mesh);
      for (let mx = 10; mx < 23; mx += 3) b.r(mx, 11, 1, 13, shade(mesh, 0.78)); // fios verticais
    } else {
      b.r(9, 11, 14, 13, mix(body, hi, 0.32)); // estofo da frente, sem malha à vista
      b.dither(10, 12, 12, 11, mix(body, hi, 0.5), mix(body, hi, 0.28));
      b.r(15, 11, 2, 13, sh); // vinco central do estofo
    }
    b.r(8, 20, 16, 2, hi); // barra de apoio lombar
    b.r(8, 22, 16, 1, sh);
  };

  /** Apoios de braço (aba + coluna), os dois. */
  const arms = (): void => {
    b.r(3, 14, 4, 2, body);
    b.r(3, 14, 4, 1, hi);
    b.r(2, 16, 2, 7, sh);
    b.r(25, 14, 4, 2, body);
    b.r(25, 14, 4, 1, hi);
    b.r(28, 16, 2, 7, sh);
  };

  /** Assento contornado. */
  const seat = (): void => {
    b.r(7, 27, 18, 5, body);
    b.r(7, 27, 18, 1, hi);
    b.r(7, 31, 18, 1, sh);
    b.dither(9, 28, 14, 3, mix(body, hi, 0.3), body);
  };

  /** Cilindro de gás + base em estrela de 5 pontas com rodízios (igual de todos os lados). */
  const pedestal = (): void => {
    b.r(14, 32, 4, 4, metal);
    b.r(14, 32, 1, 4, metalHi);
    b.r(8, 36, 16, 1, metal); // cubo central da estrela
    b.r(15, 36, 2, 1, metalHi);
    for (const cx of [4, 10, 15, 20, 25]) {
      b.r(cx + 1, 36, 1, 1, metal); // raio até ao rodízio
      b.r(cx, 37, 3, 2, "#13171d"); // rodízio
      b.r(cx, 37, 3, 1, metalHi);
    }
  };

  // ── Peças de perfil (autoradas viradas à ESQUERDA; a direita é o espelho).
  /** Encosto de perfil, a cair para trás, com o apoio de cabeça em cima. */
  const sideBackrest = (): void => {
    b.r(19, 9, 7, 6, body);
    b.r(18, 14, 7, 13, body);
    b.r(19, 9, 7, 1, hi);
    b.r(19, 9, 2, 5, mix(body, hi, 0.3)); // aresta virada à câmara
    b.r(18, 14, 2, 13, mix(body, hi, 0.35));
    b.r(25, 10, 1, 5, sh);
    b.r(24, 15, 1, 12, sh);
    b.r(18, 26, 7, 1, sh);
    b.r(19, 20, 5, 2, hi); // apoio lombar de perfil
    b.r(20, 2, 6, 7, body); // apoio de cabeça
    b.r(20, 2, 6, 1, hi);
    b.r(20, 3, 2, 6, mix(body, hi, 0.3));
    b.r(25, 3, 1, 6, sh);
    b.r(21, 8, 3, 2, sh); // haste do apoio de cabeça
  };

  /** Assento de perfil: bico dianteiro à esquerda. */
  const sideSeat = (): void => {
    b.r(5, 27, 19, 5, body);
    b.r(5, 27, 19, 1, hi);
    b.r(5, 31, 19, 1, sh);
    b.r(5, 28, 2, 3, mix(body, hi, 0.3)); // bico dianteiro iluminado
    b.dither(8, 28, 13, 3, mix(body, hi, 0.3), body);
  };

  /** O braço deste lado — a peça que passa por DIANTE de quem está sentado. */
  const sideArm = (): void => {
    b.r(7, 21, 15, 2, body);
    b.r(7, 21, 15, 1, hi);
    b.r(7, 23, 3, 6, sh); // poste dianteiro até ao assento
    b.r(7, 23, 1, 6, mix(body, hi, 0.25));
    b.r(20, 23, 2, 4, sh); // ligação ao encosto
  };

  if (facing === "up") {
    // De costas para a câmara: o encosto E os braços estão entre nós e o corpo.
    if (front) {
      backrest(true);
      arms();
      return;
    }
    headrest(); // atrás da nuca: só se vê na cadeira vazia
    seat();
    pedestal();
    return;
  }
  if (facing === "down") {
    // De frente: o corpo tapa a cadeira toda. Nada passa à frente dele.
    if (front) return;
    headrest();
    backrest(false);
    arms();
    seat();
    pedestal();
    return;
  }
  // De perfil: o encosto fica ao LADO das costas — quem passa à frente é o braço.
  if (front) {
    sideArm();
    return;
  }
  sideBackrest();
  sideSeat();
  pedestal();
}

/**
 * Mesa de reunião oval com bisel, veio, passa-cabos e 3 pernas. detail=2 → 96×72.
 *
 * Plano B do `conference-table-v1.png`, e desenhado à MESMA medida final que ele:
 * se os dois planos tivessem tamanhos diferentes, a sala mudava de composição
 * consoante o PNG carregasse ou não — e um plano B que muda a sala não é um plano
 * B, é uma segunda sala.
 */
function drawMeetingTable(b: Brush): void {
  const wood = "#7c5430";
  const woodHi = "#9c6d41";
  const woodMid = "#8a5f38";
  const woodSh = "#5a3c22";
  const woodDk = "#3c2717";
  // ── Corpo oval (rectângulo alargado por bandas → cantos redondos).
  b.r(8, 14, 80, 36, wood);
  b.r(4, 20, 88, 24, wood);
  b.r(12, 10, 72, 44, wood);
  b.r(8, 14, 80, 2, woodHi); // aresta de cima iluminada
  b.r(12, 10, 72, 2, woodHi);
  b.r(4, 20, 3, 24, mix(wood, woodHi, 0.5)); // flanco esq iluminado
  b.dither(14, 18, 68, 28, woodMid, wood); // veio
  b.r(22, 24, 52, 2, mix(woodHi, wood, 0.35)); // reflexo comprido
  b.r(28, 34, 40, 1, mix(woodHi, wood, 0.3));
  b.r(8, 51, 80, 3, woodDk); // bisel/sombra inferior
  b.r(89, 20, 3, 24, woodSh); // flanco dir sombreado
  // ── Passa-cabos (2).
  for (const gx of [33, 59]) {
    b.r(gx, 30, 4, 3, "#2a2119");
    b.r(gx, 30, 4, 1, "#4a3d30");
  }
  // ── 3 pernas + sapatas nas pontas.
  for (const lx of [12, 45, 78]) {
    b.r(lx, 54, 6, 17, woodSh);
    b.r(lx, 54, 2, 17, woodMid);
    b.r(lx + 4, 54, 2, 17, woodDk);
  }
  for (const lx of [12, 78]) b.r(lx, 69, 6, 2, "#241812");
}

/**
 * Cadeira de reunião estofada, nas quatro vistas. detail=2 → 32×36.
 *
 * Não tem braços — logo, de perfil NADA passa à frente de quem lá está: a camada
 * da frente fica vazia e o encosto desenha-se de canto, ao lado das costas.
 *
 * A paleta é a VERDE-AZEITONA do pacote de arte (amostrada do próprio PNG da mesa
 * de reunião), não a cinzenta-azulada de antes: estas cadeiras passaram a rodear
 * uma mesa que vem de lá. Base em metal escuro em vez de crómio brilhante — o
 * crómio destoava do resto do pacote. Fora da sala de reunião não aparecem: a TI
 * tem as suas `officeChair` ergonómicas e é com elas que a sala é coerente.
 */
function drawMeetingChair(b: Brush, facing: Facing, layer: SeatLayer): void {
  const body = "#335800";
  const hi = "#5f8c02";
  const sh = "#234900";
  const dk = "#0e1a00";
  const pad = "#3d6801";
  const chrome = "#2a2f26";
  const chromeHi = "#4d5347";
  const front = layer === "front";

  /** Encosto almofadado (cápsula) + a haste que o liga ao assento. */
  const backrest = (): void => {
    b.r(7, 3, 18, 15, body);
    b.r(9, 1, 14, 3, body); // topo arredondado
    b.r(7, 3, 18, 2, hi);
    b.r(7, 3, 2, 15, hi);
    b.r(23, 3, 2, 15, sh);
    b.r(7, 16, 18, 2, sh);
    b.r(10, 5, 12, 10, pad); // estofo central
    b.dither(11, 6, 10, 8, mix(pad, hi, 0.3), pad);
    b.r(15, 5, 1, 10, sh); // vinco central
    b.r(14, 18, 4, 4, sh); // haste
  };

  /** Assento. */
  const seat = (): void => {
    b.r(8, 21, 16, 5, body);
    b.r(8, 21, 16, 2, hi);
    b.r(8, 25, 16, 1, sh);
    b.dither(10, 22, 12, 3, mix(body, hi, 0.25), body);
  };

  /** Coluna + base cromada de pernas com rodízios (uma estrela lê-se igual de todo o lado). */
  const base = (): void => {
    b.r(15, 26, 2, 4, chrome);
    b.r(15, 26, 1, 4, chromeHi);
    b.r(9, 30, 14, 1, chrome);
    for (const cx of [8, 15, 22]) {
      b.r(cx, 30, 2, 4, chrome);
      b.r(cx, 30, 1, 4, chromeHi);
      b.r(cx, 33, 3, 2, dk);
    }
  };

  if (facing === "up") {
    // A cápsula inteira tapa as costas; só a cabeça passa por cima dela.
    if (front) {
      backrest();
      return;
    }
    seat();
    base();
    return;
  }
  if (facing === "down") {
    if (front) return;
    backrest();
    seat();
    base();
    return;
  }
  // Perfil (autorado à esquerda): a cápsula de canto, atrás das costas.
  if (front) return;
  b.r(19, 2, 6, 16, body);
  b.r(20, 1, 4, 2, body); // topo arredondado, de canto
  b.r(19, 2, 6, 1, hi);
  b.r(19, 2, 2, 16, mix(body, hi, 0.35)); // face virada à câmara
  b.r(24, 3, 1, 15, sh);
  b.r(19, 17, 6, 1, sh);
  b.r(20, 5, 3, 10, pad); // estofo visto de lado
  b.r(17, 18, 4, 4, sh); // haste
  b.r(6, 21, 18, 5, body); // assento de perfil, bico dianteiro à esquerda
  b.r(6, 21, 18, 2, hi);
  b.r(6, 25, 18, 1, sh);
  b.r(6, 22, 2, 3, mix(body, hi, 0.3));
  b.dither(9, 22, 12, 3, mix(body, hi, 0.25), body);
  base();
}

/** Quadro branco com rabiscos coloridos e calha de marcadores. w4 (64 art). */
function drawWhiteboard(b: Brush): void {
  b.r(2, 2, 60, 20, "#c8ccd2"); // moldura
  b.r(4, 4, 56, 15, "#f6f8fa"); // superfície
  b.r(4, 4, 56, 1, "#ffffff"); // brilho de cima
  b.r(4, 18, 56, 1, "#d6dade");
  b.r(8, 8, 18, 1, "#2f9be0"); // rabiscos
  b.r(8, 11, 12, 1, "#e8574f");
  b.r(30, 7, 20, 1, "#22b364");
  b.r(30, 10, 14, 1, "#f0a53a");
  b.r(30, 13, 22, 1, "#2f9be0");
  b.r(8, 14, 9, 1, "#8b5cf6");
  b.r(6, 20, 52, 2, "#aeb3ba"); // calha
  b.r(12, 21, 5, 1, "#e8574f"); // marcadores
  b.r(20, 21, 5, 1, "#2f9be0");
}

/**
 * Sofá de 3 lugares: encosto + assento almofadados, braços com volume.
 * detail=2 → 96×46, a medida do `couch-v1.png`.
 *
 * As faixas não são um gosto — vieram do perfil de luminância do PNG, para os
 * dois planos lerem igual: encosto y 0..18 · costura escura y 19 · almofadas do
 * assento y 20..28 (a faixa clara) · saia y 29..41 · pés y 42..45.
 * É a linha 20 que o `CUSHION_TOP.couch = 26` conta a partir do fundo,
 * portanto quem mexer nela tem de mexer lá também ou o boneco senta-se no ar.
 */
function drawCouch(b: Brush): void {
  const body = "#556880";
  const hi = shade(body, 1.2);
  const sh = shade(body, 0.78);
  const dk = shade(body, 0.58);
  const cush = "#62758d";
  const cushHi = shade(cush, 1.16);
  const cushSh = shade(cush, 0.8);
  const seam = shade(body, 0.62);
  // ── Encosto (3 almofadas com costura). Começa em y=0: no render o topo do
  // encosto É o topo da arte, e os 2 px de folga que aqui havia eram folga de
  // uma tela de 48 que já não existe.
  b.r(10, 0, 76, 19, body);
  b.r(10, 0, 76, 2, hi);
  for (let i = 0; i < 3; i++) {
    const cx = 12 + i * 25;
    b.r(cx, 2, 22, 15, cush);
    b.r(cx, 2, 22, 2, cushHi);
    b.r(cx, 15, 22, 2, cushSh);
    b.r(cx, 2, 2, 15, shade(cush, 1.08));
    b.dither(cx + 3, 5, 16, 9, mix(cush, cushHi, 0.3), cush);
    if (i < 2) b.r(cx + 23, 1, 1, 17, seam);
  }
  b.r(10, 19, 76, 1, seam); // a costura escura encosto/assento — y=19 no PNG
  // ── Braços (volume arredondado). Mais baixos que o encosto e a descer até à
  // linha dos pés, como no render — pararem antes deixava um degrau vazio nos
  // cantos de baixo que o PNG não tem.
  for (const ax of [0, 86]) {
    b.r(ax, 6, 10, 36, body);
    b.r(ax, 6, 10, 3, hi);
    b.r(ax, 6, 2, 36, ax === 0 ? hi : body);
    b.r(ax + 8, 8, 2, 34, sh);
    b.r(ax, 40, 10, 2, dk);
  }
  // ── Almofadas do assento (3, com vinco e frente). O topo em y=20 é o número
  // que o `CUSHION_TOP.couch` lê ao contrário: 46 − 20 = 26.
  b.r(10, 20, 76, 13, body);
  for (let i = 0; i < 3; i++) {
    const cx = 11 + i * 25;
    b.r(cx, 20, 23, 9, cush);
    b.r(cx, 20, 23, 2, cushHi);
    b.r(cx, 27, 23, 2, cushSh);
    b.dither(cx + 2, 22, 19, 5, mix(cush, cushHi, 0.25), cush);
    if (i < 2) b.r(cx + 23, 20, 1, 9, seam);
  }
  b.r(10, 29, 76, 4, sh); // sombra sob as almofadas
  // ── Saia/base + pés de madeira. Sem barra clara: a linha luminosa que o perfil
  // do PNG marca em y=40 é o BORDO do estofo a apanhar luz, não uma calha — pintá-la
  // a claro punha uma faixa branca a atravessar o sofá todo.
  b.r(10, 33, 76, 9, dk);
  b.r(10, 33, 76, 1, sh);
  for (const fx of [12, 79]) {
    b.r(fx, 42, 5, 4, "#241d17");
    b.r(fx, 42, 5, 1, "#3a2e22");
  }
}

/** Quantas fatias tem o sofá de perfil — uma por linha de chão da pegada 1×3. */
export const COUCH_SIDE_SLICES = 3;

/**
 * O MESMO sofá de três lugares visto de PERFIL, para encostar a uma parede
 * lateral: encosto contra a parede da direita, assento virado para dentro da
 * sala. detail=2 → 40×60 POR FATIA.
 *
 * ── Porquê fatiado, e não uma imagem só ──────────────────────────────────────
 * O y-sort da cena dá UMA profundidade por imagem, tirada da linha de baixo da
 * pegada (`baseY = (row + h) * TILE`). Numa peça de três linhas isso punha o
 * sofá inteiro à frente de quem se sentasse nas duas linhas de trás: duas das
 * três pessoas desapareciam por trás do próprio móvel onde se tinham sentado.
 * Cada fatia leva a profundidade da SUA linha, portanto quem se senta fica à
 * frente da sua fatia e atrás da seguinte — que é o que a vista de 3/4 pede.
 *
 * ── A geometria que o desenho tem de respeitar ───────────────────────────────
 * As três fatias montam um COMPOSTO de 40×124: cada uma assenta na base do SEU
 * tile, logo o fundo da fatia i fica 32 px abaixo do fundo da i−1, e um composto
 * de N fatias mede 32·(N−1) + h. A fatia i mostra as linhas do composto
 * `local + 32·i` (a última) — e é por isso que cada fatia só pinta a SUA banda:
 *
 *   fatia 0 → composto 0..45   (local 0..45)   braço norte + almofada 1
 *   fatia 1 → composto 45..77  (local 13..45)  almofada 2
 *   fatia 2 → composto 77..124 (local 13..60)  almofada 3 + braço sul + base
 *
 * As fronteiras caem nas COSTURAS das almofadas e não em marcas de 32 px de
 * propósito. Com marcas de 32, a fatia i+1 pintava o terço de baixo da almofada
 * DA fatia i — e como leva mais profundidade, pintava-o À FRENTE de quem lá
 * estivesse sentado. Era exactamente o bug que a fatiagem existe para não ter.
 * Cada linha do composto é pintada por UMA fatia só: nada se sobrepõe, nada se
 * soma duas vezes.
 *
 * E a "saia" corre ao COMPRIDO, na banda oeste, não em baixo: de perfil, a
 * frente do assento é o lado esquerdo. Trocar isso era desenhar o sofá de
 * frente deitado de lado, que é outra coisa.
 *
 * Bandas em x (o tile ocupa 4..36 dos 40; o resto transborda 4 para cada lado —
 * é a profundidade que a pegada 1×3 não tem e que a arte finge), medidas no
 * render de origem: saia 2..5 · almofada do assento 5..23 · vinco 23..27 ·
 * encosto 27..37 · sombra contra a parede 37..39.
 */
function drawCouchSide(b: Brush, fatia: number): void {
  const body = "#556880";
  const hi = shade(body, 1.2);
  const sh = shade(body, 0.78);
  const dk = shade(body, 0.58);
  const cush = "#62758d";
  const cushHi = shade(cush, 1.16);
  const cushSh = shade(cush, 0.8);
  const seam = shade(body, 0.62);
  const topo = fatia === 0;
  const fundo = fatia === COUCH_SIDE_SLICES - 1;
  // Onde a banda desta fatia começa, em pixels locais: a primeira arranca no
  // topo da peça, as outras 13 px abaixo, porque esses 13 já pertencem à fatia
  // anterior (ver o mapa de posse acima).
  const y0 = topo ? 0 : 13;
  // E onde a almofada acaba: em local 45 nas TRÊS. Não é coincidência — as
  // costuras do composto estão a 45, 77 e 109, e 77−32 = 45, 109−64 = 45.
  const FIM = 45;

  // ── Estofo deste lugar: encosto contra a parede, assento e saia a oeste.
  const alt = FIM - y0;
  b.r(27, y0, 10, alt, body);
  b.r(27, y0, 2, alt, hi); // a aresta virada à sala apanha a luz
  b.r(37, y0, 2, alt, sh); // a que fica contra a parede, não
  b.r(2, y0, 25, alt, body);
  b.r(2, y0, 3, alt, dk); // a frente do estofo, em contraluz
  b.r(3, y0, 2, alt, hi);
  b.r(23, y0, 4, alt, sh); // vinco onde o assento encontra o encosto

  // ── A almofada: começa depois do braço na primeira fatia, e nas outras enche
  // a banda toda. O ponto de sentar cai em local 34 (`CUSHION_TOP` = 26 sobre um
  // chão em 60), a dois terços da almofada nas três — foi assim que as medidas
  // do render se escolheram.
  const cy = topo ? 14 : y0;
  const calt = FIM - cy;
  b.r(5, cy, 18, calt, cush);
  b.r(5, cy, 2, calt, cushHi); // bordo iluminado, do lado da sala
  b.r(21, cy, 2, calt, cushSh);
  b.dither(7, cy + 2, 14, calt - 4, mix(cush, cushHi, 0.3), cush);
  b.r(29, cy, 7, calt, cush); // almofada do encosto, mais estreita e recuada
  b.r(29, cy, 2, calt, cushHi);
  b.dither(31, cy + 2, 4, calt - 4, mix(cush, cushHi, 0.25), cush);
  // A costura entre almofadas cai no topo da banda: é a única linha horizontal
  // do móvel, e é ela que faz ler TRÊS lugares e não um banco corrido.
  if (!topo) b.r(5, y0, 31, 1, seam);

  // ── Braços: são as duas pontas do COMPRIMENTO, logo há um em cada extremo da
  // peça inteira — não um por fatia.
  if (topo) {
    b.r(2, 0, 36, 14, body);
    b.r(2, 0, 36, 4, hi);
    b.r(2, 11, 36, 3, sh); // a sombra que o braço deita sobre a primeira almofada
  }
  if (fundo) {
    b.r(2, FIM, 36, 11, body);
    b.r(2, FIM, 36, 3, hi);
    // Base escura a todo o comprimento: no render os pés fundem-se nela a esta
    // escala (40 px de largo para um móvel de 429), e inventar dois pés soltos
    // era desenhar uma peça diferente do PNG que isto substitui num 404.
    b.r(2, 56, 36, 4, "#241d17");
    b.r(2, 56, 36, 1, "#3a2e22");
  }
}

/** Mesa de centro em madeira com cantos suaves, veio e objetos. detail=2 → 64×32. */
function drawCoffeeTable(b: Brush): void {
  const wood = "#6b4a30";
  const woodHi = "#8f6543";
  const woodMid = "#7a5537";
  const woodSh = "#4a3220";
  const woodDk = "#2c1d12";
  // ── Tampo (rectângulo alargado ao meio → cantos redondos).
  b.r(4, 12, 56, 8, wood);
  b.r(2, 14, 60, 4, wood);
  b.r(4, 12, 56, 2, woodHi);
  b.r(2, 14, 2, 4, mix(wood, woodHi, 0.5));
  b.dither(8, 14, 48, 4, woodMid, wood);
  b.r(10, 15, 40, 1, mix(woodHi, wood, 0.4)); // reflexo de verniz
  b.r(4, 19, 56, 1, woodDk);
  b.r(2, 17, 60, 1, woodSh);
  // ── 4 pernas (frente iluminadas, trás em sombra).
  for (const lx of [6, 54]) {
    b.r(lx, 20, 4, 10, woodSh);
    b.r(lx, 20, 1, 10, woodMid);
    b.r(lx + 3, 20, 1, 10, woodDk);
  }
  for (const lx of [14, 46]) b.r(lx, 20, 3, 7, shade(woodSh, 0.9));
  // ── Objetos: pilha de revistas + caneca de café.
  b.r(12, 8, 18, 4, "#c34a52");
  b.r(12, 8, 18, 1, "#e0787e");
  b.r(14, 6, 16, 3, "#2f7fb8");
  b.r(14, 6, 16, 1, "#5ba6d8");
  b.r(16, 7, 10, 1, "#dfeaf3");
  b.r(44, 7, 6, 5, "#e8ede8");
  b.r(44, 7, 6, 1, "#ffffff");
  b.r(49, 8, 2, 2, "#cdd4cd"); // asa
  b.r(45, 8, 4, 1, "#6b4a30"); // café
}

/**
 * A televisão da Sala de Descanso vista de TRÁS — plano B do `tv-v1.png`.
 *
 * De trás, e não de frente com o ecrã aceso, porque é isso que a câmara vê: o
 * sofá está na row 18 com o encosto a norte (quem lá se senta olha para SUL) e a
 * TV na row 22, encostada à parede sul a olhar para o sofá — ver o comentário da
 * lounge em `space.ts`. Do lado do jogador só há costas de aparelho. A versão
 * anterior desenhava um ecrã aceso com céu, colinas e passarinhos: bonito e
 * virado ao contrário, uma televisão a dar imagem para a parede.
 *
 * detail=2 → grelha de 84×54, a MESMA medida do PNG (contrato em `PECAS`, com
 * teste a confrontar as duas). O aparelho ocupa y=0..48 e o pé com a base os
 * últimos 6 — que é o que põe o painel a subir pela parede em vez de o pé
 * avançar para o chão de quem passa.
 *
 * Foi autorado a 96×62 e reduzido a 84×54 em 2026-08-31 («diminua um pouco o
 * tamanho da tv»). Redesenhado na grelha nova, não escalado: escalar arte de
 * pixel por 0,875 parte as linhas de 1 px — as nervuras, as grelhas de ventilação
 * e os bicos RCA são todos finos, e a 0,875 desapareciam ou ficavam a dois tons.
 */
function drawTV(b: Brush): void {
  const aro = "#3c424a";
  const aroEsq = "#373c42";
  const aroDir = "#2b3037";
  const corpo = "#22272d";
  const covo = "#1c2026";
  const funda = "#141519";
  const nervura = "#363b44";
  const grelha = "#26282d";
  const rodape = "#2f343e";

  // ── Painel traseiro: aro a toda a volta, tampa escura lá dentro.
  // As duas arestas verticais são AMBAS mais escuras do que a face do aro, e é
  // isso que se lê no render: a luz da sala cai de cima, não de lado.
  b.r(0, 0, 84, 48, aro);
  b.r(0, 2, 3, 46, aroEsq);
  b.r(81, 2, 3, 46, aroDir);
  b.r(3, 3, 78, 43, corpo);
  b.r(3, 46, 78, 2, aroDir);

  // ── Ventilação do topo: duas grelhas com uma nervura a separá-las.
  for (const y of [6, 10]) {
    b.r(7, y, 70, 3, grelha);
    b.dither(7, y, 70, 3, grelha, covo);
  }
  b.r(7, 9, 70, 1, nervura);

  // ── O covo onde moram as ventoinhas e as ligações.
  b.r(11, 13, 61, 28, covo);

  // ── Duas ventoinhas empilhadas à esquerda. Redondas a sério: meia-corda por
  // linha. Um quadrado com o canto cortado lia-se como caixa, não como hélice.
  //
  // Raio 5 por fora e 2 por dentro (era 6/3): o anel de pá fica nos mesmos 3 px
  // que tinha. Manter o 3 interior na grelha pequena deixava 2 px de pá e a
  // ventoinha lia-se como argola.
  for (const cy of [18, 30]) {
    for (let y = -5; y <= 5; y += 1) {
      const meia = Math.round(Math.sqrt(Math.max(0, 25 - y * y)));
      if (meia > 0) b.r(21 - meia, cy + y, meia * 2, 1, funda);
    }
    for (let y = -2; y <= 2; y += 1) {
      const meia = Math.round(Math.sqrt(Math.max(0, 4 - y * y)));
      if (meia > 0) b.r(21 - meia, cy + y, meia * 2, 1, grelha);
    }
    b.r(19, cy - 1, 4, 3, nervura);
  }

  // ── Placa de etiqueta ao meio: o rectângulo claro que se vê nas costas de
  // qualquer aparelho, com as três linhas de letra miúda que não se lêem.
  b.r(36, 21, 12, 9, "#272c32");
  b.r(36, 21, 12, 1, nervura);
  b.r(38, 24, 9, 1, "#515459");
  b.r(38, 26, 7, 1, "#515459");
  b.r(38, 28, 5, 1, "#45484d");

  // ── Painel de ligações, à direita: HDMI em cima, RCA a cor no meio, antena e
  // rede em baixo. A cor dos RCA é o único sítio deste desenho que não é cinza.
  b.r(52, 15, 20, 24, "#28242f");
  b.r(52, 15, 20, 1, nervura);
  for (let i = 0; i < 3; i += 1) b.r(55 + i * 6, 17, 4, 3, "#7f807c");
  b.r(55, 23, 4, 4, "#c8b24a");
  b.r(61, 23, 4, 4, "#c9cbc6");
  b.r(67, 23, 4, 4, "#a44b47");
  b.r(55, 30, 5, 4, "#3a3f47");
  b.r(63, 30, 7, 4, "#3a3f47");

  // ── Cabos a cair do painel para o chão, com a curva que um cabo tem.
  for (let y = 0; y < 19; y += 1) {
    b.r(73 + Math.round(Math.sin(y / 4) * 2), 26 + y, 3, 1, "#191b21");
  }

  // ── Rodapé: a faixa de baixo apanha mais luz do que o resto das costas.
  b.r(3, 39, 78, 9, rodape);
  b.r(3, 39, 78, 1, nervura);
  b.dither(11, 41, 35, 5, rodape, corpo);

  // ── Pé e base, no azul do suporte. Centrados nos 42 do meio da grelha — o
  // desenho de 96 trazia-os 2 px à direita do centro, que era deriva de autoria.
  b.r(37, 43, 10, 7, "#26446a");
  b.r(37, 43, 2, 7, "#31578a");
  b.r(21, 49, 42, 5, "#376793");
  b.r(21, 49, 42, 1, "#417eb2");
  b.r(21, 52, 42, 2, "#273753");
}

/**
 * Marca GV — casa de telhado inclinado com a folha a brotar do cume, em verde
 * sobre o fundo que já lá está.
 *
 * Não é o `drawGVLeaf`: esse traz chapa branca redonda própria (é um crachá, para
 * assentar sobre madeira ou relva). Este é a marca NUA, para pousar num painel
 * creme que já faz de fundo — que é como ela vem nos ficheiros do pacote, tanto
 * no balcão da recepção como no cartaz do parque.
 *
 * Grelha de 9×8 unidades escalada por `u`, e é de propósito que as duas peças
 * partilham a função: o cartaz leva-a a u=4 (36×32) e o balcão a u=2 (18×16). Se
 * um dia divergirem de forma, é porque alguém voltou a desenhar uma delas à mão.
 */
function marcaGV(b: Brush, x: number, y: number, u: number): void {
  const g = "#1f8a4d";
  const gHi = "#43c07a";
  const gDk = "#12633a";
  const p = (cx: number, cy: number, w: number, h: number, c: string): void =>
    b.r(x + cx * u, y + cy * u, w * u, h * u, c);
  // ── Telhado: duas águas em degraus a descer do cume até ao beiral.
  p(3, 1, 3, 1, g);
  p(2, 2, 5, 1, g);
  p(1, 3, 7, 1, g);
  p(0, 4, 9, 1, gHi); // beiral, a fiada iluminada
  // ── Corpo: duas paredes e o VÃO da porta entre elas, que deixa ver o fundo.
  p(1, 5, 2, 2, g);
  p(6, 5, 2, 2, g);
  p(0, 7, 9, 1, gDk); // soleira
  // ── Folha, por cima do telhado, a subir do cume para a direita.
  p(4, 0, 3, 1, gHi);
  p(3, 1, 4, 1, gHi);
  p(4, 2, 4, 1, gHi);
  p(5, 3, 3, 1, gHi);
  p(6, 4, 1, 1, gDk); // ponta a pousar no beiral
  p(3, 1, 1, 3, gDk); // nervura a descer ao cume
}

/**
 * Balcão de recepção: bancada creme parede a parede, corpo em madeira com
 * gavetas, painel de marca ao meio, monitor e planta em cima. detail=2 → 96×52.
 *
 * Reescrito de raiz quando entrou o PNG do pacote. Era 128×56 sobre 4 tiles; é
 * 96×52 sobre 3, e um balcão que ENCOLHE na horizontal não se resolve com um
 * invólucro que desloca o pincel — só um desenho que se estreita. As bandas
 * saíram MEDIDAS do ficheiro final por mapa de cores, não a olho:
 *
 * ```
 * y  0..12  planta x5..19 · monitor x66..87 (ecrã y3..11) · pé y13..16
 * y 13..20  tampo creme de parede a parede · placa verde x34..61
 * y 21..25  friso de madeira + faixa de marca
 * y 26..47  gavetas x2..21 e x74..93 · pilastras x22..27 e x68..73 · painel x28..67
 * y 48..51  rodapé escuro
 * ```
 */
function drawReceptionDesk(b: Brush): void {
  const wood = "#6b4a2b";
  const woodHi = "#8a6238";
  const woodDk = "#3b2717";
  const woodEdge = "#1c1208";
  const topC = "#ded8c9";
  const topHi = "#f2ede1";
  const topSh = "#b6ae9c";
  const green = "#1f8a4d";
  const greenHi = "#43c07a";
  const greenDk = "#12633a";
  const gold = "#c8a046";
  const cream = "#ebe6d7";
  // ── Corpo, e a faixa de marca a cavalo no topo dele.
  b.r(2, 21, 92, 31, wood);
  b.r(2, 21, 92, 1, woodHi);
  b.r(2, 22, 92, 4, green);
  b.r(2, 22, 92, 1, greenHi);
  b.r(2, 25, 92, 1, greenDk);
  // ── Gavetas nas duas pontas: o vão do meio é painel, não gaveta.
  for (const gx of [3, 75]) {
    b.r(gx, 27, 18, 20, woodDk);
    b.r(gx, 27, 18, 1, woodHi);
    b.r(gx, 27, 1, 20, woodHi);
    b.r(gx + 17, 27, 1, 20, woodEdge);
    b.r(gx, 36, 18, 1, woodEdge); // junta entre as duas gavetas
    b.r(gx + 5, 32, 8, 1, gold);
    b.r(gx + 5, 41, 8, 1, gold);
  }
  // ── Pilastras verdes a emoldurar o painel de marca.
  for (const px of [22, 68]) {
    b.r(px, 26, 6, 22, green);
    b.r(px, 26, 1, 22, greenHi);
    b.r(px + 5, 26, 1, 22, greenDk);
    b.r(px + 2, 29, 2, 16, gold);
  }
  // ── Painel de marca: logótipo e as duas linhas do letreiro.
  b.r(28, 26, 40, 22, cream);
  b.r(28, 26, 40, 1, "#f7f3e6");
  b.r(28, 47, 40, 1, "#c3bca8");
  marcaGV(b, 39, 27, 2);
  b.r(33, 44, 30, 2, green); // "GREEN VILLAGE"
  b.r(38, 46, 20, 1, greenDk); // "MOBILE HOMES"
  // ── Rodapé.
  b.r(3, 48, 90, 4, woodDk);
  b.r(3, 48, 90, 1, woodEdge);
  // ── Bancada, que sai por cima da frente e vai de parede a parede.
  b.r(0, 13, 96, 8, topC);
  b.r(0, 13, 96, 2, topHi);
  b.r(0, 19, 96, 2, topSh);
  b.dither(4, 16, 88, 2, shade(topC, 1.03), topC);
  b.r(34, 15, 28, 3, greenDk); // placa de atendimento pousada no tampo
  b.r(34, 15, 28, 1, green);
  // ── Monitor da recepcionista, virado para dentro.
  b.r(66, 1, 22, 12, "#1b1f26");
  b.r(67, 3, 20, 9, "#25506e");
  b.dither(67, 3, 20, 9, "#2f6f96", "#245066");
  b.r(67, 3, 20, 1, "#6fb6dd");
  b.r(74, 13, 11, 3, "#2a2f37"); // pé
  b.r(76, 16, 7, 1, "#1b1f26");
  // ── Planta na ponta esquerda da bancada.
  b.r(9, 0, 6, 2, "#5fd07f");
  b.r(6, 2, 12, 3, "#3f8a52");
  b.r(5, 4, 14, 4, "#347546");
  b.dither(6, 3, 12, 4, "#4b9c60", "#347546");
  b.r(8, 8, 8, 2, "#2a5c39");
  b.r(8, 10, 8, 4, "#9a6440"); // vaso
  b.r(8, 10, 8, 1, "#b87a4e");
  b.r(8, 13, 8, 1, "#6d4529");
}

/**
 * Massa de folhas arredondada e orgânica: um corpo central que se alarga ao meio
 * (cantos recuados → contorno redondo, não rectângulo) + pontas de folha soltas no
 * topo e o topo iluminado. É o TIJOLO de toda a folhagem — plantas, árvore, sebes —
 * o que dá "vida" em vez de blocos verdes chapados: sobrepõem-se vários cachos.
 */
function leafBlob(b: Brush, x: number, y: number, w: number, h: number, col: string): void {
  b.r(x + 1, y, w - 2, h, col); // corpo
  b.r(x, y + 1, w, h - 2, col); // alarga ao meio → arredonda os cantos
  b.r(x + 1, y, w - 2, 1, shade(col, 1.16)); // aresta de cima iluminada
  // pontas de folha a espreitar por cima (silhueta recortada)
  b.r(x + 2, y - 1, 2, 1, col);
  b.r(x + Math.floor(w / 2) - 1, y - 2, 2, 2, col);
  b.r(x + w - 4, y - 1, 2, 1, col);
}

/** Vaso de terracota reutilizável (plantas): rebordo, luz/sombra, terra. */
function drawPot(b: Brush, x: number, y: number, w: number, h: number): void {
  const pot = "#a86140";
  const potHi = "#c9835b";
  const potSh = "#7c4529";
  b.r(x + 1, y + 2, w - 2, h - 2, pot);
  b.r(x - 1, y, w + 2, 3, potHi); // rebordo saliente
  b.r(x - 1, y, w + 2, 1, "#dd9871"); // brilho do rebordo
  b.r(x - 1, y + 2, w + 2, 1, potSh); // sombra sob o rebordo
  b.r(x + 1, y + 2, 2, h - 2, potHi); // luz esquerda
  b.r(x + w - 3, y + 2, 2, h - 2, potSh); // sombra direita
  b.r(x + 1, y + h - 1, w - 2, 1, "#5a3420"); // base
  b.r(x + 2, y + Math.floor(h / 2) + 1, w - 4, 1, shade(pot, 1.12)); // anel
  b.r(x + 1, y + 1, w - 2, 2, "#4a2c19"); // terra
}

/** Planta de vaso frondosa: folhas em cachos sobrepostos + rim light. detail=2 → 32×44. */
function drawPlant(b: Brush): void {
  const g = "#2f8a4c";
  const gMid = "#3f9e5b";
  const gHi = "#63c77e";
  const gRim = "#8ff0a4";
  const gSh = "#1c6337";
  const gDk = "#124a28";
  drawPot(b, 9, 33, 14, 10);
  // ── Folhagem: cachos sobrepostos, de trás (escuro) para a frente (claro).
  const clusters: [number, number, number, number, string][] = [
    [8, 20, 16, 13, gSh], // massa de fundo (sombra)
    [5, 14, 12, 13, g], // fronde esquerda
    [15, 12, 12, 14, g], // fronde direita
    [10, 5, 12, 12, gMid], // topo
    [11, 17, 10, 11, gMid], // centro-frente
  ];
  for (const [x, y, w, h, col] of clusters) leafBlob(b, x, y, w, h, col);
  // veios / separação de folhas
  b.r(15, 7, 1, 22, gSh);
  b.r(10, 16, 5, 1, gSh);
  b.r(16, 20, 5, 1, gSh);
  b.r(10, 24, 5, 1, gSh);
  // luz de cima (rim) + destaques (canto sup-esq)
  b.r(11, 4, 6, 1, gRim);
  b.r(7, 15, 1, 9, gHi);
  b.r(12, 7, 3, 2, gHi);
  b.r(17, 13, 2, 3, gHi);
  b.r(10, 19, 3, 2, gHi);
  // sombra interior (baixo-direita)
  b.r(19, 22, 5, 7, gDk);
  b.r(10, 28, 10, 2, gDk);
}

/** Árvore de interior: tronco lenhoso curvo + copa em cachos. detail=2 → 32×72. */
function drawPlantTall(b: Brush): void {
  const g = "#2f8a4c";
  const gMid = "#3f9e5b";
  const gHi = "#63c77e";
  const gRim = "#8ff0a4";
  const gSh = "#1c6337";
  const gDk = "#114826";
  drawPot(b, 10, 58, 12, 12);
  // ── Tronco lenhoso com ramos e veio.
  const bark = "#6f4a2c";
  const barkHi = "#8a5f39";
  const barkSh = "#4a3120";
  b.r(14, 40, 4, 18, bark);
  b.r(14, 40, 1, 18, barkHi); // luz
  b.r(17, 40, 1, 18, barkSh); // sombra
  b.dither(15, 44, 2, 12, barkSh, bark);
  b.r(11, 45, 3, 2, bark); // ramo esq
  b.r(11, 46, 3, 1, barkSh);
  b.r(18, 41, 3, 2, bark); // ramo dir
  b.r(20, 41, 2, 2, barkSh);
  // ── Copa: cachos grandes sobrepostos.
  const clusters: [number, number, number, number, string][] = [
    [4, 18, 24, 22, gSh], // massa de fundo
    [3, 24, 12, 14, g], // fronde esq
    [17, 22, 12, 15, g], // fronde dir
    [8, 8, 16, 16, gMid], // corpo alto
    [11, 3, 12, 12, gMid], // topo
    [10, 20, 12, 12, g], // centro
  ];
  for (const [x, y, w, h, col] of clusters) leafBlob(b, x, y, w, h, col);
  // separação de folhas
  b.r(15, 5, 1, 34, gSh);
  b.r(8, 16, 6, 1, gSh);
  b.r(17, 22, 6, 1, gSh);
  b.r(8, 30, 6, 1, gSh);
  // luz de cima + destaques (rim light canto sup-esq)
  b.r(12, 3, 7, 1, gRim);
  b.r(6, 24, 1, 10, gHi);
  b.r(11, 7, 4, 2, gHi);
  b.r(18, 14, 3, 3, gHi);
  b.r(9, 22, 3, 2, gHi);
  // sombra interior baixo-direita
  b.r(20, 30, 7, 9, gDk);
  b.r(9, 36, 12, 2, gDk);
}

/**
 * Bancada de cozinha com armários, lava-loiça e máquina de café. w4 (64×29 art).
 *
 * Os 5 cortes que a grelha ganhou quando o PNG entrou foram TODOS para o corpo
 * dos armários, e não repartidos pelo desenho todo: o tampo, o lava-loiça e a
 * máquina de café estão ancorados ao TOPO da bancada e essa altura não mudou —
 * o que a arte nova mostra mais alto é a parte de baixo. Como a peça assenta
 * pelo chão (`setOrigin(0.5, 1)`), a última linha desenhada É o rodapé: se o
 * desenho parasse em y=24 numa grelha de 29, a bancada ficava a pairar 10px.
 */
function drawCounter(b: Brush): void {
  b.r(0, 6, 64, 23, "#7a6a58"); // armários
  b.r(0, 6, 64, 1, "#95836e");
  b.r(0, 4, 64, 3, "#d9dde1"); // tampo
  b.r(0, 4, 64, 1, "#eef1f3");
  b.r(0, 7, 64, 1, "#5f5344"); // sombra sob o tampo
  b.r(0, 27, 64, 2, "#5f5344"); // rodapé, encostado ao chão
  // portas dos armários
  for (const x of [4, 20, 36, 52]) {
    b.r(x, 10, 12, 16, "#6b5c4c");
    b.r(x, 10, 12, 1, "#83725f");
    b.r(x + 10, 16, 1, 4, "#3d342a"); // puxador
  }
  // lava-loiça embutida
  b.r(8, 1, 12, 4, "#aeb4bb");
  b.r(9, 2, 10, 2, "#7f868e");
  b.r(13, 0, 2, 2, "#c8ccd1"); // torneira
  // máquina de café
  b.r(44, 0, 10, 5, "#25292f");
  b.r(45, 1, 8, 1, "#e8574f"); // luz da máquina
  b.r(47, 4, 4, 1, "#3a2018");
}

/** Frigorífico de inox, duas portas. w1 (16×31 art), ancorado em baixo. */
function drawFridge(b: Brush): void {
  const steel = "#c2c7cd";
  b.r(2, 2, 12, 29, steel);
  b.r(2, 2, 12, 1, "#e0e3e6"); // luz de cima
  b.r(2, 2, 1, 29, "#d4d8dc"); // luz esq
  b.r(13, 2, 1, 29, "#9aa0a7"); // sombra dir
  b.r(2, 13, 12, 1, "#9aa0a7"); // separação das portas
  b.r(11, 5, 1, 6, "#7f868e"); // puxadores
  b.r(11, 16, 1, 9, "#7f868e");
  b.dither(4, 4, 8, 8, "#c8cdd3", steel); // reflexo do inox
}

/** Mesa de refeições quadrada de madeira com veio e 4 pernas. detail=2 → 64×56. */
function drawDiningTable(b: Brush): void {
  const wood = "#875e37";
  const woodHi = "#a5764a";
  const woodMid = "#946a41";
  const woodSh = "#5f4126";
  const woodDk = "#3d2a17";
  // ── Tampo com cantos suavizados.
  b.r(4, 12, 56, 22, wood);
  b.r(2, 16, 60, 14, wood);
  b.r(4, 12, 56, 2, woodHi);
  b.r(2, 16, 2, 14, mix(wood, woodHi, 0.5));
  b.dither(8, 15, 48, 16, woodMid, wood);
  b.r(10, 17, 44, 1, mix(woodHi, wood, 0.4)); // reflexos
  b.r(14, 20, 36, 1, mix(woodHi, wood, 0.35));
  b.r(4, 33, 56, 2, woodDk); // bisel inferior
  b.r(2, 30, 60, 1, woodSh);
  // ── 4 pernas.
  b.r(8, 34, 5, 18, woodSh);
  b.r(8, 34, 1, 18, woodMid);
  b.r(12, 34, 1, 18, woodDk);
  b.r(51, 34, 5, 18, woodSh);
  b.r(51, 34, 1, 18, woodMid);
  b.r(55, 34, 1, 18, woodDk);
  b.r(20, 34, 4, 12, shade(woodSh, 0.88)); // pernas traseiras
  b.r(40, 34, 4, 12, shade(woodSh, 0.88));
}

/**
 * Cadeira de madeira de ripas, nas quatro vistas. detail=2 → 32×36.
 *
 * O encosto é vazado: quando tapa as costas, a camisa continua a ver-se pelos
 * intervalos das ripas — é o que dá a leitura de "sentado NA cadeira" em vez de
 * "colado à frente dela". Também não tem braços ⇒ de perfil nada passa à frente.
 */
function drawDiningChair(b: Brush, facing: Facing, layer: SeatLayer): void {
  const wood = "#6f4d30";
  const woodHi = "#8f6743";
  const woodMid = "#7c5638";
  const woodSh = "#4a3320";
  const woodDk = "#2c1d12";
  const front = layer === "front";

  /** Encosto: montantes + travessa de topo + 2 ripas (com vazios entre elas). */
  const backrest = (): void => {
    b.r(8, 3, 3, 20, wood);
    b.r(8, 3, 1, 20, woodHi);
    b.r(21, 3, 3, 20, wood);
    b.r(23, 3, 1, 20, woodDk);
    b.r(8, 3, 16, 3, wood);
    b.r(8, 3, 16, 1, woodHi);
    b.r(10, 9, 12, 3, woodMid);
    b.r(10, 9, 12, 1, woodHi);
    b.r(10, 14, 12, 3, woodMid);
    b.r(10, 14, 12, 1, woodHi);
  };

  /** Assento. */
  const seat = (): void => {
    b.r(6, 22, 20, 5, wood);
    b.r(6, 22, 20, 2, woodHi);
    b.r(6, 26, 20, 1, woodDk);
    b.dither(9, 23, 14, 3, woodMid, wood);
  };

  /** 4 pernas (as de trás mais escuras). */
  const legs = (): void => {
    b.r(7, 27, 3, 8, woodSh);
    b.r(7, 27, 1, 8, woodMid);
    b.r(22, 27, 3, 8, woodSh);
    b.r(24, 27, 1, 8, woodDk);
    b.r(11, 27, 2, 6, shade(woodSh, 0.9));
    b.r(19, 27, 2, 6, shade(woodSh, 0.9));
  };

  if (facing === "up") {
    if (front) {
      backrest();
      return;
    }
    seat();
    legs();
    return;
  }
  if (facing === "down") {
    if (front) return;
    backrest();
    seat();
    legs();
    return;
  }
  // Perfil (autorado à esquerda): vê-se um montante de canto, atrás das costas.
  if (front) return;
  b.r(19, 2, 4, 21, wood); // montante visto de lado
  b.r(19, 2, 2, 21, woodHi); // face virada à câmara
  b.r(22, 3, 1, 20, woodDk);
  b.r(18, 2, 5, 3, wood); // travessa de topo, de canto
  b.r(18, 2, 5, 1, woodHi);
  b.r(5, 22, 19, 5, wood); // assento de perfil
  b.r(5, 22, 19, 2, woodHi);
  b.r(5, 26, 19, 1, woodDk);
  b.dither(8, 23, 13, 3, woodMid, wood);
  b.r(6, 27, 3, 8, woodSh); // perna dianteira
  b.r(6, 27, 1, 8, woodMid);
  b.r(19, 27, 3, 8, woodSh); // perna traseira
  b.r(21, 27, 1, 8, woodDk);
  b.r(10, 27, 2, 6, shade(woodSh, 0.9)); // pernas do lado de lá
  b.r(17, 27, 2, 6, shade(woodSh, 0.9));
}

/**
 * Bebedouro de garrafão. detail=2 → 16×44, à medida do PNG do pacote.
 *
 * Media 32×52 (era `{w:16,h:26}` SEM detail, e `texture` desenha sempre a 2×) —
 * 1,9 m de aparelho à escala do avatar de 32×48, mais alto do que uma pessoa.
 * Desceu a 14×38, e voltou a subir a 16×44 quando o pedido foi «aumente o tamanho
 * dos bebedouros». Os 44 px são 1,60 m aos 27,4 px/m que os 48 px do avatar
 * fixam — o aparelho COM o garrafão em cima, e ainda 4 px abaixo da cabeça de
 * quem lá bebe, que é o tecto que esta sala já levou uma vez.
 *
 * Não é o desenho de 14×38 esticado: as bandas foram reautoradas na grelha nova,
 * porque esticar 21 linhas de corpo para 24 dá costuras de um pixel nos frisos.
 * Bandas do ficheiro final:
 *
 * ```
 * y  0..15  garrafão · y 16..19 cinta · y 20..30 nicho da torneira
 * y 31..33  friso GV · y 35..41 porta · y 42..43 base
 * ```
 */
/**
 * Relógio de pé, 24×68. Frente à esquerda (cols 0-16), flanco em sombra à
 * direita (17-23) — a mesma esguelha do PNG, para o plano B não virar a peça.
 */
function drawGrandfatherClock(b: Brush): void {
  const madeira = "#8a4a22";
  const madeiraHi = "#b0652f";
  const madeiraSh = "#5c2f15";
  const madeiraDk = "#3a1c0c";
  const ouro = "#c9a44a";
  // ── Flanco, de alto a baixo: é o que dá volume à caixa.
  b.r(17, 5, 6, 61, madeiraSh);
  b.r(22, 5, 1, 61, madeiraDk);
  // ── Coroa em arco.
  b.r(5, 0, 8, 2, madeiraDk);
  b.r(2, 2, 15, 4, madeira);
  b.r(1, 6, 17, 2, madeiraHi);
  // ── Capelo com o mostrador.
  b.r(1, 8, 16, 16, madeira);
  b.r(4, 10, 10, 11, madeiraDk);
  b.r(5, 11, 8, 9, "#d9c894");
  b.r(8, 13, 1, 4, madeiraDk); // ponteiro das horas
  b.r(9, 16, 3, 1, madeiraDk); // ponteiro dos minutos
  b.r(1, 23, 17, 1, madeiraHi);
  // ── Tronco com a porta de vidro e o pêndulo.
  b.r(2, 24, 14, 31, madeira);
  b.r(5, 27, 8, 25, "#1d2126");
  b.r(6, 29, 1, 9, ouro); // pesos
  b.r(11, 29, 1, 9, ouro);
  b.r(8, 28, 1, 15, ouro); // haste
  b.r(7, 43, 3, 3, ouro); // disco
  b.r(2, 24, 1, 31, madeiraHi);
  // ── Base e pés.
  b.r(1, 55, 17, 11, madeira);
  b.r(1, 55, 17, 1, madeiraHi);
  b.r(6, 59, 6, 2, ouro);
  b.r(1, 66, 4, 2, madeiraDk);
  b.r(13, 66, 4, 2, madeiraDk);
}

function drawWaterCooler(b: Brush): void {
  const body = "#e8e3d5";
  const bodyHi = "#f6f2e6";
  const bodySh = "#b9b3a3";
  const bodyDk = "#7d786b";
  const agua = "#7fd0e8";
  const aguaHi = "#b9ecfa";
  const aguaDk = "#3f9dbd";
  // ── Garrafão: gargalo estreito em cima, bojo a alargar até assentar na cinta.
  b.r(5, 0, 6, 2, aguaDk);
  b.r(4, 2, 8, 2, agua);
  b.r(2, 4, 12, 12, agua);
  b.dither(2, 4, 12, 10, aguaHi, agua);
  b.r(3, 4, 1, 11, aguaHi);
  b.r(12, 4, 1, 12, aguaDk);
  b.r(2, 14, 12, 2, aguaDk);
  // ── Cinta onde o garrafão encaixa, de parede a parede.
  b.r(0, 16, 16, 4, bodySh);
  b.r(0, 16, 16, 1, bodyHi);
  b.r(0, 19, 16, 1, bodyDk);
  // ── Corpo: frente à esquerda, flanco sombreado à direita (visto de esguelha).
  b.r(0, 20, 16, 24, body);
  b.r(8, 20, 8, 24, bodySh);
  b.r(15, 20, 1, 24, bodyDk);
  b.r(0, 20, 1, 24, bodyHi);
  // ── Nicho da torneira: o recorte escuro onde entra o copo.
  b.r(1, 20, 7, 11, "#2b3138");
  b.r(1, 20, 7, 1, "#161a1f");
  b.r(2, 22, 2, 2, agua); // botão de fria
  b.r(5, 22, 2, 2, "#c8502f"); // botão de quente
  b.r(2, 25, 1, 3, "#8d949c"); // bicos
  b.r(6, 25, 1, 3, "#8d949c");
  b.r(1, 30, 7, 1, bodyDk); // grelha de escorrimento
  // ── Friso GV e porta do compartimento.
  b.r(0, 31, 16, 3, "#1f8a4d");
  b.r(0, 31, 16, 1, "#43c07a");
  b.r(0, 33, 16, 1, "#12633a");
  b.r(1, 35, 7, 7, bodyHi);
  b.r(1, 35, 7, 1, bodySh);
  b.r(6, 37, 1, 3, bodyDk); // puxador
  // ── Base.
  b.r(0, 42, 16, 2, "#4a463d");
  b.r(0, 43, 16, 1, "#2b2823");
}

/** Paleta de lombadas — tons de biblioteca, não os néons da UI. */
const BOOK_COLS = [
  "#a8433b",
  "#b06a2f",
  "#c39a3a",
  "#3f7a4e",
  "#2f6f9e",
  "#42538c",
  "#7a4c86",
  "#9c5a6f",
  "#3a8f86",
  "#8a8f3a",
] as const;

/**
 * Enche uma prateleira com livros variados: verticais (com friso de título),
 * pilhas horizontais, um vaso ocasional e um livro inclinado — a variação é o
 * que faz parecer uma estante viva, não uma fila de rectângulos iguais.
 * RNG determinístico (semente por prateleira) → nunca usa Math.random.
 */
function drawShelfBooks(b: Brush, x0: number, top: number, x1: number, gapH: number, seed0: number): void {
  const baseline = top + gapH; // os livros assentam aqui
  let x = x0;
  let seed = (seed0 * 2654435761 + 40503) >>> 0;
  const rnd = () => {
    seed = (seed * 1103515245 + 12345) >>> 0;
    return (seed >>> 8) & 0xffff;
  };
  while (x < x1 - 3) {
    const kind = rnd() % 10;
    if (kind < 6) {
      // livro vertical
      const bw = 3 + (rnd() % 3);
      const bh = gapH - (rnd() % 3);
      const col = BOOK_COLS[rnd() % BOOK_COLS.length]!;
      const y = baseline - bh;
      b.r(x, y, bw, bh, col);
      b.r(x, y, 1, bh, shade(col, 1.18)); // lombada iluminada (esq)
      b.r(x + bw - 1, y, 1, bh, shade(col, 0.74)); // sombra (dir)
      b.r(x, y, bw, 1, shade(col, 1.28)); // topo
      b.r(x + 1, y + 2, bw - 2, 1, shade(col, 1.4)); // friso do título
      if (bh > 8) b.r(x + 1, y + 4, bw - 2, 1, shade(col, 0.85));
      x += bw + 1;
    } else if (kind < 8) {
      // pilha horizontal
      const sw = 7 + (rnd() % 4);
      const n = 2 + (rnd() % 2);
      for (let k = 0; k < n; k++) {
        const col = BOOK_COLS[rnd() % BOOK_COLS.length]!;
        const yy = baseline - 2 - k * 2;
        b.r(x, yy, sw, 2, col);
        b.r(x, yy, sw, 1, shade(col, 1.2));
        b.r(x, yy + 1, sw, 1, shade(col, 0.8));
      }
      x += sw + 2;
    } else if (kind === 8) {
      // vasinho com folha
      b.r(x + 1, baseline - 4, 5, 4, "#9a6440");
      b.r(x + 1, baseline - 4, 5, 1, "#b98056");
      b.r(x + 2, baseline - 8, 3, 4, "#3f8a52");
      b.r(x + 3, baseline - 9, 1, 2, "#5fd07f");
      x += 8;
    } else {
      // livro inclinado (encostado ao vizinho)
      const bh = gapH - 1;
      const col = BOOK_COLS[rnd() % BOOK_COLS.length]!;
      for (let i = 0; i < bh; i++) {
        const off = Math.floor(i / 3);
        b.r(x + off, baseline - 1 - i, 3, 1, col);
        b.r(x + off, baseline - 1 - i, 1, 1, shade(col, 1.15));
      }
      x += 5;
    }
  }
}

/**
 * Estante alta: carcaça de madeira com veio + 4 prateleiras cheias de vida.
 * detail=2 → 64×54, a medida do `bookshelf-v1.png`.
 *
 * A carcaça passou a começar em y=0 (tinha 3 linhas vazias no cimo, folga de uma
 * tela de 56 que já não existe) e as prateleiras passaram a um passo de 12
 * (vão de 10 + tábua de 2), tops em 2/14/26/38. Isto arruma de caminho um
 * transbordo antigo: com o passo de 13 e `gapH: 11` a última tábua era escrita
 * em y 56..58 de uma arte de 56 linhas, ou seja o vão de baixo nunca teve
 * frente — só não se via porque o pincel corta em silêncio.
 */
function drawBookshelf(b: Brush): void {
  const wood = "#6a4a30";
  const woodHi = "#916a45";
  const woodMid = "#795538";
  const woodDk = "#2b1d12";
  const back = "#2c2016";
  // ── Carcaça com montantes iluminado/sombreado.
  b.r(0, 0, 64, 54, wood);
  b.r(0, 0, 64, 2, woodHi); // topo iluminado
  b.r(0, 0, 3, 54, woodHi); // montante esq
  b.r(61, 0, 3, 54, woodDk); // montante dir
  b.r(0, 50, 64, 4, woodDk); // base pesada
  b.r(0, 50, 64, 1, woodMid); // aresta da base
  b.dither(1, 3, 2, 45, woodMid, wood);
  // ── 4 vãos com fundo escuro; livros por cima; prateleira física.
  const gaps = [2, 14, 26, 38];
  const gapH = 10;
  for (let s = 0; s < gaps.length; s++) {
    const top = gaps[s]!;
    b.r(4, top, 56, gapH, back);
    b.r(4, top, 56, 1, shade(back, 0.7)); // sombra no cimo do vão
    drawShelfBooks(b, 5, top, 60, gapH, s);
    b.r(3, top + gapH, 58, 2, woodMid);
    b.r(3, top + gapH, 58, 1, woodHi); // aresta frontal iluminada
  }
}

// ── Parque de mobile homes (o recinto a sul do prédio) ──────

/**
 * Emblema da marca **Green Village Mobile Homes**: uma folha verde em forma de casa
 * (corpo + telhado-folha com nervura e ponta) sobre chapa branca arredondada.
 * Reutilizado GRANDE no cartaz do parque e PEQUENO como selo em cada mobile home —
 * o pedido "não se esqueça da logo da green village mobilehomes". `u` = unidade de
 * escala (u=1 → ~10px; u=2 → ~20px).
 */
function drawGVLeaf(b: Brush, x: number, y: number, u: number): void {
  const white = "#f4fbf6";
  const g = "#1f9f5c";
  const gHi = "#4fd07f";
  const gDk = "#12633a";
  // Chapa branca (disco arredondado — cantos recuados).
  b.r(x + u, y, 8 * u, 10 * u, white);
  b.r(x, y + u, 10 * u, 8 * u, white);
  b.r(x + u, y + 9 * u, 8 * u, u, "#d6e8dd"); // sombra do bordo inferior
  // Corpo da casa (verde).
  b.r(x + 3 * u, y + 5 * u, 4 * u, 4 * u, g);
  b.r(x + 3 * u, y + 5 * u, 4 * u, u, gHi);
  // Telhado-folha: triângulo verde a subir para a direita (silhueta de folha).
  b.r(x + 2 * u, y + 4 * u, 6 * u, u, gDk);
  b.r(x + 3 * u, y + 3 * u, 4 * u, u, g);
  b.r(x + 4 * u, y + 2 * u, 3 * u, u, g);
  b.r(x + 5 * u, y + u, 2 * u, u, gHi); // ponta da folha
  b.r(x + 5 * u, y + 3 * u, u, 4 * u, gDk); // nervura central
  // Porta recortada (branco) → lê-se como casinha.
  b.r(x + 4 * u, y + 6 * u, 2 * u, 3 * u, white);
}

/** Um ponto no referencial da arte (80×74 para a mobile home). */
type Pt = readonly [number, number];

/**
 * Enche um quadrilátero convexo, coluna a coluna.
 *
 * O `Brush` só sabe rectângulos direitos — e é essa a razão por que os desenhos
 * anteriores desta secção eram vistas de LADO: uma fachada de frente cabe em
 * `r()`, uma casa vista de canto não tem uma única aresta horizontal. Aqui
 * mede-se, em cada coluna, onde entram e saem as arestas do quadrilátero e
 * pinta-se uma tira de 1 px entre as duas. Duas chamadas fazem o corpo de uma
 * caravana em 3/4; tudo o resto é decoração colada por cima.
 */
function quad(b: Brush, a: Pt, c: Pt, d: Pt, e: Pt, cor: string): void {
  const arestas: readonly [Pt, Pt][] = [
    [a, c],
    [c, d],
    [d, e],
    [e, a],
  ];
  const x0 = Math.floor(Math.min(a[0], c[0], d[0], e[0]));
  const x1 = Math.ceil(Math.max(a[0], c[0], d[0], e[0]));
  for (let x = x0; x < x1; x += 1) {
    const cx = x + 0.5;
    let yt = Number.POSITIVE_INFINITY;
    let yb = Number.NEGATIVE_INFINITY;
    for (const [p, q] of arestas) {
      if (p[0] === q[0]) continue; // aresta vertical: não define topo nem base
      if (cx < Math.min(p[0], q[0]) || cx > Math.max(p[0], q[0])) continue;
      const y = p[1] + ((q[1] - p[1]) * (cx - p[0])) / (q[0] - p[0]);
      if (y < yt) yt = y;
      if (y > yb) yb = y;
    }
    if (!Number.isFinite(yt) || yb <= yt) continue;
    const topo = Math.round(yt);
    b.r(x, topo, 1, Math.max(1, Math.round(yb) - topo), cor);
  }
}

/**
 * Um rectângulo COLADO a uma face inclinada.
 *
 * `a → z` é a aresta de topo da face; `t` corre ao longo dela (0 = `a`, 1 = `z`)
 * e `dy` desce dela em linha recta. Uma janela a meio de uma fachada em fuga
 * fica assim escrita como "de 62% a 84% da parede, 7 a 17 abaixo do beiral" —
 * que é como se pensa nela — em vez de quatro pares de coordenadas que ninguém
 * consegue mexer sem refazer a conta toda.
 */
function naFace(a: Pt, z: Pt, t0: number, t1: number, dy0: number, dy1: number): [Pt, Pt, Pt, Pt] {
  const em = (t: number, dy: number): Pt => [a[0] + (z[0] - a[0]) * t, a[1] + (z[1] - a[1]) * t + dy];
  return [em(t0, dy0), em(t1, dy0), em(t1, dy1), em(t0, dy1)];
}

/** Um disco cheio (a roda, as copas dos arbustos) — o `Brush` só tem rectângulos. */
function disco(b: Brush, cx: number, cy: number, r: number, cor: string): void {
  for (let dy = -r; dy <= r; dy += 1) {
    const dx = Math.floor(Math.sqrt(Math.max(0, r * r - dy * dy)));
    if (dx > 0) b.r(cx - dx, cy + dy, dx * 2, 1, cor);
  }
}

/**
 * MOBILE HOME vista em 3/4 — a casa do stand: telhado escuro de telha com um
 * aparelho de AC, empena curta à esquerda com janela, fachada comprida à direita
 * com porta central e degraus de madeira, saia verde escura na base, roda à
 * mostra e arbustos floridos a ladear. Selo GV na fachada. coords 80×74.
 *
 * É o PLANO B: o parque desenha-se com a arte do pacote (`officeFurniture.ts`
 * regista o `mobile-home-v1.png` recortado a 224×208 nesta mesma chave, e quem
 * chega primeiro fica com ela). Este desenho só aparece se o PNG faltar — e por
 * isso é a MESMA casa, no MESMO ângulo e na mesma proporção: um plano B que
 * mudasse a composição do recinto seria pior do que não existir, porque um 404
 * passaria de invisível a "o parque está diferente e ninguém sabe porquê".
 *
 * Sai daqui a 160×148 e não a 224×208 porque o `texture()` desenha SEMPRE a 2×
 * do tamanho pedido e não há forma de lhe pedir outro factor. Quem repõe a
 * medida é o `OfficeSpace`, que normaliza estas seis chaves à largura da pegada
 * (`PARK_HOME_PROPS`) — com o PNG carregado a conta dá 1 e não faz nada.
 *
 * A orientação espelhada (`mobileHomeFlip`) sai daqui sem uma linha a mais: é o
 * `mirror` do `texture()`, o mesmo que já dava a cadeira virada à direita.
 */
function drawMobileHome(b: Brush): void {
  const roof = "#3c4046";
  const roofHi = "#4e535a";
  const roofSh = "#2b2e33";
  const rim = "#c2c7cd";
  const rimSh = "#8f959c";
  const body = "#d9c9a3";
  const bodyHi = "#ecdfc0";
  const bodySh = "#b6a67f";
  const plank = "#c4b48f";
  const skirt = "#245a11";
  const skirtHi = "#3a7d20";
  const skirtSh = "#163a0a";
  const glass = "#1e3d47";
  const glassHi = "#4a7d8a";
  const frame = "#f2ead6";
  const door = "#16323a";
  const doorHi = "#2c5b68";
  const wood = "#8a5a2b";
  const woodHi = "#ab7440";
  const woodSh = "#5c3a1a";
  const bush = "#2f7d3e";
  const bushHi = "#4fb063";
  const flor = "#e08a2b";

  // ── A caixa. Quatro cantos do telhado e uma altura: tudo o resto sai destes.
  // O canto NEAR (`RN`) é o que está mais perto de quem olha; a empena curta vai
  // dele para a esquerda, a fachada comprida vai dele para a direita, e as duas
  // fogem para cima — é o que faz a casa ler-se como um volume e não como um
  // cartaz. ALT é a altura do corpo, do beiral ao chão.
  const RL: Pt = [6, 28];
  const RT: Pt = [44, 4];
  const RR: Pt = [72, 16];
  const RN: Pt = [34, 40];
  const ALT = 30;
  const BL: Pt = [RL[0], RL[1] + ALT];
  const BN: Pt = [RN[0], RN[1] + ALT];
  const BR: Pt = [RR[0], RR[1] + ALT];

  // ── Arbusto de trás (à direita, meio tapado pela casa): pinta-se ANTES para o
  // corpo o cortar, que é o que dá a sensação de estar plantado atrás.
  disco(b, 74, 42, 6, bush);
  disco(b, 70, 48, 5, bush);
  disco(b, 74, 39, 3, bushHi);

  // ── Roda. Também vem antes do corpo, e por uma razão de leitura: uma roda
  // pintada POR CIMA da parede fica uma bola colada à fachada. Pintada por baixo,
  // o corpo e a saia comem-lhe a metade de cima e só assoma o que assoma mesmo
  // debaixo do chassis — que é como se reconhece uma casa que tem rodas.
  disco(b, 66, 52, 5, "#17181b");
  disco(b, 66, 52, 2, "#4a5058");

  // ── Telhado. Superfície escura + juntas das telhas paralelas à empena, e o
  // bordo de trás iluminado (é o que apanha o céu).
  quad(b, RL, RT, RR, RN, roof);
  for (const k of [0.22, 0.44, 0.66, 0.88]) {
    const a: Pt = [RL[0] + (RT[0] - RL[0]) * k, RL[1] + (RT[1] - RL[1]) * k];
    const z: Pt = [RN[0] + (RR[0] - RN[0]) * k, RN[1] + (RR[1] - RN[1]) * k];
    quad(b, ...naFace(a, z, 0, 1, 0, 1.4), roofSh);
  }
  quad(b, ...naFace(RL, RT, 0, 1, 0, 1.6), roofHi);
  quad(b, ...naFace(RT, RR, 0, 1, 0, 1.6), roofHi);

  // ── Aparelho de AC no telhado, assente no plano do telhado (as duas faces
  // seguem as mesmas fugas do volume, senão fica um cubo colado torto).
  quad(b, [30, 14], [41, 7], [49, 11], [38, 18], "#efe8da");
  quad(b, [30, 14], [38, 18], [38, 24], [30, 20], "#c9c1af");
  quad(b, [38, 18], [49, 11], [49, 17], [38, 24], "#ded5c2");
  for (let g = 40; g < 48; g += 2) b.r(g, 13 + ((g - 40) >> 1), 1, 4, "#a49c8b");

  // ── Beiral: a chapa clara por baixo da aresta do telhado, dos dois lados.
  quad(b, ...naFace(RL, RN, 0, 1, 0, 2.6), rimSh);
  quad(b, ...naFace(RN, RR, 0, 1, 0, 2.6), rim);

  // ── Corpo. A empena curta está de sombra (foge para longe da luz) e a fachada
  // comprida está iluminada — é essa diferença, e não o contorno, que separa as
  // duas paredes ao olhar.
  quad(b, RL, RN, BN, BL, bodySh);
  quad(b, RN, RR, BR, BN, body);
  quad(b, ...naFace(RN, RR, 0, 0.06, 2.6, ALT), bodyHi); // aresta do canto vivo

  // ── Tábuas horizontais (sidings). Acompanham a fuga de cada parede.
  for (const d of [7, 11, 15, 19, 23]) {
    quad(b, ...naFace(RL, RN, 0, 1, d, d + 0.8), shade(bodySh, 0.93));
    quad(b, ...naFace(RN, RR, 0, 1, d, d + 0.8), plank);
  }

  // ── Janela da empena: moldura clara, vidro escuro com o reflexo em diagonal.
  quad(b, ...naFace(RL, RN, 0.24, 0.74, 7.5, 19.5), frame);
  quad(b, ...naFace(RL, RN, 0.29, 0.69, 9, 18), glass);
  quad(b, ...naFace(RL, RN, 0.31, 0.5, 10, 13), glassHi);

  // ── Janela da fachada.
  quad(b, ...naFace(RN, RR, 0.62, 0.86, 6.5, 18.5), frame);
  quad(b, ...naFace(RN, RR, 0.65, 0.83, 8, 17), glass);
  quad(b, ...naFace(RN, RR, 0.66, 0.74, 9, 12), glassHi);

  // ── Saia (skirt) verde escura a fechar a base, com aresta e sombra de contacto.
  quad(b, ...naFace(RL, RN, 0, 1, ALT - 5, ALT), skirt);
  quad(b, ...naFace(RN, RR, 0, 1, ALT - 5, ALT), skirt);
  quad(b, ...naFace(RL, RN, 0, 1, ALT - 5, ALT - 4), skirtSh);
  quad(b, ...naFace(RN, RR, 0, 1, ALT - 5, ALT - 4), skirtHi);
  quad(b, BL, BN, [BN[0], BN[1] + 1.6], [BL[0], BL[1] + 1.6], skirtSh);
  quad(b, BN, BR, [BR[0], BR[1] + 1.6], [BN[0], BN[1] + 1.6], skirtSh);

  // ── Porta central com postigo e puxador. Vem DEPOIS da saia e desce até `ALT`,
  // que é o chão: uma porta que parasse cinco pixels acima ficava com a banda
  // verde a passar-lhe à frente e lia-se como uma porta a pairar. É a saia que se
  // interrompe na porta, não o contrário.
  quad(b, ...naFace(RN, RR, 0.15, 0.35, 5.5, ALT), frame);
  quad(b, ...naFace(RN, RR, 0.17, 0.33, 7, ALT - 1), door);
  quad(b, ...naFace(RN, RR, 0.2, 0.3, 9, 14), doorHi);
  quad(b, ...naFace(RN, RR, 0.305, 0.325, 16, 18), "#d8b45e");

  // ── Degraus a descer da soleira PARA O OBSERVADOR. A direcção não é uma
  // escolha: no chão desta casa só existem dois eixos, o da fachada (`RN→RR`) e o
  // da empena (`RL→RN`), e sair da fachada é andar ao longo do segundo — ou seja
  // para baixo E PARA A DIREITA, no rácio 28:12 desse eixo. Qualquer outra
  // direcção põe a escada num terceiro plano que a casa não tem, e é isso que faz
  // uns degraus parecerem largados ao lado da porta em vez de encostados a ela.
  //
  // O tampo de cada degrau é o paralelogramo dos dois eixos, e a sua ESPESSURA no
  // ecrã é o produto externo deles: com o avanço a apontar para a esquerda os dois
  // eixos ficam quase opostos, o produto cancela-se e sobra uma tábua de 1,5 px.
  // Com o avanço certo o produto soma — 29 px² por 10,7 de largura ≈ 2,8 de banda
  // visível, que é o que faz um degrau ler-se como degrau.
  // O degrau seguinte não arranca no bordo do anterior: arranca 1,6 MAIS ABAIXO,
  // que é a altura do espelho. Encostados, o tampo de baixo pintava por cima do
  // espelho de cima e os dois fundiam-se numa única prancha inclinada — foi o que
  // aconteceu à primeira. O tampo é a face virada à luz (claro), o espelho fica em
  // meio-tom e por baixo corre a sombra de contacto: são os três valores que dizem
  // "isto tem altura" sem uma única linha de contorno.
  const AVA: Pt = [4.7, 2]; // um degrau de avanço, no eixo da empena
  const ESP = 1.6; // altura do espelho
  for (let i = 0; i < 2; i += 1) {
    const ox = 40.5 + AVA[0] * i;
    const oy = 65 + (AVA[1] + ESP) * i;
    const p = (dx: number, dy: number): Pt => [ox + dx, oy + dy];
    quad(b, p(0, 0), p(6, -3.8), p(6 + AVA[0], AVA[1] - 3.8), p(AVA[0], AVA[1]), woodHi);
    quad(
      b,
      p(AVA[0], AVA[1]),
      p(6 + AVA[0], AVA[1] - 3.8),
      p(6 + AVA[0], AVA[1] - 3.8 + ESP),
      p(AVA[0], AVA[1] + ESP),
      wood,
    );
    quad(
      b,
      p(AVA[0], AVA[1] + ESP),
      p(6 + AVA[0], AVA[1] - 3.8 + ESP),
      p(6 + AVA[0], AVA[1] - 2.9 + ESP),
      p(AVA[0], AVA[1] + ESP + 0.9),
      woodSh,
    );
  }

  // ── Arbustos da frente (esquerda): tapam o canto da saia, como no pacote.
  disco(b, 9, 62, 6, bush);
  disco(b, 16, 66, 5, bush);
  disco(b, 8, 59, 3, bushHi);
  disco(b, 17, 63, 2, bushHi);
  for (const [fx, fy] of [
    [6, 60],
    [12, 64],
    [15, 62],
    [19, 67],
    [10, 67],
    [72, 40],
    [76, 44],
    [69, 47],
  ] as const) {
    b.r(fx, fy, 1, 1, flor);
  }

  // ── Selo GV entre a porta e a janela da fachada.
  drawGVLeaf(b, 47, 40, 1);
}

/**
 * MOBILE HOME em ALÇADO LATERAL — a casa comprida verde-azeitona: telhado escuro
 * de duas águas rasas com chaminé, friso claro no beiral, quatro janelas de vidro
 * escuro alinhadas na fachada, rodapé e chassis pretos, e as rodas e os calços a
 * assomar por baixo. coords 96×33.
 *
 * É o PLANO B do `mobile-home-v2.png` (288×100 no pacote), e como todos os planos
 * B deste ficheiro desenha a MESMA casa na mesma proporção — um 404 no PNG tem de
 * ser invisível para quem está no parque, não uma casa diferente sem explicação.
 * Sai a 192×66 porque o `texture()` é fixo em 2×; o `OfficeSpace` normaliza-a
 * depois à largura da pegada, tal como faz à de 3/4.
 *
 * Nota de projecção, que é o que distingue esta das outras duas: isto é um alçado
 * PURO, sem fuga nenhuma. Não há aqui `quad` nem `naFace` porque não há um segundo
 * eixo — é tudo bandas horizontais e rectângulos, e é por isso que a arte tem
 * praticamente a altura da pegada (100 contra 96) em vez de subir 80 px como a de
 * 3/4: não há plano de telhado a ver-se de cima para transbordar.
 */
function drawMobileHomeSide(b: Brush): void {
  const roof = "#33322f";
  const roofHi = "#45443f";
  const roofSh = "#232220";
  const trim = "#b8a282";
  const trimSh = "#8f7c60";
  const wall = "#537835";
  const wallHi = "#648c42";
  const wallSh = "#3f5c28";
  const glass = "#0b2a33";
  const glassHi = "#2f6272";
  const frame = "#e0cba5";
  const skirt = "#2c2a24";
  const skirtSh = "#131211";
  const roda = "#17181b";
  const cubo = "#4a5058";

  const TOPO = 2; // aresta de cima do telhado
  const BEIRAL = 9; // fim do telhado / início do friso
  const PAREDE = 11; // primeira linha de tábua
  const RODAPE = 26; // fim da parede
  const CHAO = 30; // fim do chassis: daqui para baixo só rodas e calços

  // ── Chaminé. Vem primeiro para o telhado lhe comer a base.
  b.r(66, 0, 5, 4, roofSh);
  b.r(66, 0, 5, 1, trimSh);

  // ── Telhado: um trapézio que ALARGA para baixo. É o beiral a avançar sobre a
  // parede — ao contrário do instinto, a linha estreita é a do cume e não a do
  // chão, e é essa inversão que faz uma água rasa ler-se como telhado e não como
  // um friso grosso.
  for (let y = TOPO; y < BEIRAL; y += 1) {
    const k = (y - TOPO) / (BEIRAL - 1 - TOPO);
    const x0 = Math.round(11 - 9 * k);
    const x1 = Math.round(85 + 9 * k);
    b.r(x0, y, x1 - x0, 1, y === TOPO ? roofHi : roof);
  }
  // Duas fiadas de telha, e a sombra por baixo da aresta do beiral.
  b.r(6, 5, 84, 1, roofSh);
  b.r(4, 7, 88, 1, roofSh);

  // ── Friso do beiral: a chapa clara entre o telhado e a parede, com a sua
  // própria sombra por baixo. São duas linhas e é o que separa os dois materiais.
  b.r(2, BEIRAL, 92, 2, trim);
  b.r(2, BEIRAL + 1, 92, 1, trimSh);

  // ── Parede. Base + tábuas horizontais de dois em dois, a de cima mais clara
  // (apanha a luz que passa pelo beiral) e a de baixo mais escura.
  b.r(2, PAREDE, 92, RODAPE - PAREDE, wall);
  for (let y = PAREDE + 1; y < RODAPE; y += 3) b.r(2, y, 92, 1, wallSh);
  b.r(2, PAREDE, 92, 1, wallHi);

  // ── As quatro janelas. Mesma banda, larguras diferentes: é a assimetria das
  // larguras que diz onde ficam os quartos e onde fica a sala, e alinhá-las todas
  // iguais dava um autocarro.
  for (const [jx, jw] of [
    [9, 10],
    [30, 5],
    [43, 12],
    [64, 7],
  ] as const) {
    b.r(jx, 12, jw, 10, frame);
    b.r(jx + 1, 13, jw - 2, 8, glass);
    b.r(jx + 1, 13, jw - 2, 2, glassHi);
    // Caixilho do meio: uma janela sem divisão nenhuma lê-se como um buraco.
    b.r(jx + ((jw / 2) | 0), 13, 1, 8, frame);
  }

  // ── Rodapé e chassis. O rodapé é a tábua escura que fecha a parede; o chassis é
  // a caixa preta por baixo, e é ele que põe a casa acima do chão.
  b.r(2, RODAPE, 92, 2, skirt);
  b.r(1, RODAPE + 2, 94, CHAO - RODAPE - 2, skirtSh);
  b.r(1, RODAPE + 2, 94, 1, skirt);

  // ── Rodas ao centro e calços nas pontas. Uma casa destas não assenta no chão em
  // todo o comprimento: apoia-se em dois pontos e o resto está no ar.
  for (const rx of [42, 51]) {
    b.r(rx, CHAO, 6, 3, roda);
    b.r(rx + 2, CHAO + 1, 2, 1, cubo);
  }
  b.r(5, CHAO, 11, 2, skirtSh);
  b.r(78, CHAO, 11, 2, skirtSh);

  // ── Selo GV entre a segunda e a terceira janela.
  drawGVLeaf(b, 37, 16, 1);
}

/**
 * MOBILE HOME de TELHADO EM ARCO vista em 3/4 — a casa âmbar: telhado de chapa
 * abaulado (o arco é o que lhe dá o nome), empena curta à esquerda onde a curva
 * se mostra de topo, fachada comprida à direita com porta e janelas de vidro
 * escuro, degraus de madeira, arbustos e rodas. coords 80×57.
 *
 * PLANO B do `mobile-home-v3.png` (224×160 no pacote), na mesma proporção. Sai a
 * 160×114 pelo 2× fixo do `texture()` e é reposto à largura da pegada no
 * `OfficeSpace`, como as outras duas.
 *
 * O telhado NÃO é um `quad` como o da `drawMobileHome`: é uma varredura. A secção
 * da empena é um arco (a recta `RL→RN` empenada para cima por `ARCO·sen(πu)`) e o
 * telhado é essa secção arrastada ao longo do eixo de fuga `RL→RT`. Sai daí de
 * graça o que um quad achatado nunca dava — o brilho corre ao longo do cume e
 * apaga-se nas duas beiras, que é exactamente como uma chapa curva se comporta à
 * luz, e a silhueta fica redonda em vez de facetada.
 */
function drawMobileHomeArch(b: Brush): void {
  const roof = "#40403f";
  const roofHi = "#6a6a67";
  const roofSh = "#2c2c2b";
  const rim = "#cfc7b6";
  const rimSh = "#9a9385";
  const body = "#d3a96e";
  const bodyHi = "#e6c48d";
  const bodySh = "#a8834f";
  const plank = "#c39a60";
  const glass = "#0f2f3c";
  const glassHi = "#3b6f80";
  const frame = "#efe3cc";
  const door = "#142f3a";
  const doorHi = "#2b5a6a";
  const wood = "#7a4a1e";
  const woodHi = "#a36b32";
  const woodSh = "#452104";
  const bush = "#285214";
  const bushHi = "#4a8a2a";
  const flor = "#e8b53a";

  // ── A caixa, na mesma convenção da casa em 3/4 ao lado: `RN` é o canto mais
  // perto de quem olha, a empena curta vai dele para a esquerda (`RL`), a fachada
  // comprida vai dele para a direita (`RR`), e o eixo de fuga é `RL→RT`.
  const RL: Pt = [4, 26];
  const RT: Pt = [40, 8];
  const RR: Pt = [72, 16];
  const RN: Pt = [36, 34];
  const ALT = 18;
  const ARCO = 5;
  const BL: Pt = [RL[0], RL[1] + ALT];
  const BN: Pt = [RN[0], RN[1] + ALT];
  const BR: Pt = [RR[0], RR[1] + ALT];
  const FUGA: Pt = [RT[0] - RL[0], RT[1] - RL[1]];

  const reto = (u: number): Pt => [RL[0] + (RN[0] - RL[0]) * u, RL[1] + (RN[1] - RL[1]) * u];
  const arco = (u: number): Pt => {
    const p = reto(u);
    return [p[0], p[1] - ARCO * Math.sin(Math.PI * u)];
  };
  const longe = (p: Pt): Pt => [p[0] + FUGA[0], p[1] + FUGA[1]];

  // ── Arbusto de trás e roda, ambos ANTES do corpo para que a casa os corte.
  disco(b, 70, 40, 5, bush);
  disco(b, 68, 45, 4, bush);
  disco(b, 71, 37, 2, bushHi);
  disco(b, 62, 49, 4, "#17181b");
  disco(b, 62, 49, 2, "#4a5058");

  // ── O telhado, varrido. 72 fatias para uma aresta de 33 px: a mais do que
  // preciso, de propósito, porque com uma fatia por pixel as diagonais das duas
  // pontas deixavam falhas de meio pixel entre quads vizinhos.
  const FATIAS = 72;
  for (let i = 0; i < FATIAS; i += 1) {
    const u0 = i / FATIAS;
    const u1 = (i + 1) / FATIAS;
    const a = arco(u0);
    const c = arco(u1);
    // A luz corre no cume e apaga-se nas beiras — é a curvatura a fazer-se ver.
    quad(b, a, longe(a), longe(c), c, shade(roof, 0.78 + 0.62 * Math.sin(Math.PI * u0)));
    // A empena: a fatia da secção entre o arco e a recta do beiral. Escura, porque
    // está virada ao contrário da luz, e é ela que mostra a curva de topo.
    quad(b, a, c, reto(u1), reto(u0), i < FATIAS / 2 ? roofSh : shade(roofSh, 1.25));
  }
  // Aresta de trás iluminada + costura do cume ao longo da fuga.
  quad(b, ...naFace(arco(0), arco(1), 0, 1, -0.9, 0), rimSh);
  quad(b, longe(arco(0.42)), longe(arco(0.58)), arco(0.58), arco(0.42), shade(roofHi, 1.05));

  // ── Grelha de ventilação no cume, encostada à ponta que foge.
  b.r(48, 10, 7, 3, rimSh);
  for (let g = 49; g < 55; g += 2) b.r(g, 10, 1, 3, roofSh);

  // ── Beiral: a chapa clara por baixo da aresta, dos dois lados.
  quad(b, ...naFace(RL, RN, 0, 1, 0, 2.2), rimSh);
  quad(b, ...naFace(RN, RR, 0, 1, 0, 2.2), rim);

  // ── Corpo: empena de sombra à esquerda, fachada iluminada à direita.
  quad(b, RL, RN, BN, BL, bodySh);
  quad(b, RN, RR, BR, BN, body);
  quad(b, ...naFace(RN, RR, 0, 0.07, 2.2, ALT), bodyHi);

  // ── Tábuas horizontais, cada uma a seguir a fuga da sua parede.
  for (const d of [5, 9, 13] as const) {
    quad(b, ...naFace(RL, RN, 0, 1, d, d + 0.7), shade(bodySh, 0.93));
    quad(b, ...naFace(RN, RR, 0, 1, d, d + 0.7), plank);
  }

  // ── Janela da empena.
  quad(b, ...naFace(RL, RN, 0.26, 0.72, 5, 12), frame);
  quad(b, ...naFace(RL, RN, 0.3, 0.68, 6, 11), glass);
  quad(b, ...naFace(RL, RN, 0.32, 0.48, 6.6, 8.6), glassHi);

  // ── Janela da fachada.
  quad(b, ...naFace(RN, RR, 0.58, 0.86, 4.5, 12), frame);
  quad(b, ...naFace(RN, RR, 0.61, 0.83, 5.6, 11), glass);
  quad(b, ...naFace(RN, RR, 0.62, 0.7, 6.2, 8.4), glassHi);

  // ── Porta, a descer até ao chão pela mesma razão da casa ao lado: uma porta que
  // parasse acima da base ficava a pairar sobre o rodapé.
  quad(b, ...naFace(RN, RR, 0.16, 0.36, 3.6, ALT), frame);
  quad(b, ...naFace(RN, RR, 0.18, 0.34, 5, ALT - 0.8), door);
  quad(b, ...naFace(RN, RR, 0.21, 0.31, 6.4, 10), doorHi);
  quad(b, ...naFace(RN, RR, 0.315, 0.335, 11.5, 13), "#d8b45e");

  // ── Rodapé escuro a fechar a base, e a sombra de contacto por baixo.
  quad(b, ...naFace(RL, RN, 0, 1, ALT - 3, ALT), "#4a3a24");
  quad(b, ...naFace(RN, RR, 0, 1, ALT - 3, ALT), "#5c4830");
  quad(b, BL, BN, [BN[0], BN[1] + 1.4], [BL[0], BL[1] + 1.4], "#2c2218");
  quad(b, BN, BR, [BR[0], BR[1] + 1.4], [BN[0], BN[1] + 1.4], "#2c2218");

  // ── Degraus a descer da soleira PARA O OBSERVADOR, no eixo da empena, pela
  // mesma conta da casa ao lado: fora desse eixo a escada cai num terceiro plano
  // que a casa não tem e passa a parecer largada ao lado da porta.
  const AVA: Pt = [3.6, 1.5];
  const ESP = 1.4;
  for (let i = 0; i < 2; i += 1) {
    const ox = 41 + AVA[0] * i;
    const oy = 51 + (AVA[1] + ESP) * i;
    const p = (dx: number, dy: number): Pt => [ox + dx, oy + dy];
    quad(b, p(0, 0), p(5, -3), p(5 + AVA[0], AVA[1] - 3), p(AVA[0], AVA[1]), woodHi);
    quad(
      b,
      p(AVA[0], AVA[1]),
      p(5 + AVA[0], AVA[1] - 3),
      p(5 + AVA[0], AVA[1] - 3 + ESP),
      p(AVA[0], AVA[1] + ESP),
      wood,
    );
    quad(
      b,
      p(AVA[0], AVA[1] + ESP),
      p(5 + AVA[0], AVA[1] - 3 + ESP),
      p(5 + AVA[0], AVA[1] - 2.2 + ESP),
      p(AVA[0], AVA[1] + ESP + 0.8),
      woodSh,
    );
  }

  // ── Arbustos da frente, a tapar o canto do rodapé.
  disco(b, 8, 47, 5, bush);
  disco(b, 14, 51, 4, bush);
  disco(b, 7, 44, 2, bushHi);
  disco(b, 15, 48, 2, bushHi);
  for (const [fx, fy] of [
    [6, 45],
    [11, 49],
    [14, 47],
    [17, 52],
    [9, 52],
    [69, 38],
    [72, 42],
  ] as const) {
    b.r(fx, fy, 1, 1, flor);
  }

  // ── Selo GV entre a porta e a janela da fachada.
  drawGVLeaf(b, 48, 32, 1);
}

/**
 * CASA FLUTUANTE vista em 3/4 — o pontão de madeira sobre a água com a casinha
 * branca de telhado azul em cima, guarda-corpos nas duas beiras à vista e a
 * ondulação a lamber a plataforma. coords 96×49.
 *
 * PLANO B do `floating-house.png`, nos mesmos 192×98 de ecrã.
 *
 * A mesma convenção de caixa das duas casas acima — `RN` é o canto mais perto,
 * a empena curta vai para a esquerda (`RL`), a fachada comprida para a direita
 * (`RE`), a fuga é `RL→RF`. Muda o telhado: aqui é de DUAS ÁGUAS, e por isso a
 * secção da empena é uma tenda em vez do arco da casa âmbar. Bastam dois quads
 * (uma água é um plano, não uma curva), e as duas saem com brilhos diferentes de
 * propósito — a água norte fica quase de perfil e apanha o céu, a sul recebe a
 * luz de frente. É essa diferença que faz o vinco do cume ler-se sem o desenhar.
 *
 * O pontão é o que a distingue: a casa não assenta no chão, assenta numa laje de
 * pranchas com 3 px de canto à vista, e é a sombra dessa espessura contra as
 * ondas que diz "isto flutua". Sem ela seria uma casa num descampado azul.
 */
function drawFloatingHouse(b: Brush): void {
  const roof = "#2653b8";
  const roofHi = "#7d99f2";
  const roofSh = "#16439a";
  const wall = "#e6e6e7";
  const wallHi = "#f6f7f9";
  const wallSh = "#b9bec5";
  const trim = "#8e959f";
  const glass = "#123246";
  const glassHi = "#3f7d99";
  const door = "#7a4a1e";
  const doorHi = "#a36b32";
  const deck = "#8a6438";
  const deckHi = "#a67e4c";
  const deckSh = "#4e341a";
  const agua = "#3f96c6";
  const espuma = "#b7e6eb";

  // ── O pontão. Losango no chão (o plano de assentamento) + 3 px de espessura.
  const DL: Pt = [4, 33];
  const DN: Pt = [46, 44];
  const DR: Pt = [92, 31];
  const DF: Pt = [50, 20];
  const ESP = 3;
  const noDeck = (s: number, t: number): Pt => [
    DL[0] + (DN[0] - DL[0]) * s + (DF[0] - DL[0]) * t,
    DL[1] + (DN[1] - DL[1]) * s + (DF[1] - DL[1]) * t,
  ];

  // ── Ondas, ANTES de tudo: nascem por fora da laje e é a laje que as corta.
  for (let i = 1; i <= 4; i += 1) {
    const s = i / 5;
    const ex = 4 + 42 * s;
    const ey = 36 + 11 * s;
    b.r(Math.round(ex - 6), Math.round(ey + 1), 5, 1, espuma);
    b.r(Math.round(ex - 9), Math.round(ey + 3), 4, 1, agua);
    const dx = 46 + 46 * s;
    const dy = 47 - 13 * s;
    b.r(Math.round(dx + 2), Math.round(dy + 1), 5, 1, espuma);
    b.r(Math.round(dx + 5), Math.round(dy + 3), 4, 1, agua);
  }

  // ── Canto do pontão: empena de sombra à esquerda, testa iluminada à direita.
  quad(b, DL, DN, [DN[0], DN[1] + ESP], [DL[0], DL[1] + ESP], deckSh);
  quad(b, DN, DR, [DR[0], DR[1] + ESP], [DN[0], DN[1] + ESP], shade(deck, 0.82));
  quad(b, [DL[0], DL[1] + ESP], [DN[0], DN[1] + ESP], [DN[0], DN[1] + ESP + 1], [DL[0], DL[1] + ESP + 1], "#241505");
  quad(b, [DN[0], DN[1] + ESP], [DR[0], DR[1] + ESP], [DR[0], DR[1] + ESP + 1], [DN[0], DN[1] + ESP + 1], "#241505");

  // ── Tábuas do estrado, a correr no eixo de fuga. As juntas são o que dá escala
  // à laje: sem elas os 88×24 px de castanho liso pareciam um tapete.
  quad(b, DL, DN, DR, DF, deck);
  for (let k = 1; k < 9; k += 1) {
    const s = k / 9;
    quad(b, noDeck(s, 0), noDeck(s + 0.016, 0), noDeck(s + 0.016, 1), noDeck(s, 1), deckSh);
  }
  quad(b, noDeck(0, 0.96), noDeck(1, 0.96), noDeck(1, 1), noDeck(0, 1), deckHi);

  // ── A casa. `ALT` é a parede; o resto é telhado.
  const RL: Pt = [17, 18];
  const RN: Pt = [43, 25];
  const RE: Pt = [68, 18];
  const ALT = 15;
  const CUME = 9;
  const BL: Pt = [RL[0], RL[1] + ALT];
  const BN: Pt = [RN[0], RN[1] + ALT];
  const BE: Pt = [RE[0], RE[1] + ALT];
  const FUGA: Pt = [RE[0] - RN[0], RE[1] - RN[1]];

  quad(b, RL, RN, BN, BL, wallSh);
  quad(b, RN, RE, BE, BN, wall);
  quad(b, ...naFace(RN, RE, 0, 0.05, 0, ALT), wallHi);

  // Tábuas horizontais, cada uma a seguir a fuga da sua parede.
  for (const d of [4, 8, 12] as const) {
    quad(b, ...naFace(RL, RN, 0, 1, d, d + 0.6), shade(wallSh, 0.92));
    quad(b, ...naFace(RN, RE, 0, 1, d, d + 0.6), shade(wall, 0.9));
  }

  // Janela da empena.
  quad(b, ...naFace(RL, RN, 0.3, 0.68, 4.5, 10.5), trim);
  quad(b, ...naFace(RL, RN, 0.33, 0.65, 5.4, 9.6), glass);
  quad(b, ...naFace(RL, RN, 0.35, 0.47, 6, 7.8), glassHi);

  // Duas janelas da fachada.
  for (const [t0, t1] of [
    [0.46, 0.66],
    [0.74, 0.92],
  ] as const) {
    quad(b, ...naFace(RN, RE, t0, t1, 3.5, 10), trim);
    quad(b, ...naFace(RN, RE, t0 + 0.03, t1 - 0.03, 4.5, 9), glass);
    quad(b, ...naFace(RN, RE, t0 + 0.04, t0 + 0.1, 5.1, 7), glassHi);
  }

  // Porta, a descer até ao estrado pela mesma razão das casas ao lado: uma porta
  // que parasse acima da base ficava a pairar sobre o rodapé.
  quad(b, ...naFace(RN, RE, 0.12, 0.32, 2.8, ALT), trim);
  quad(b, ...naFace(RN, RE, 0.145, 0.295, 4, ALT - 0.6), door);
  quad(b, ...naFace(RN, RE, 0.17, 0.26, 5.2, 9), doorHi);
  quad(b, ...naFace(RN, RE, 0.27, 0.29, 9.4, 10.6), "#d8b45e");

  // Rodapé escuro onde a casa encosta às pranchas.
  quad(b, ...naFace(RL, RN, 0, 1, ALT - 2, ALT), "#6b7079");
  quad(b, ...naFace(RN, RE, 0, 1, ALT - 2, ALT), "#828892");

  // ── Telhado de duas águas. Empena por baixo (tapa costuras de arredondamento),
  // depois a água norte e por fim a sul, que é a que fica à frente.
  const OG: Pt = [(RN[0] - RL[0]) * 0.1, (RN[1] - RL[1]) * 0.1];
  const OF: Pt = [FUGA[0] * 0.08, FUGA[1] * 0.08];
  const meio: Pt = [(RL[0] + RN[0]) / 2, (RL[1] + RN[1]) / 2 - CUME];
  const perto = (p: Pt): Pt => [p[0] - OF[0], p[1] - OF[1]];
  const longe = (p: Pt): Pt => [p[0] + FUGA[0] + OF[0], p[1] + FUGA[1] + OF[1]];
  const EL: Pt = [RL[0] - OG[0], RL[1] - OG[1]];
  const EN: Pt = [RN[0] + OG[0], RN[1] + OG[1]];

  quad(b, RL, RN, meio, meio, shade(wallSh, 0.9));
  quad(b, perto(EL), longe(EL), longe(meio), perto(meio), shade(roof, 1.42));
  quad(b, perto(meio), longe(meio), longe(EN), perto(EN), roof);

  // Fiadas de telha a acompanhar o cume, e o cume ele mesmo. Uma fiada é só uma
  // faixa fina paralela ao cume: dois pontos a caminho da beira (`entre`) e os
  // mesmos dois um nada mais abaixo (`desce`).
  const entre = (p: Pt, q: Pt, t: number): Pt => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
  const desce = (p: Pt, dy: number): Pt => [p[0], p[1] + dy];
  for (const t of [0.28, 0.54, 0.8] as const) {
    const a = entre(perto(meio), perto(EN), t);
    const c = entre(longe(meio), longe(EN), t);
    quad(b, a, c, desce(c, 0.7), desce(a, 0.7), shade(roof, 0.8));
  }
  quad(b, perto(meio), longe(meio), desce(longe(meio), 1.1), desce(perto(meio), 1.1), roofHi);
  quad(b, perto(EN), longe(EN), desce(longe(EN), 1.5), desce(perto(EN), 1.5), roofSh);

  // ── Selo GV na fachada, entre a porta e a primeira janela.
  drawGVLeaf(b, 55, 29, 1);

  // ── Guarda-corpos das duas beiras à vista. Vêm por ÚLTIMO: estão à frente de
  // tudo, casa incluída, porque a beira do pontão é o que está mais perto.
  const guarda = (a: Pt, z: Pt, cor: string, n: number): void => {
    const H = 5;
    quad(b, [a[0], a[1] - H], [z[0], z[1] - H], [z[0], z[1] - H + 1], [a[0], a[1] - H + 1], cor);
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      b.r(Math.round(a[0] + (z[0] - a[0]) * t), Math.round(a[1] + (z[1] - a[1]) * t) - H, 1, H, cor);
    }
  };
  guarda(DL, DN, deckSh, 7);
  guarda(DN, DR, shade(deck, 0.86), 8);

  // ── Dois cabeços de amarração na água, à frente do canto — a pista de que a
  // plataforma está PRESA e não à deriva.
  for (const [px, py] of [
    [37, 47],
    [55, 46],
  ] as const) {
    b.r(px, py - 4, 2, 4, "#3b2a15");
    b.r(px - 1, py - 5, 4, 1, "#5c421f");
    b.r(px - 3, py + 1, 8, 1, espuma);
  }
}

/** A carroçaria de um carro: as quatro tintas mais os dois traços de silhueta. */
interface Carro {
  /** Tinta base. */
  body: string;
  /** Aresta virada à luz (norte/poente). */
  hi: string;
  /** Aresta em sombra (nascente) e os panos rebaixados. */
  sh: string;
  /** O mais escuro: traseira, sulcos, aileron. */
  dk: string;
  /**
   * MEIA-largura da carroçaria, em px. É o único número que separa as quatro
   * silhuetas vistas de cima — de 22 (desportivo, estreito e afilado) a 26
   * (SUV, uma caixa). Vista de cima, um carro é a sua largura: não há altura
   * nenhuma para distinguir um Cullinan de um SF90.
   */
  larg: number;
  /** Aileron traseiro — só os dois de motor central o levam. */
  aileron: boolean;
}

/**
 * CARRO visto de cima (top-down), frente para norte — o plano B dos quatro
 * carros das baias, para quando o PNG do pacote não chega.
 *
 * coords 64×128 (2×4 tiles, cobertura exacta da pegada), 1 célula = 1 px de
 * ecrã. Mesma projecção e mesma família de tons das carrinhas logo acima, de
 * propósito: se um dos quatro ficheiros faltar, o que entra no lugar tem de
 * ler-se como parte da MESMA fila, não como uma peça de outro jogo.
 */
function drawCar(b: Brush, c: Carro): void {
  const glass = "#b6d4e4";
  const glassHi = "#dceef6";
  const trim = "#1c2026";
  const tyre = "#17191c";
  const farol = "#fff4cc";
  const farolim = "#d94b3a";
  const x0 = 32 - c.larg;
  const w = c.larg * 2;

  // Carroçaria. As duas caixas sobrepostas (uma mais larga e curta, outra mais
  // estreita e comprida) dão o nariz e a traseira arredondados sem precisar de
  // desenhar cantos — é o mesmo truque do corpo da carrinha.
  b.r(x0, 6, w, 116, c.body);
  b.r(x0 + 2, 3, w - 4, 122, c.body);
  b.r(x0, 6, w, 3, c.hi);
  b.r(x0, 6, 3, 116, c.hi);
  b.r(x0 + w - 3, 6, 3, 116, c.sh);
  b.r(x0 + 2, 118, w - 4, 4, c.dk);

  // Frente: grelha ao meio, faróis nas quinas, capô rebaixado.
  b.r(26, 4, 12, 3, trim);
  b.r(x0 + 6, 5, 8, 3, farol);
  b.r(32 + c.larg - 14, 5, 8, 3, farol);
  b.r(x0 + 4, 10, w - 8, 12, c.sh);

  // Habitáculo: pára-brisas, tejadilho, óculo traseiro.
  b.r(x0 + 5, 26, w - 10, 16, glass);
  b.dither(x0 + 5, 26, w - 10, 14, glassHi, glass);
  b.r(x0 + 5, 26, w - 10, 2, glassHi);
  b.r(x0 + 5, 42, w - 10, 26, c.body);
  b.r(x0 + 5, 42, w - 10, 1, c.hi);
  b.r(x0 + 6, 68, w - 12, 12, glass);
  b.r(x0 + 6, 68, w - 12, 1, glassHi);

  // Traseira: tampa do motor com sulcos de ventilação.
  b.r(x0 + 4, 82, w - 8, 30, c.sh);
  for (let y = 88; y < 110; y += 6) b.r(x0 + 7, y, w - 14, 1, c.dk);
  if (c.aileron) {
    b.r(x0 - 2, 104, w + 4, 5, c.dk);
    b.r(x0 - 2, 104, w + 4, 2, c.sh);
  }
  b.r(x0 + 5, 119, 10, 3, farolim);
  b.r(32 + c.larg - 15, 119, 10, 3, farolim);

  // Pneus: assomam de fora da carroçaria, que é o que se vê de cima.
  for (const [px, py] of [
    [x0 - 4, 22],
    [x0 + w - 1, 22],
    [x0 - 4, 86],
    [x0 + w - 1, 86],
  ] as const) {
    b.r(px, py, 5, 22, tyre);
  }
}
function drawCarSport(b: Brush): void {
  drawCar(b, { body: "#d31f26", hi: "#f4585c", sh: "#9b1319", dk: "#660c10", larg: 22, aileron: true });
}
function drawCarSedan(b: Brush): void {
  drawCar(b, { body: "#1e4a34", hi: "#37765a", sh: "#143223", dk: "#0b2016", larg: 25, aileron: false });
}
function drawCarSuper(b: Brush): void {
  drawCar(b, { body: "#f0c018", hi: "#ffe173", sh: "#b88c0a", dk: "#7c5c05", larg: 23, aileron: true });
}
function drawCarSuv(b: Brush): void {
  drawCar(b, { body: "#232529", hi: "#43474e", sh: "#17181b", dk: "#0d0e10", larg: 26, aileron: false });
}

/**
 * CANDEEIRO de rua preto com cabeça de luz âmbar (a auréola quente no chão é aditada
 * na cena). Poste alto de ferro + braço curvo + luminária. coords 32×112 (1 tile de
 * pegada, sobe 3.5 tiles).
 */
function drawStreetLamp(b: Brush): void {
  const iron = "#17171c";
  const ironHi = "#3a3a44";
  const ironDk = "#0a0a0e";
  const glass = "#ffcf7a";
  const glassHi = "#ffe8b8";
  const core = "#fff3d0";
  b.r(10, 104, 12, 8, iron); // base
  b.r(10, 104, 12, 1, ironHi);
  b.r(8, 110, 16, 2, ironDk);
  b.r(14, 18, 4, 90, iron); // poste
  b.r(14, 18, 1, 90, ironHi);
  b.r(17, 18, 1, 90, ironDk);
  b.r(14, 18, 9, 3, iron); // braço
  b.r(20, 14, 3, 6, iron);
  b.r(16, 8, 12, 10, iron); // luminária
  b.r(16, 8, 12, 1, ironHi);
  b.r(18, 10, 8, 7, glass);
  b.dither(18, 10, 8, 6, glassHi, glass);
  b.r(19, 11, 6, 4, core);
  b.r(16, 17, 12, 2, iron);
  b.r(20, 6, 4, 2, iron); // topo
}

/**
 * CARTAZ do parque: painel creme em moldura verde pendurado por ferragens em
 * dois postes de madeira que correm de alto a baixo. detail=2 → 96×106.
 *
 * Era 96×80 com os postes só na metade de baixo e o painel a flutuar em cima. O
 * ficheiro do pacote tem os postes INTEIROS a passar por trás do painel, que é o
 * que faz o cartaz parecer plantado no chão em vez de encostado. Cresceu 26 px em
 * altura por causa disso — a pegada continua a ser 3 tiles, o cartaz é que sobe
 * mais. Bandas medidas do ficheiro final:
 *
 * ```
 * y  0..10  capacetes dos postes    · y 11..16 moldura de topo + ferragem
 * y 16..65  painel (logo y19..50, "GREEN VILLAGE" y52..58, fita y59..64)
 * y 66..71  moldura de base + 2ª ferragem · y 72..105 pernas
 * ```
 */
function drawParkSign(b: Brush): void {
  const post = "#6f4e2f";
  const postHi = "#916941";
  const postDk = "#3f2c17";
  const board = "#f4fbf6";
  const boardSh = "#d6e2da";
  const frame = "#1f8a4d";
  const frameHi = "#43c07a";
  const frameDk = "#12633a";
  const gold = "#c8a046";
  const metal = "#8d949c";
  // ── Postes inteiros, de y0 ao chão. As duas fiadas estreitas do topo são o
  // capacete: um poste que acaba num corte a direito lê-se como cano, não madeira.
  for (const px of [1, 84]) {
    b.r(px + 2, 0, 6, 2, postHi);
    b.r(px + 1, 2, 8, 2, post);
    b.r(px + 1, 2, 8, 1, postHi);
    b.r(px, 4, 10, 102, post);
    b.r(px, 4, 2, 102, postHi);
    b.r(px + 8, 4, 2, 102, postDk);
    b.dither(px + 2, 6, 6, 98, post, "#5e4127"); // veio
  }
  // ── Moldura verde + friso de ouro + painel creme.
  b.r(9, 11, 79, 61, frame);
  b.r(9, 11, 79, 2, frameHi);
  b.r(9, 70, 79, 2, frameDk);
  b.r(11, 15, 75, 52, gold);
  b.r(12, 16, 73, 50, board);
  b.r(12, 16, 73, 2, "#ffffff");
  b.r(12, 63, 73, 3, boardSh);
  // ── Ferragens: uma por cima e uma por baixo, a prender o painel aos postes.
  for (const my of [11, 66]) {
    for (const mx of [1, 86]) {
      b.r(mx, my, 9, 6, metal);
      b.r(mx, my, 9, 1, "#b6bdc5");
      b.r(mx, my + 5, 9, 1, "#4d545c");
      b.r(mx + 3, my + 2, 3, 2, "#4d545c"); // parafuso
    }
  }
  // ── Logótipo grande, o mesmo do balcão da recepção mas ao dobro.
  marcaGV(b, 30, 19, 4);
  // ── Letreiro: blocos de letra, que é o que 7 px de altura dá para ler.
  const letras = (x0: number, n: number, y: number, w: number, h: number, passo: number, c: string): void => {
    for (let i = 0; i < n; i++) b.r(x0 + i * passo, y, w, h, c);
  };
  letras(18, 5, 52, 3, 7, 5, frameDk); // GREEN
  letras(47, 7, 52, 3, 7, 5, frameDk); // VILLAGE
  // ── Fita mais fina, em creme sobre verde.
  b.r(24, 59, 48, 6, frame);
  b.r(24, 59, 48, 1, frameHi);
  b.r(24, 64, 48, 1, frameDk);
  letras(27, 6, 61, 2, 3, 4, board); // MOBILE
  letras(53, 5, 61, 2, 3, 4, board); // HOMES
}

/** CONTENTOR verde do lixo com tampas inclinadas, rodas e selo GV. coords 64×56. */
function drawDumpster(b: Brush): void {
  const g = "#2f7d3e";
  const gHi = "#4fb063";
  const gSh = "#1c5a2c";
  const gDk = "#123f1f";
  const lid = "#256b34";
  const lidHi = "#3f9550";
  const metal = "#3a3f45";
  b.r(4, 20, 56, 30, g); // corpo
  b.r(4, 20, 56, 2, gHi);
  b.r(4, 48, 56, 2, gDk);
  b.r(4, 20, 3, 30, gHi);
  b.r(57, 20, 3, 30, gSh);
  b.r(2, 46, 60, 4, gSh); // saia inferior
  b.r(20, 22, 1, 26, gSh); // vincos
  b.r(43, 22, 1, 26, gSh);
  b.dither(8, 26, 48, 18, gHi, g);
  b.r(2, 12, 30, 9, lid); // tampas
  b.r(2, 12, 30, 2, lidHi);
  b.r(2, 20, 30, 1, gDk);
  b.r(32, 12, 30, 9, lid);
  b.r(32, 12, 30, 2, lidHi);
  b.r(32, 20, 30, 1, gDk);
  b.r(30, 10, 4, 4, metal); // dobradiça
  b.r(10, 50, 8, 6, metal); // rodas
  b.r(46, 50, 8, 6, metal);
  b.r(11, 51, 6, 4, "#1c1f23");
  b.r(47, 51, 6, 4, "#1c1f23");
  drawGVLeaf(b, 26, 26, 1);
}

/** CAIXOTE do lixo metálico com tampa e caneluras. coords 32×48. */
function drawTrashCan(b: Brush): void {
  const m = "#8a9099";
  const mHi = "#b6bbc2";
  const mSh = "#5f646b";
  const mDk = "#42464c";
  b.r(6, 12, 20, 32, m); // corpo
  b.r(6, 12, 3, 32, mHi);
  b.r(23, 12, 3, 32, mSh);
  for (let y = 16; y < 44; y += 5) b.r(6, y, 20, 1, mSh);
  b.r(6, 42, 20, 2, mDk);
  b.r(4, 8, 24, 6, mHi); // tampa
  b.r(4, 8, 24, 1, "#d0d4d9");
  b.r(4, 13, 24, 1, mDk);
  b.r(13, 4, 6, 4, mSh); // puxador
  b.r(13, 4, 6, 1, mHi);
}

/** CONE de sinalização laranja com faixas reflectoras brancas. coords 32×40. */
function drawCone(b: Brush): void {
  const or = "#e8712a";
  const orHi = "#ff9a52";
  const orSh = "#c65f1f";
  const band = "#f4f0e8";
  const bandSh = "#cfc9bd";
  b.r(3, 34, 26, 4, orSh); // base larga
  b.r(3, 34, 26, 1, or);
  b.r(6, 31, 20, 3, orSh);
  b.r(9, 24, 14, 8, or); // corpo em degraus
  b.r(9, 24, 3, 8, orHi);
  b.r(20, 24, 3, 8, orSh);
  b.r(11, 16, 10, 8, or);
  b.r(11, 16, 2, 8, orHi);
  b.r(19, 16, 2, 8, orSh);
  b.r(13, 8, 6, 8, or);
  b.r(13, 8, 1, 8, orHi);
  b.r(14, 4, 4, 4, or); // topo
  b.r(15, 3, 2, 1, orHi);
  b.r(9, 22, 14, 3, band); // faixas brancas
  b.r(9, 24, 14, 1, bandSh);
  b.r(12, 13, 8, 3, band);
  b.r(12, 15, 8, 1, bandSh);
}

/**
 * CANTEIRO de pedra com maciço de flores laranja. coords 64×44 (2×1 tiles + 12 px).
 *
 * PLANO B: o que se vê no jogo é o `flowerbed-v2.png` (ver `officeFlora`), que
 * ganha esta chave antes de aqui se chegar. Este desenho chegou a estar em uso —
 * foi escrito para endireitar o canteiro, porque o ficheiro do pacote (`-v1`)
 * está em ISOMÉTRICO (as duas faces da caixa fogem ~30°) e o parque está todo em
 * 3/4 com as faces frontais a direito. O Kawã viu os dois e preferiu o ficheiro
 * torto: a arte desenhada perde em riqueza o que ganha em alinhamento, e a
 * diferença nota-se mais que a inclinação. Depois gerou um canteiro NOVO que é
 * frontal e tem a folhagem toda, e é esse o `-v2` — a saída que nenhum dos dois
 * lados da escolha tinha, e a lição a reter: quando o dilema tem duas más metades,
 * vale mais dissolvê-lo do que ganhá-lo.
 *
 * Fica como rede de segurança (um 404 no PNG devolve-lhe a chave, ver `falhadas`),
 * e é por isso que continua à medida certa: frontal, SÓ com rectângulos alinhados
 * aos eixos, na MESMA paleta do `drawStoneWall` — canteiro e muro do parque como
 * obra da mesma mão.
 *
 * Tem 27 px de muro (quase o tile todo) e 12 px de folhagem ACIMA da pegada —
 * a licença do beiral, a mesma do `gameTable`: a arte sobe, o chão que ocupa não.
 */
function drawFlowerBox(b: Brush): void {
  const W = 64;
  const stone = "#a7adb4";
  const stoneHi = "#c4c9cf";
  const stoneSh = "#7f858c";
  const stoneDk = "#5f646b";
  const soil = "#4a3527";
  const soilHi = "#5e4433";
  const leaf = "#2f7d3e";
  const leafHi = "#54b96a";
  const leafSh = "#1c5a2c";

  // Terra à boca do canteiro. Vê-se pouco (a folhagem tapa-a quase toda), mas é
  // o que impede o maciço de parecer pousado EM CIMA da pedra.
  b.r(5, 12, W - 10, 9, soil);
  b.dither(5, 12, W - 10, 4, soilHi, soil);

  // Maciço. Não é um rectângulo verde nem uma pilha de caixotes verdes — as duas
  // primeiras tentativas foram isso, e ambas se liam como geometria em vez de
  // folha. É um PERFIL DE ALTURAS: para cada 2 px de frente, a linha onde a
  // folhagem começa. Faz uma silhueta serrilhada de degraus de 1-2 px, que a
  // esta escala o olho lê como folhagem, e não introduz uma única aresta oblíqua
  // longa — que é a condição toda deste desenho.
  //
  // Três elevações desencontradas (a do meio é a alta) porque um maciço com um
  // pico só fica simétrico, e simetria a esta escala lê-se como objecto moldado.
  //
  // O degrau anterior anda numa VARIÁVEL do ciclo em vez de se ir buscar a
  // `perfil[i - 1]`: com `noUncheckedIndexedAccess` ligado, indexar para trás dá
  // `number | undefined` e obrigava a um `?? 0` que nunca dispara — ruído a
  // fingir de segurança. `null` na primeira volta diz a verdade: não há anterior.
  const perfil = [11, 9, 7, 6, 6, 7, 5, 6, 8, 9, 8, 6, 4, 3, 3, 4, 5, 7, 9, 8, 7, 6, 7, 8, 9, 10, 11, 12];
  let ant: number | null = null;
  for (const [i, topo] of perfil.entries()) {
    const x = 4 + i * 2;
    b.r(x, topo, 2, 21 - topo, leaf);
    // O degrau que DESCE fica em sombra: é o que dá volume ao monte em vez de o
    // deixar chapado. O que sobe apanha o fio de luz de cima.
    if (ant !== null && topo > ant) b.r(x, topo, 2, topo - ant, leafSh);
    b.r(x, topo, 2, 1, leafHi);
    b.dither(x, topo + 1, 2, 3, leafHi, leaf);
    ant = topo;
  }
  b.r(4, 19, W - 8, 2, leafSh); // onde a folhagem entra na pedra

  // Flores. Laranja em bloco de 3×3 com o topo aceso e o miolo amarelo; brancas
  // em 2×2, menos e mais acima, que é o contraste que o ficheiro original tinha.
  const laranja: [number, number][] = [
    [9, 9],
    [15, 5],
    [21, 11],
    [28, 4],
    [34, 8],
    [40, 3],
    [47, 10],
    [53, 6],
    [12, 13],
    [44, 13],
    [58, 11],
  ];
  for (const [x, y] of laranja) {
    b.r(x - 1, y, 3, 3, "#e8862c");
    b.r(x - 1, y, 3, 1, "#f7a93a");
    b.r(x, y + 1, 1, 1, "#ffd66b"); // miolo
  }
  const brancas: [number, number][] = [
    [18, 8],
    [31, 10],
    [50, 4],
    [7, 12],
  ];
  for (const [x, y] of brancas) {
    b.r(x, y, 2, 2, "#f2efe6");
    b.r(x, y, 2, 1, "#ffffff");
  }

  // Muro. O rebordo (capping) vai de borda a borda e o pano recua 1 px de cada
  // lado — é essa diferença de 1 px que faz o tampo SOBRESSAIR sem uma única
  // linha oblíqua. Juntas desencontradas de fiada para fiada, como no muro.
  b.r(0, 17, W, 4, stoneHi);
  b.r(0, 20, W, 1, stoneSh); // fio de sombra debaixo do rebordo
  b.r(1, 21, W - 2, 20, stone);
  for (let ry = 24; ry < 41; ry += 8) {
    const off = (((ry / 8) | 0) % 2) * 8;
    for (let x = 1 + off; x < W - 2; x += 16) b.r(x, ry, 1, 8, stoneSh);
    b.r(1, ry, W - 2, 1, stoneSh);
  }
  b.r(1, 21, 1, 20, stoneHi); // aresta iluminada (a luz vem da esquerda)
  b.r(W - 2, 21, 1, 20, stoneSh);
  b.r(1, 41, W - 2, 3, stoneDk); // pé, encostado à linha do chão
}

/** VEDAÇÃO de madeira (ripas pontiagudas + 2 travessas). coords 128×48 (4 tiles). */
function drawFence(b: Brush): void {
  const W = 128;
  const wood = "#8a6238";
  const woodHi = "#a67d4c";
  const woodSh = "#5f4222";
  const woodDk = "#3f2c17";
  b.r(0, 18, W, 6, wood); // travessa superior
  b.r(0, 18, W, 1, woodHi);
  b.r(0, 23, W, 1, woodDk);
  b.r(0, 30, W, 6, wood); // travessa inferior
  b.r(0, 30, W, 1, woodHi);
  b.r(0, 35, W, 1, woodDk);
  for (let x = 4; x < W; x += 14) {
    b.r(x, 6, 8, 38, wood); // ripa
    b.r(x, 6, 2, 38, woodHi);
    b.r(x + 6, 6, 2, 38, woodSh);
    b.r(x + 1, 3, 6, 3, wood); // ponta em bico
    b.r(x + 2, 1, 4, 2, wood);
    b.r(x + 3, 0, 2, 1, woodHi);
    b.r(x + 3, 10, 2, 8, woodDk); // veio
  }
}

/** MURO baixo de pedra com sebe verde por cima (a delimitação sul). coords 128×44. */
function drawStoneWall(b: Brush): void {
  const W = 128;
  const stone = "#a7adb4";
  const stoneHi = "#c4c9cf";
  const stoneSh = "#7f858c";
  const stoneDk = "#5f646b";
  const hedge = "#2f7d3e";
  const hedgeHi = "#54b96a";
  const hedgeSh = "#1c5a2c";
  b.r(0, 6, W, 12, hedge); // sebe
  b.dither(0, 6, W, 12, hedgeHi, hedge);
  b.r(0, 6, W, 2, hedgeHi);
  b.r(0, 16, W, 2, hedgeSh);
  for (let x = 2; x < W; x += 8) b.r(x, 4, 4, 2, hedge); // topo recortado
  b.r(0, 18, W, 22, stone); // muro
  b.r(0, 18, W, 2, stoneHi);
  b.r(0, 38, W, 2, stoneDk);
  for (let ry = 20; ry < 38; ry += 8) {
    const off = (((ry / 8) | 0) % 2) * 8;
    for (let x = off; x < W; x += 16) b.r(x, ry, 1, 8, stoneSh); // juntas verticais
    b.r(0, ry, W, 1, stoneSh); // junta horizontal
  }
}

/**
 * LINHAS de estacionamento — só as riscas brancas sobre fundo TRANSPARENTE, para o
 * alcatrão ladrilhado aparecer por baixo. Desenhado plano (como o tapete).
 * coords 192×128 (6×4 tiles, cobertura exacta) = TRÊS baias de 2 tiles.
 *
 * As coordenadas estavam a MENTIR e metade das riscas nunca chegou ao ecrã. O
 * registo pedia 64×48 com `detail` 1, e o `texture` faz disso uma grelha de
 * `w × detail` por `h × detail` células — 64×48, não 128×96. Com W=128 e H=96
 * escritos à mão, as divisórias caíam de 32 em 32 CÉLULAS (o dobro do passo
 * pretendido, só duas cabiam), corriam 84 células de alto num canvas de 48 e o
 * batente de topo esticava-se para o dobro da largura. Via-se um bloco com uma
 * risca de um lado e nada do outro.
 *
 * Agora o registo pede `detail` 2, que põe a célula do pincel exactamente a 1 px
 * de ecrã: as coordenadas abaixo são px de ecrã e não é preciso conta nenhuma
 * para as ler.
 */
function drawParkingLines(b: Brush): void {
  const W = 192;
  const H = 128;
  const BAIA = 64; // 2 tiles, a largura de um carro
  const line = "#e2e6e4";
  const lineDk = "#b8bcbb";
  // Quatro divisórias para três baias: as das pontas recuam 2 px para ficarem
  // INTEIRAS dentro da pegada — uma risca a transbordar pinta o tile do vizinho.
  for (let i = 0; i * BAIA <= W; i++) {
    const x = Math.min(i * BAIA, W - 2);
    b.r(x, 6, 2, H - 12, line);
    b.r(x + 1, 6, 1, H - 12, lineDk);
  }
  b.r(0, 6, W, 2, line); // batente de topo
}

// ── Bunker de automações (subsolo + a boca no relvado) ─────
//
// Estas quatro peças são o DESENHO de reserva: três delas (`serverRack`,
// `aiConsole`, `analyticsScreen`) têm arte de pacote em `officeFurniture.ts`, que
// se regista debaixo da mesma chave e ganha quando o PNG carrega. Por isso as
// medidas daqui são as MESMAS que lá — 32×70, 160×96, 96×48. Divergir era ter um
// bastidor que muda de tamanho consoante a rede esteja boa: a sala tem de ficar
// igual nos dois casos, e é a única maneira de o 404 silencioso do Phaser (que
// mete um quadrado preto e não se queixa) passar despercebido em vez de partir a
// composição. A escotilha não tem PNG nenhum — é sempre este desenho.

/**
 * BASTIDOR de servidores: chapa escura, oito lâminas com grelha e LEDs vivos.
 *
 * detail=2 → 32×70. A pegada é UM tile (32×32), portanto 38 px transbordam para
 * CIMA — é isso que faz uma fila encostada à parede ler-se como armários altos e
 * não como caixotes. Os LEDs vêm do índice da lâmina e não de `Math.random`: o
 * mundo tem de ser o mesmo em todos os clientes.
 */
function drawServerRack(b: Brush): void {
  const steel = "#2b313a";
  const steelHi = "#454d59";
  const steelSh = "#1b2027";
  const steelDk = "#0e1116";
  const vent = "#121620";
  // ── Tampa com respiradouros.
  b.r(2, 0, 28, 6, steel);
  b.r(2, 0, 28, 1, steelHi);
  for (let x = 5; x < 27; x += 4) b.r(x, 2, 2, 3, vent);
  b.r(2, 5, 28, 1, steelSh);
  // ── Corpo.
  b.r(2, 6, 28, 60, steel);
  b.r(2, 6, 2, 60, steelHi);
  b.r(28, 6, 2, 60, steelDk);
  b.r(2, 64, 28, 2, steelDk);
  // ── Oito lâminas: grelha de arrefecimento, puxador e dois LEDs.
  for (let i = 0; i < 8; i += 1) {
    const y = 9 + i * 7;
    b.r(5, y, 22, 5, steelSh);
    b.r(5, y, 22, 1, mix(steel, steelHi, 0.4));
    b.r(5, y + 4, 22, 1, steelDk);
    b.dither(7, y + 1, 12, 3, vent, steelSh);
    b.r(20, y + 1, 5, 1, steelDk); // puxador
    b.r(25, y + 1, 1, 1, "#3ee27a"); // ligado
    b.r(25, y + 3, 1, 1, i % 3 === 0 ? "#f0a53a" : i % 3 === 1 ? "#2f9be0" : "#1f6b3c"); // tráfego
  }
  // ── Pés + os cabos que saem por trás.
  b.r(3, 66, 4, 4, steelDk);
  b.r(25, 66, 4, 4, steelDk);
  b.r(10, 66, 3, 3, "#14181f");
  b.r(15, 66, 3, 3, "#1d2a3a");
}

/**
 * A MÁQUINA do meio: parede de ecrãs, núcleo aceso e consola inclinada.
 *
 * detail=2 → 160×96, sobre uma pegada de 160×64 — os 32 px de sobra sobem, e é o
 * que põe a parede de ecrã acima da linha dos olhos de quem lhe passa à frente. É
 * a peça que justifica a sala ter largura ímpar: desenhada centrada em `baseX`,
 * cai no eixo exacto da sala e do vão da porta.
 */
function drawAIConsole(b: Brush): void {
  const shell = "#1e2530";
  const shellHi = "#39424f";
  const shellSh = "#141a23";
  const shellDk = "#0a0e14";
  const glass = "#0b1420";
  const core = "#2fd4c8";
  const coreHi = "#b9fff8";
  // ── Coroa: alhetas de dissipação.
  b.r(10, 0, 140, 8, shell);
  b.r(10, 0, 140, 2, shellHi);
  for (let x = 16; x < 144; x += 6) b.r(x, 2, 3, 5, shellDk);
  b.r(10, 7, 140, 1, shellSh);
  // ── Corpo alto + o vidro corrido dos três ecrãs.
  b.r(6, 8, 148, 56, shell);
  b.r(6, 8, 3, 56, shellHi);
  b.r(151, 8, 3, 56, shellDk);
  b.r(6, 62, 148, 2, shellSh);
  b.r(12, 12, 136, 46, glass);
  b.r(12, 12, 136, 1, "#1b2b3c");
  // ── Ecrã esquerdo: linhas de registo a correr.
  b.r(16, 16, 40, 38, "#08131c");
  b.r(16, 16, 40, 1, "#123246");
  for (let i = 0; i < 9; i += 1) {
    b.r(19, 19 + i * 4, 8 + ((i * 7) % 26), 1, i % 4 === 0 ? "#3ee27a" : "#1f7f52");
  }
  // ── Ecrã central: o núcleo. Círculo por linhas (a raiz dá a meia-largura de
  // cada uma) com o degradê a escurecer para baixo — a esfera nasce da luz.
  b.r(62, 16, 36, 38, "#050d16");
  const cx = 80;
  const cy = 34;
  const rad = 13;
  for (let dy = -rad; dy <= rad; dy += 1) {
    const half = Math.round(Math.sqrt(rad * rad - dy * dy));
    if (half <= 0) continue;
    b.r(cx - half, cy + dy, half * 2, 1, mix(core, "#134a5e", (dy + rad) / (2 * rad)));
  }
  b.r(cx - 5, cy - 9, 8, 3, coreHi); // reflexo especular
  b.r(cx - 7, cy - 6, 3, 2, coreHi);
  b.r(cx - 16, cy, 4, 1, core); // o anel que o cerca, cortado pela esfera
  b.r(cx + 12, cy, 4, 1, core);
  // ── Ecrã direito: barras de carga.
  b.r(104, 16, 40, 38, "#08131c");
  b.r(104, 16, 40, 1, "#123246");
  for (let i = 0; i < 7; i += 1) {
    const h = 4 + ((i * 5) % 22);
    b.r(107 + i * 5, 50 - h, 3, h, i % 2 === 0 ? "#2f9be0" : "#8b5cf6");
  }
  b.r(106, 50, 36, 1, "#1c4c68");
  // ── Faixa média: respiradouros dos dois lados e o LED de estado ao centro.
  b.r(6, 64, 148, 10, shellSh);
  b.dither(14, 66, 60, 6, shellDk, shellSh);
  b.dither(86, 66, 60, 6, shellDk, shellSh);
  b.r(76, 66, 8, 6, core);
  b.r(76, 66, 8, 1, coreHi);
  // ── Consola inclinada (o teclado da máquina).
  b.r(20, 74, 120, 10, shell);
  b.r(20, 74, 120, 2, shellHi);
  b.r(20, 82, 120, 2, shellDk);
  for (let x = 26; x < 134; x += 6) {
    b.r(x, 76, 4, 2, "#2c3542");
    b.r(x, 76, 4, 1, "#4a5666");
  }
  // ── Plinto, com a luz do núcleo a bater-lhe em cima.
  b.r(2, 84, 156, 12, shellDk);
  b.r(2, 84, 156, 2, shellSh);
  b.r(60, 84, 40, 1, mix(core, shellSh, 0.6));
  b.r(2, 94, 156, 2, "#05080c");
}

/**
 * ECRÃ de parede com métricas: barras à esquerda, tendência à direita.
 *
 * detail=2 → 96×48, sobre uma pegada de 96×32 — 16 px sobem pela parede, como um
 * painel pendurado sobe. Não é sólido (tal como a TV da Sala de Descanso): está
 * na parede, não se esbarra nele.
 */
function drawAnalyticsScreen(b: Brush): void {
  const frame = "#141a22";
  const frameHi = "#2c3542";
  const frameSh = "#0a0e13";
  const glass = "#08131c";
  b.r(0, 0, 96, 44, frame);
  b.r(0, 0, 96, 2, frameHi);
  b.r(0, 0, 2, 44, frameHi);
  b.r(94, 0, 2, 44, frameSh);
  b.r(0, 42, 96, 2, frameSh);
  b.r(4, 4, 88, 36, glass);
  b.r(4, 4, 88, 1, "#173650");
  // ── Cabeçalho (um título que não se lê a esta escala — lê-se como título).
  b.r(8, 7, 26, 2, "#4fd1c5");
  b.r(8, 7, 12, 2, "#a7f3ec");
  // ── Histograma.
  for (let i = 0; i < 10; i += 1) {
    const h = 3 + ((i * 7) % 19);
    b.r(8 + i * 5, 34 - h, 3, h, i % 3 === 0 ? "#3ee27a" : "#2f9be0");
  }
  b.r(7, 34, 51, 1, "#1c4c68");
  // ── Linha de tendência: cada ponto puxa um degrau vertical até ao anterior,
  // senão via-se uma escada de traços soltos em vez de uma linha.
  const linha = [30, 26, 28, 22, 18, 20, 14];
  let prev = 30;
  for (let i = 0; i < linha.length; i += 1) {
    const y = linha[i] ?? prev;
    b.r(62 + i * 4, y, 4, 1, "#f0a53a");
    if (i > 0) b.r(62 + i * 4, Math.min(prev, y), 1, Math.abs(prev - y) + 1, "#f0a53a");
    prev = y;
  }
  b.r(61, 34, 32, 1, "#1c4c68");
  // ── Suporte de parede.
  b.r(42, 44, 12, 3, frameSh);
  b.r(42, 44, 12, 1, frameHi);
}

export const DOOR_HATCH_SLIDE = "door:hatchSlide";

/**
 * Vão da escotilha, em art-cells da grelha 96×64 de `drawBunkerHatch` — e, como
 * aqui detail=2, uma art-cell é um pixel final. Mesmo papel que `EXT_DOOR_GAP`:
 * é daqui que a cena tira a máscara que impede as folhas de pintarem por cima do
 * aro de betão quando recolhem.
 */
export const HATCH_GAP = { x: 20, y: 20, w: 56, h: 32 } as const;

/**
 * A ESCOTILHA no relvado: aro de betão, calhas de aço e o poço com escada.
 *
 * detail=2 → 96×64, exactamente a pegada de 3×2 — nada transborda, porque isto é
 * CHÃO e não um móvel: pisa-se de propósito. Desenhada de cima (como o tapete),
 * com o poço a escurecer para o fundo para dar profundidade sem perspectiva.
 *
 * A tampa NÃO está aqui. Isto é só a moldura FIXA, com o buraco já pintado como se
 * sempre tivesse estado aberto; as duas folhas que o tapam são sprites à parte
 * (`DOOR_HATCH_SLIDE`) que a cena faz correr. É o mesmo contrato das portas
 * exteriores, e é o único que permite ver a escotilha ABRIR ao chegar perto —
 * desenhar a tampa aqui dentro dava uma escotilha eternamente aberta, que é
 * precisamente o que o Kawã não pediu ("um bunker que se abre").
 */
function drawBunkerHatch(b: Brush): void {
  const rim = "#8d949b";
  const rimHi = "#b6bcc2";
  const rimSh = "#6a7076";
  const rimDk = "#4a4f55";
  const shaft = "#0b0f14";
  const g = HATCH_GAP;
  // ── Aro de betão assente na relva (mais estreito em cima → lê-se chanfrado).
  b.r(8, 6, 80, 52, rim);
  b.r(12, 4, 72, 4, rim);
  b.r(12, 4, 72, 2, rimHi);
  b.r(8, 6, 3, 52, rimHi);
  b.r(85, 6, 3, 52, rimDk);
  b.r(8, 56, 80, 2, rimDk);
  b.dither(12, 8, 72, 48, shade(rim, 0.94), rim);
  // ── A boca. O topo é o sítio mais fundo: é o que dá o ângulo de vista de cima.
  b.r(g.x, g.y, g.w, g.h, shaft);
  b.r(g.x, g.y, g.w, 2, "#04070a");
  b.r(g.x, g.y, 3, g.h, "#050810");
  b.r(g.x + g.w - 3, g.y, 3, g.h, "#11161d");
  for (let i = 0; i < 6; i += 1) {
    b.r(g.x + 4 + i, g.y + 4 + i * 2, g.w - 8 - i * 2, 1, mix("#1a222c", shaft, i / 6));
  }
  // ── Escada a mergulhar: os degraus apagam-se com a profundidade.
  b.r(38, g.y + 2, 2, g.h - 4, "#565f6a");
  b.r(56, g.y + 2, 2, g.h - 4, "#565f6a");
  for (let i = 0; i < 7; i += 1) b.r(38, g.y + 4 + i * 4, 20, 1, mix("#7c8794", shaft, i / 7));
  // ── Calhas de aço, uma de cada lado da boca: é por elas que as folhas correm, e
  // sem elas o par de chapas parecia estar simplesmente pousado no buraco.
  b.r(g.x - 4, g.y - 4, g.w + 8, 4, "#8e959e");
  b.r(g.x - 4, g.y - 4, g.w + 8, 1, "#b6bdc6");
  b.r(g.x - 4, g.y + g.h, g.w + 8, 4, "#6e757e");
  b.r(g.x - 4, g.y + g.h + 3, g.w + 8, 1, "#41474e");
  // ── Faixas de perigo nos dois cantos + a sombra do aro no relvado.
  for (const sx of [10, 62]) {
    for (let i = 0; i < 6; i += 1) b.r(sx + i * 4, 9, 3, 4, i % 2 ? "#22272e" : "#f0a53a");
  }
  b.r(8, 54, 80, 1, rimSh);
  b.r(10, 58, 76, 4, "#3a3f45");
  b.r(8, 62, 80, 2, "#0d1a10"); // contacto com a relva
}

/**
 * MEIA-TAMPA da escotilha — 28×32 px (14×16 a detail=2), metade exacta do vão de
 * 56 (`HATCH_GAP.w`), por isso o par fechado encosta ao pixel e o curso de
 * abertura é a própria largura da folha.
 *
 * Aço nervurado, não madeira: isto tapa um bunker. O bordo DIREITO é o livre (o
 * que encosta na outra folha) — a folha da direita é esta com `setFlipX(true)`,
 * exactamente como nas portas exteriores.
 */
function drawHatchLeaf(b: Brush): void {
  const aco = "#7a828c";
  const acoHi = "#a9b2bd";
  const acoSh = "#565d66";
  const acoDk = "#2e343b";
  b.r(0, 0, 28, 32, aco);
  b.r(0, 0, 28, 2, acoHi); // aresta de topo apanha a luz
  b.r(0, 0, 2, 32, mix(aco, acoHi, 0.5)); // bordo que recolhe
  b.r(26, 0, 2, 32, acoDk); // bordo livre → a junta ao meio lê-se dura
  b.r(0, 30, 28, 2, acoSh);
  // ── Nervuras diagonais de reforço (o que distingue chapa grossa de chapa fina).
  for (let i = 0; i < 6; i += 1) {
    const x0 = 1 + i * 5;
    for (let j = 0; j < 24; j += 1) {
      const nx = x0 + ((j / 3) | 0);
      if (nx < 25) b.r(nx, 4 + j, 2, 1, j % 2 ? acoSh : shade(aco, 0.93));
    }
  }
  // ── Chapa central + rebites (dois pixels: sombra e brilho, senão são manchas).
  b.r(4, 10, 18, 12, aco);
  b.r(4, 10, 18, 1, acoHi);
  b.r(4, 21, 18, 1, acoSh);
  for (const rx of [6, 19]) {
    for (const ry of [12, 19]) {
      b.r(rx, ry, 2, 2, acoDk);
      b.r(rx, ry, 1, 1, acoHi);
    }
  }
  // ── Puxador encastrado no bordo livre.
  b.r(22, 14, 4, 5, acoDk);
  b.r(23, 15, 2, 3, "#c2cad4");
}

/** Paleta de uma casa de exposição: quatro cores, tudo o resto sai delas. */
type ShowPal = { telha: string; parede: string; vidro: string; base: string };

/** Como acaba a casa em cima. */
type ShowTopo = "plano" | "arco" | "domo";

/**
 * As quatro paletas, lidas dos próprios ficheiros. Não são inventadas: cada uma
 * é a cor dominante do telhado, da fachada, dos vãos e da base da casa que
 * substitui, para que a falha de um PNG não mude a COR do parque — só o grau de
 * detalhe.
 */
const PAL_CASAS = {
  dome: { telha: "#8f9aa4", parede: "#d7cdb8", vidro: "#2a4e58", base: "#6b6152" },
  capsula: { telha: "#c8ccd0", parede: "#dfe3e6", vidro: "#2b4d57", base: "#5a6068" },
  modular: { telha: "#4e5359", parede: "#c08f57", vidro: "#254450", base: "#3c3f44" },
  capsulaAlta: { telha: "#dfe3e7", parede: "#f0f2f4", vidro: "#2a4c58", base: "#6d747c" },
} satisfies Record<string, ShowPal>;

/**
 * O plano B das quatro casas novas — UM desenhador para as quatro, e não quatro
 * desenhadores.
 *
 * A razão é o `Brush`: ele só sabe `r()` e `dither()`, não leva medida nenhuma,
 * e por isso todos os desenhadores desta folha pintam em coordenadas ABSOLUTAS
 * para uma grelha fixa. Reaproveitar o `drawMobileHome` (80×74) numa caixa de
 * 80×48 não encolhia a casa — cortava-lhe a saia e as rodas pelo fundo. Ou se
 * escrevia uma função por casa, ou se escrevia UMA que recebe a caixa. É esta:
 * tudo aqui dentro deriva de `w`/`h`, portanto cada casa enche exactamente o seu
 * quadro e nenhuma transborda.
 *
 * A geometria é a mesma vista de três-quartos do `drawMobileHome` — quatro
 * cantos de telhado e uma altura — mas com as proporções em fracções da caixa:
 * a laje foge 0,45·w em y (a fuga que dá o volume), limitada a 55% da altura
 * disponível para que uma caixa baixa e larga (a cápsula elevada, 96×49) leia
 * como um módulo comprido e não como uma laje esmagada. O que sobra é o corpo.
 *
 * Isto quase nunca chega a pintar-se: `ensureFurnitureTextures` corre ANTES e
 * enche `prop:<kind>` com o PNG assado, e `texture()` desiste à primeira linha
 * se a chave já existir. Só se um ficheiro faltar é que esta função aparece — e
 * é por isso que ela mede o MESMO que o PNG (metade das dimensões do ficheiro,
 * porque o canvas do `texture()` é sempre 2×): falhe o que falhar, a casa ocupa
 * o mesmo sítio e o parque não muda de forma.
 */
function drawShowHome(b: Brush, w: number, h: number, pal: ShowPal, topo: ShowTopo): void {
  const telhaHi = shade(pal.telha, 1.24);
  const telhaSh = shade(pal.telha, 0.7);
  const paredeHi = shade(pal.parede, 1.13);
  const paredeSh = shade(pal.parede, 0.76);
  const vidroHi = mix(pal.vidro, "#cfe6ee", 0.5);
  const aro = mix(pal.parede, "#ffffff", 0.6);

  // ── A caixa. `coroa` é o que a abóbada/cúpula sobe ACIMA da laje, e é descontada
  // antes de repartir a altura — senão a curva saía fora do quadro e era cortada a
  // meio.
  const coroa = topo === "arco" || topo === "domo" ? Math.max(3, Math.round(h * 0.14)) : 0;
  const rd = Math.min(Math.round(w * 0.45), Math.round((h - 2 - coroa) * 0.55));
  const alt = h - 2 - coroa - rd;
  const yT = 1 + coroa;

  // Cantos da laje: T ao fundo, L à esquerda, R à direita, N o mais perto de quem
  // olha. Os x são fracções da largura (a casa tem de encher o quadro ao lado);
  // os y saem todos de `rd`, que é a fuga.
  const T: Pt = [Math.round(w * 0.55), yT];
  const L: Pt = [Math.round(w * 0.075), yT + Math.round(rd * 0.667)];
  const R: Pt = [Math.round(w * 0.9), yT + Math.round(rd * 0.333)];
  const N: Pt = [Math.round(w * 0.075) + (Math.round(w * 0.9) - Math.round(w * 0.55)), yT + rd];
  const BL: Pt = [L[0], L[1] + alt];
  const BN: Pt = [N[0], N[1] + alt];
  const BR: Pt = [R[0], R[1] + alt];

  // ── Telhado. Três acabamentos, e a escolha não é decorativa: é o que faz o
  // plano B ter a MESMA silhueta do PNG que substitui. Uma cápsula com telhado
  // plano lia-se como outra casa qualquer.
  if (topo === "domo") {
    // Casca elíptica com meridianos — a cúpula geodésica reduzida ao essencial.
    const cx = Math.round((L[0] + R[0]) / 2);
    const ry = coroa + Math.round(rd * 0.8);
    const rx = Math.round((R[0] - L[0]) / 2);
    for (let dy = -ry; dy <= 0; dy += 1) {
      const dx = Math.round(rx * Math.sqrt(Math.max(0, 1 - (dy * dy) / (ry * ry))));
      const t = Math.abs(dy) / ry;
      b.r(cx - dx, N[1] + dy, dx * 2, 1, mix(telhaSh, telhaHi, t));
    }
    for (const u of [-0.72, -0.36, 0, 0.36, 0.72]) {
      for (let dy = -ry; dy <= 0; dy += 1) {
        const dx = rx * Math.sqrt(Math.max(0, 1 - (dy * dy) / (ry * ry)));
        b.r(Math.round(cx + u * dx), N[1] + dy, 1, 1, telhaSh);
      }
    }
  } else if (topo === "arco") {
    // Abóbada de berço: varre-se a fuga L→N e cada tira sobe `coroa·sen(πs)`. Em
    // s=0 e s=1 o seno é zero, logo a abóbada encosta EXACTAMENTE em L e em N e
    // as paredes continuam a nascer da mesma aresta.
    const dx = R[0] - N[0];
    const dy = R[1] - N[1];
    const passos = Math.max(10, rd);
    const em = (s: number): Pt => [L[0] + (N[0] - L[0]) * s, L[1] + (N[1] - L[1]) * s - coroa * Math.sin(Math.PI * s)];
    for (let i = 0; i < passos; i += 1) {
      const a = em(i / passos);
      const z = em((i + 1) / passos);
      const t = Math.sin(Math.PI * ((i + 0.5) / passos));
      quad(b, a, [a[0] + dx, a[1] + dy], [z[0] + dx, z[1] + dy], z, mix(telhaSh, telhaHi, t));
    }
  } else {
    quad(b, L, T, R, N, pal.telha);
    for (const k of [0.28, 0.56, 0.84]) {
      const a: Pt = [L[0] + (T[0] - L[0]) * k, L[1] + (T[1] - L[1]) * k];
      const z: Pt = [N[0] + (R[0] - N[0]) * k, N[1] + (R[1] - N[1]) * k];
      quad(b, ...naFace(a, z, 0, 1, 0, 1.2), telhaSh);
    }
    quad(b, ...naFace(L, T, 0, 1, 0, 1.4), telhaHi);
    quad(b, ...naFace(T, R, 0, 1, 0, 1.4), telhaHi);
    // Caixa de AC assente no plano da laje (as faces seguem as mesmas fugas).
    const ax = Math.round(w * 0.34);
    const ay = Math.round(L[1] + (N[1] - L[1]) * 0.35);
    const aw = Math.max(6, Math.round(w * 0.14));
    const ah = Math.max(4, Math.round(rd * 0.2));
    quad(b, [ax, ay], [ax + aw, ay - ah], [ax + aw + 4, ay - ah + 2], [ax + 4, ay + 2], "#efe8da");
    quad(b, [ax, ay], [ax + 4, ay + 2], [ax + 4, ay + 2 + ah], [ax, ay + ah], "#c9c1af");
    quad(b, [ax + 4, ay + 2], [ax + aw + 4, ay - ah + 2], [ax + aw + 4, ay + 2], [ax + 4, ay + 2 + ah], "#ded5c2");
  }

  // ── Beiral: a chapa fina por baixo das duas arestas da frente. É ela que
  // separa telhado de parede quando as duas cores são próximas.
  quad(b, ...naFace(L, N, 0, 1, 0, 1.8), telhaSh);
  quad(b, ...naFace(N, R, 0, 1, 0, 1.8), telhaHi);

  // ── Corpo. Empena à sombra, fachada à luz: é a diferença entre as duas, e não
  // um contorno, que faz o canto ler-se.
  quad(b, L, N, BN, BL, paredeSh);
  quad(b, N, R, BR, BN, pal.parede);
  quad(b, ...naFace(N, R, 0, 0.05, 1.8, alt), paredeHi);
  const passo = Math.max(3, Math.round(alt * 0.22));
  for (let d = Math.round(alt * 0.16); d < alt - 2; d += passo) {
    quad(b, ...naFace(L, N, 0, 1, d, d + 0.7), shade(paredeSh, 0.93));
    quad(b, ...naFace(N, R, 0, 1, d, d + 0.7), shade(pal.parede, 0.94));
  }

  // ── Vãos. Uma janela por parede, com moldura, vidro e o reflexo no quadrante
  // superior esquerdo — sempre o mesmo, que é o que dá a sensação de uma só luz.
  const jy0 = Math.max(2, Math.round(alt * 0.2));
  const jy1 = Math.round(alt * 0.62);
  const janela = (a: Pt, z: Pt, t0: number, t1: number): void => {
    quad(b, ...naFace(a, z, t0, t1, jy0 - 1, jy1 + 1), aro);
    quad(b, ...naFace(a, z, t0, t1, jy0, jy1), pal.vidro);
    quad(b, ...naFace(a, z, t0, (t0 + t1) / 2, jy0 + 1, (jy0 + jy1) / 2), vidroHi);
  };
  janela(L, N, 0.24, 0.72);
  janela(N, R, 0.58, 0.86);

  // ── Base e porta, por esta ordem: a faixa da base atravessa a fachada toda e é
  // a PORTA que a interrompe. Ao contrário, ficava uma tira a passar à frente da
  // porta e a porta a pairar.
  const faixa = Math.max(2, Math.round(alt * 0.16));
  quad(b, ...naFace(L, N, 0, 1, alt - faixa, alt), pal.base);
  quad(b, ...naFace(N, R, 0, 1, alt - faixa, alt), shade(pal.base, 1.1));
  quad(b, ...naFace(N, R, 0.14, 0.34, Math.round(alt * 0.12), alt), aro);
  quad(b, ...naFace(N, R, 0.17, 0.31, Math.round(alt * 0.18), alt - 1), shade(pal.vidro, 0.65));
  quad(b, ...naFace(N, R, 0.185, 0.25, Math.round(alt * 0.24), Math.round(alt * 0.42)), shade(pal.vidro, 1.3));
  quad(b, ...naFace(N, R, 0.295, 0.315, Math.round(alt * 0.46), Math.round(alt * 0.46) + 2), "#d8b45e");
}

// ── A escada do 1.º andar ───────────────────────────────────
//
// As duas peças medem 96×128 (a pegada 3×4 de `space.ts`). Filas y 0-31 são o
// remate (a laje em baixo, o patamar em cima) e y 32-127 os seis degraus, dois
// por tile. Na largura, a escada ocupa x 4-57 — mais estreita do que as duas
// colunas que se pisam (0-63), a pedido: *"diminua um pouco a largura dela"* —
// e o corrimão vem logo a seguir, em x 58-79. O que NÃO é peça fica
// transparente: o chão da sala está pintado por baixo e tem de se ver.
//
// Cada degrau, visto de cima e de frente, é o que faz uma escada ler como
// escada e não como um painel de tábuas: o PISO claro em cima, o focinho a
// apanhar a luz, o ESPELHO escuro por baixo, e a longarina de lado em dente de
// serra. A passadeira verde ao meio, presa por varões de latão, dá o ritmo.
//
// Eram oito degraus em quatro filas, e saíram dois: *"diminuir uns 3 degraus
// dessa escada ou 2, tá muito grande"*. Menos de três filas não dá — a troca de
// andar precisa da folga de dois degraus (ver `ESCADAS`).

const DEGRAUS = 6;
/** Onde acabam os degraus, em px da arte. */
const FIM_DOS_DEGRAUS = 32 + DEGRAUS * 16;
/** A largura da escada: dos degraus às longarinas. */
const ESC_X0 = 4;
const ESC_X1 = 58;
/** O corrimão encosta à longarina da direita. */
const CORR_X = ESC_X1;

/** Os degraus, do de cima para o de baixo. `escuroEm(t)` (0 em cima, 1 em baixo) diz quanto a sombra come cada um. */
function degraus(b: Brush, escuroEm: (t: number) => number): void {
  const madeira = "#b08457";
  const passadeira = "#2f6e4c";
  const px0 = ESC_X0 + 3;
  const larg = ESC_X1 - ESC_X0 - 6;
  const tx0 = ESC_X0 + 14; // a passadeira, ao meio
  const tlarg = larg - 22;
  for (let i = 0; i < DEGRAUS; i += 1) {
    const y = 32 + i * 16;
    const e = escuroEm(i / (DEGRAUS - 1));
    const piso = mix(madeira, "#0b0e12", e);
    const espelho = mix("#5a3b22", "#07090c", e);
    const tapete = mix(passadeira, "#050807", e);
    // O piso (10 px), com um veio de madeira e a sombra do espelho de cima.
    b.r(px0, y, larg, 10, piso);
    b.r(px0, y, larg, 2, shade(piso, 0.78));
    b.r(px0 + 4 + (i % 3) * 9, y + 5, 10, 1, shade(piso, 0.9));
    // O focinho: a aresta da frente, a mais clara da peça.
    b.r(px0, y + 9, larg, 2, shade(piso, 1.35));
    // O espelho (5 px), vertical e na sombra.
    b.r(px0, y + 11, larg, 5, espelho);
    b.r(px0, y + 15, larg, 1, shade(espelho, 0.6));
    // A passadeira por cima: piso, dobra no focinho, espelho.
    b.r(tx0, y, tlarg, 10, tapete);
    b.r(tx0, y, tlarg, 2, shade(tapete, 0.75));
    b.r(tx0, y + 9, tlarg, 2, shade(tapete, 1.4));
    b.r(tx0, y + 11, tlarg, 5, shade(tapete, 0.62));
    b.r(tx0 + 1, y + 1, 1, 8, shade(tapete, 1.2)); // a orla da passadeira
    b.r(tx0 + tlarg - 2, y + 1, 1, 8, shade(tapete, 1.2));
    // O varão de latão, na dobra de trás do piso.
    b.r(tx0 - 2, y + 1, tlarg + 4, 1, mix("#e0b85a", "#0b0e12", e * 0.8));
    // As longarinas em dente de serra: cada degrau deixa um degrau no perfil.
    const longa = mix("#4a3122", "#07090c", e * 0.7);
    b.r(ESC_X0, y, 3, 16, longa);
    b.r(ESC_X1 - 3, y, 3, 16, longa);
    b.r(ESC_X0, y + 9, 3, 2, shade(longa, 1.5));
    b.r(ESC_X1 - 3, y + 9, 3, 2, shade(longa, 1.5));
  }
  // A face de fora da longarina esquerda, contra a parede, e a sombra dela.
  b.r(0, 32, ESC_X0, FIM_DOS_DEGRAUS - 32, "rgba(0, 0, 0, 0.32)");
}

/** Um corrimão de balaústres com o tubo por cima, de `y0` a `y1`. */
function corrimao(b: Brush, y0: number, y1: number): void {
  b.r(CORR_X + 12, y0, 6, y1 - y0, "rgba(0, 0, 0, 0.2)"); // sombra no chão da sala
  b.r(CORR_X, y0, 12, y1 - y0, "#c9c3b6"); // o murete baixo, da cor da parede
  b.r(CORR_X + 10, y0, 2, y1 - y0, "#8f897d");
  for (let y = y0 + 3; y < y1 - 2; y += 8) {
    b.r(CORR_X + 4, y, 4, 6, "#6f7a84"); // os balaústres, um por degrau e meio
    b.r(CORR_X + 4, y, 1, 6, "#b9c2ca");
  }
  b.r(CORR_X + 3, y0, 6, y1 - y0, "rgba(154, 164, 174, 0.35)"); // o tubo, visto de cima
  b.r(CORR_X + 3, y0, 2, y1 - y0, "#d5dce2");
}

/**
 * A escada vista do RÉS-DO-CHÃO: sobe para norte e mete-se por baixo da laje do
 * 1.º andar. Os degraus de cima estão mais perto do tecto e mais escuros — é esse
 * escurecer que diz "isto sobe" sem uma seta.
 */
function drawStairs(b: Brush): void {
  degraus(b, (t) => (1 - t) * 0.55);
  // A laje: o chão do andar de cima visto de baixo, com o rebordo à frente.
  const lx1 = CORR_X + 12;
  b.r(0, 0, lx1, 26, "#1a2027");
  b.r(0, 0, lx1, 10, "#12171d");
  b.r(0, 26, lx1, 6, "#3b444e");
  b.r(0, 26, lx1, 1, "#5d6974");
  b.r(0, 31, lx1, 1, "#0a0d11");
  b.r(ESC_X0, 32, ESC_X1 - ESC_X0, 4, "rgba(0, 0, 0, 0.35)"); // a sombra da laje no degrau de cima
  // O corrimão só nas filas que o `recorte` fecha; a de baixo é a entrada.
  corrimao(b, 32, FIM_DOS_DEGRAUS - 30);
  b.r(CORR_X - 1, FIM_DOS_DEGRAUS - 34, 14, 10, "#7c858e"); // o pilar do fim, onde se agarra ao chegar
  b.r(CORR_X - 1, FIM_DOS_DEGRAUS - 34, 14, 2, "#aab3bb");
}

/**
 * A mesma escada vista do 1.º ANDAR: um poço aberto no chão, com os degraus a
 * descer para sul e a escurecer à medida que se afundam. O corrimão fecha o poço
 * até à parede — só se entra de frente, pelo patamar.
 */
function drawStairwell(b: Brush): void {
  degraus(b, (t) => t * 0.8);
  // A borda do poço no patamar: um perfil de aço onde o chão acaba.
  b.r(0, 26, CORR_X, 6, "#7f8891");
  b.r(0, 26, CORR_X, 2, "#b9c1c8");
  b.r(ESC_X0, 32, ESC_X1 - ESC_X0, 4, "rgba(0, 0, 0, 0.35)");
  // Vidro e aço, que é o gabinete de um CEO e não uma cave.
  const alto = FIM_DOS_DEGRAUS - 32;
  b.r(CORR_X, 32, 12, alto, "rgba(150, 196, 214, 0.28)");
  b.r(CORR_X, 32, 2, alto, "#aeb7bf");
  b.r(CORR_X + 10, 32, 2, alto, "#6c757e");
  b.r(CORR_X + 3, 32, 6, alto, "#9aa4ae");
  b.r(CORR_X + 3, 32, 2, alto, "#dde3e8");
  for (let y = 36; y < FIM_DOS_DEGRAUS; y += 32) b.r(CORR_X + 1, y, 10, 4, "#5f6770");
}

// ── O heliponto (no telhado, à direita do gabinete do CEO) ──

/**
 * Um heliponto de 208×208 (6,5 tiles): laje de betão com a borda amarela de
 * segurança, o círculo verde-escuro com o anel branco, o H ao meio e as luzes de
 * pista à volta. Os círculos pintam-se ponto a ponto — o pincel só faz rectângulos.
 */
function drawHeliponto(b: Brush): void {
  const L = 208;
  // A sombra da laje no telhado, e a laje.
  b.r(6, 6, L - 6, L - 6, "rgba(0, 0, 0, 0.3)");
  b.r(0, 0, L - 6, L - 6, "#8b9096");
  b.dither(2, 2, L - 10, L - 10, "#8f949a", "#878c92");
  // A borda de segurança, às riscas amarelas e pretas.
  const borda = 7;
  for (let i = 0; i < L - 6; i += 12) {
    const cor = (i / 12) % 2 === 0 ? "#e8b923" : "#1d2126";
    b.r(i, 0, 12, borda, cor);
    b.r(i, L - 6 - borda, 12, borda, cor);
    b.r(0, i, borda, 12, cor);
    b.r(L - 6 - borda, i, borda, 12, cor);
  }
  const m = (L - 6) / 2;
  // O círculo de toque: verde-escuro, anel branco, e a orla amarela por dentro.
  for (let y = 0; y < L - 6; y += 1) {
    for (let x = 0; x < L - 6; x += 1) {
      const d = Math.hypot(x + 0.5 - m, y + 0.5 - m);
      if (d > 84) continue;
      let cor = (x + y) % 2 === 0 ? "#2d4a3b" : "#2a4637";
      if (d > 76) cor = "#f1f4f2";
      else if (d > 72) cor = "#2d4a3b";
      else if (d > 69) cor = "#e8b923";
      b.r(x, y, 1, 1, cor);
    }
  }
  // O H, em branco, com uma sombra leve para se ler em cima do verde.
  const hH = 70;
  const hW = 50;
  const traco = 13;
  const hx = m - hW / 2;
  const hy = m - hH / 2;
  const H = (dx: number, dy: number, cor: string): void => {
    b.r(hx + dx, hy + dy, traco, hH, cor);
    b.r(hx + hW - traco + dx, hy + dy, traco, hH, cor);
    b.r(hx + dx, hy + (hH - traco) / 2 + dy, hW, traco, cor);
  };
  H(2, 2, "rgba(0, 0, 0, 0.35)");
  H(0, 0, "#f7f9f8");
  // As luzes de pista, nos cantos e a meio de cada lado.
  for (const [lx, ly] of [
    [12, 12],
    [m, 12],
    [L - 18, 12],
    [12, m],
    [L - 18, m],
    [12, L - 18],
    [m, L - 18],
    [L - 18, L - 18],
  ] as const) {
    b.r(lx - 4, ly - 4, 8, 8, "rgba(163, 230, 53, 0.25)");
    b.r(lx - 2, ly - 2, 4, 4, "#4b5563");
    b.r(lx - 1, ly - 1, 2, 2, "#bef264");
  }
}

// ── O telhado (visto do 1.º andar) ──────────────────────────

/** A tela do telhado: gravilha cinzenta fosca, com as juntas da impermeabilização. */
function drawRoof(b: Brush, alt: boolean): void {
  const base = alt ? "#6a6e73" : "#707479";
  b.r(0, 0, 16, 16, base);
  b.dither(0, 0, 16, 16, shade(base, 1.06), shade(base, 0.94));
  b.r(0, 15, 16, 1, shade(base, 0.8));
  b.r(alt ? 3 : 10, 4, 2, 1, shade(base, 1.2)); // pedrinhas
  b.r(alt ? 11 : 2, 10, 1, 1, shade(base, 0.78));
  b.r(alt ? 7 : 13, 12, 1, 1, shade(base, 1.16));
}

/** Um painel solar de 64×40: moldura de alumínio, 18 células, e a sombra no telhado. */
function drawSolarPanel(b: Brush): void {
  b.r(2, 34, 62, 6, "rgba(0, 0, 0, 0.3)");
  b.r(0, 0, 62, 36, "#c2c8ce");
  b.r(2, 2, 58, 32, "#162642");
  for (let cx = 0; cx < 6; cx += 1) {
    for (let cy = 0; cy < 3; cy += 1) {
      const x = 3 + cx * 9.5;
      const y = 3 + cy * 10.3;
      b.r(x, y, 8.5, 9.3, cy === 0 ? "#2b4a76" : "#223d63");
      b.r(x, y, 8.5, 1, "#44689a");
    }
  }
  b.r(0, 0, 62, 1, "#e6ebef");
  b.r(0, 35, 62, 1, "#8b9298");
}

/** Uma máquina de ar condicionado de 40×36: caixa de chapa, grelha e ventoinha. */
function drawRoofAc(b: Brush): void {
  b.r(3, 30, 37, 6, "rgba(0, 0, 0, 0.3)");
  b.r(0, 8, 38, 24, "#a6adb4"); // a frente
  b.r(0, 0, 38, 9, "#cdd3d8"); // o tampo
  b.r(0, 0, 38, 1, "#eef1f3");
  b.r(0, 8, 38, 1, "#848b92");
  for (let i = 0; i < 5; i += 1) b.r(4, 12 + i * 4, 13, 2, "#747c84"); // grelha
  b.r(22, 11, 12, 18, "#2f353b"); // ventoinha
  b.r(20, 13, 16, 14, "#2f353b");
  b.r(26, 18, 4, 4, "#a3abb2");
  b.r(23, 19, 10, 2, "#555c63");
  b.r(27, 14, 2, 12, "#555c63");
}

/**
 * Cada prop: dimensões de arte (à escala detail=1) + o desenhador + o `detail`.
 * `detail: 2` = herói autorado ao dobro da densidade de pixels na MESMA pegada de
 * ecrã (secretária, cadeira, plantas). Os restantes ficam a detail=1 (o clássico).
 *
 * `mirror` vira a peça ao espelho no momento de a pintar. Já era o que os
 * assentos faziam para nascer a versão "direita" da esquerda (ver o `views`
 * abaixo); só faltava a mesma torneira para um prop simples — e é ela que dá as
 * duas orientações da mobile home sem duplicar um único traço de desenho.
 *
 * `fatias` é a terceira variante e resolve um problema que só aparece em peças
 * FUNDAS: um prop que ocupe várias LINHAS pintado como uma imagem só recebe uma
 * profundidade única (a da linha da frente) e tapa quem estiver nas de trás.
 * Com `fatias`, a peça nasce em N texturas — uma por linha — e cada uma entra na
 * cena com a profundidade da SUA linha, deixando o y-sort fazer o resto. `w`/`h`
 * passam a ser a medida de UMA fatia, não da peça inteira.
 */
type PropArt = { w: number; h: number; detail?: number; mirror?: boolean } & (
  | { draw: (b: Brush) => void; views?: undefined; fatias?: undefined }
  | { views: SeatDrawer; draw?: undefined; fatias?: undefined }
  | {
      fatias: number;
      slice: (b: Brush, i: number) => void;
      draw?: undefined;
      views?: undefined;
    }
);

const PROP_DRAWERS: Record<PropKind, PropArt> = {
  deskPC: { w: 48, h: 27, draw: drawDeskPC, detail: 2 },
  // 32×23 a detail=2 → 64×46, a medida final de `sales-desk-v1.png`. Mesma regra
  // do `deskDual` e do `serverRack`: os dois planos medem igual, logo a sala fica
  // idêntica quer o PNG carregue quer não.
  salesDesk: { w: 32, h: 23, draw: drawSalesDesk, detail: 2 },
  // 45×26 a detail=2 → 90×52, exactamente a medida final da arte do pacote
  // (`officeFurniture.ts`). Se o PNG estiver lá, é ele que fica com a chave — e
  // como as duas medidas são a mesma, a sala fica igual em qualquer dos casos.
  deskDual: { w: 45, h: 26, draw: drawDeskDual, detail: 2 },
  officeChair: { w: 16, h: 20, views: drawOfficeChair, detail: 2 },
  // 48×36 a detail=2 → 96×72, idem: a medida final do recorte da mesa do pacote.
  meetingTable: { w: 48, h: 36, draw: drawMeetingTable, detail: 2 },
  meetingChair: { w: 16, h: 18, views: drawMeetingChair, detail: 2 },
  whiteboard: { w: 64, h: 24, draw: drawWhiteboard },
  // 48×23 a detail=2 → 96×46, a medida do PNG do pacote. Perdeu 2 px de altura
  // quando o sofá passou a vir do render: o rácio limpo dele (2,0764) sobre os 96
  // da pegada de 3×1 dá 46,2, e 46 é o par mais próximo. Ver `CUSHION_TOP.couch`
  // — é um ASSENTO, e a almofada mudou de sítio com a altura.
  couch: { w: 48, h: 23, draw: drawCouch, detail: 2 },
  // O mesmo sofá de perfil, em 3 fatias de 20×30 a detail=2 → 40×60 cada, que é
  // a medida dos três PNG do pacote (`couch-side-v1-0..2`). A largura da fatia é
  // 40 e não os 32 do tile de propósito: os 4 px que sobram de cada lado são a
  // profundidade do móvel, que a pegada 1×3 não tem. Não faz parede invisível
  // nenhuma (`couchSide` não é sólido), só se vê.
  //
  // Eram 44×44 quando o sofá era desenhado. A pegada de 3 tiles fixa o passo das
  // almofadas em 32 px — 3×32 = 96 de estofo, mais os dois braços, dá um composto
  // de 124 de alto, logo 60 por fatia (o composto de N fatias mede 64 + h). A 44
  // de largo o passo saía 34,8 e as três almofadas deixavam de bater com os tiles.
  // A largura seguia o rácio 3,100 do render (124/3,100 = 40) até 2026-09-01;
  // ficou em 34 a pedido, que é 1 px a transbordar do tile em vez de 4.
  couchSide: { w: 17, h: 30, detail: 2, fatias: COUCH_SIDE_SLICES, slice: drawCouchSide },
  coffeeTable: { w: 32, h: 16, draw: drawCoffeeTable, detail: 2 },
  tv: { w: 42, h: 27, draw: drawTV, detail: 2 },
  rug: { w: 64, h: 48, draw: drawRug, detail: 2 },
  // 48×26 a detail=2 → 96×52 de ecrã, EXACTAMENTE a medida do PNG do pacote: os
  // dois planos medem igual, logo o balcão não muda de tamanho consoante o
  // ficheiro tenha ou não carregado (precedente do `serverRack`).
  receptionDesk: { w: 48, h: 26, draw: drawReceptionDesk, detail: 2 },
  plant: { w: 16, h: 22, draw: drawPlant, detail: 2 },
  plantTall: { w: 16, h: 36, draw: drawPlantTall, detail: 2 },
  // 64×29 → 128×58 e 16×31 → 32×62: as medidas de `kitchen-counter-v1.png` e
  // `kitchen-fridge-v1.png`. Cresceram com o PNG (24→29, 30→31) porque o plano B
  // tem de ocupar os mesmos pixels do plano A — um 404 não pode encolher a copa.
  counter: { w: 64, h: 29, draw: drawCounter },
  fridge: { w: 16, h: 31, draw: drawFridge },
  diningTable: { w: 32, h: 28, draw: drawDiningTable, detail: 2 },
  diningChair: { w: 16, h: 18, views: drawDiningChair, detail: 2 },
  // 8×22 a detail=2 → 16×44, a medida do PNG. É `texture` que multiplica por 2, o
  // que obriga a arte final a ter os DOIS lados pares — a razão de o bebedouro não
  // poder crescer para o 17×45 que a proporção do render pedia (`officeFurniture`).
  waterCooler: { w: 8, h: 22, draw: drawWaterCooler, detail: 2 },
  // 32×27 a detail=2 → 64×54, a medida do PNG. Os 64 de largura são a pegada de
  // 2×1 e não se negoceiam (a estante encosta à parede em três salas); a altura
  // sai do rácio da caixa limpa do render e desceu 2 px por causa disso.
  bookshelf: { w: 32, h: 27, draw: drawBookshelf, detail: 2 },
  // 12×34 a detail=2 → 24×68, a medida de `grandfather-clock-v1.png`: o plano B
  // ocupa os mesmos pixels do PNG, para o relógio não mudar de tamanho num 404.
  grandfatherClock: { w: 12, h: 34, draw: drawGrandfatherClock, detail: 2 },
  // 32×26 a detail=2 → 64×52 de ecrã sobre uma pegada de 2×1 tiles (64×32): os
  // 20 px que sobram sobem ACIMA do chão que a mesa ocupa, que é onde fica o
  // tabuleiro em pé. A mesma licença do beiral das mobile homes — arte mais alta
  // que a pegada, e é ela que dá a leitura do jogo à distância.
  gameTable: { w: 32, h: 26, draw: drawGameTable, detail: 2 },
  // 16×35 a detail=2 → 32×70 de ecrã sobre uma pegada de 1×1 tile (32×32): os 38
  // px que sobram são o armário a subir por cima do tile. É a mesma licença do
  // bastidor do bunker, e é ela que faz a máquina ter altura de gente em vez de
  // altura de caixote. A pegada tem de continuar a ser 1×1 ou a arcade deixava
  // de caber na única coluna livre da parede da sala de descanso.
  arcade: { w: 16, h: 35, draw: drawArcade, detail: 2 },
  // ── Parque de mobile homes (exterior sul).
  // 80×74 de arte = 160×148 de ecrã. Isto JÁ NÃO É o tamanho do recorte que o
  // `officeFurniture.ts` regista nestas chaves — desde que as casas passaram a
  // 7×4 tiles o plano A tem 224×208, e o `texture()` não consegue lá chegar: o
  // desenho sai sempre a 2× do que se pede aqui e `detail` só afina a grelha,
  // não a medida. Quem fecha a diferença é o `OfficeSpace`, que normaliza estas
  // seis chaves à largura da pegada (`PARK_HOME_PROPS`); a conta dá 1 quando o
  // PNG carregou e amplia o desenho à mão quando não carregou. O que tem de bater
  // — e bate — é a PROPORÇÃO: 80/74 contra 224/208 são 1,081 e 1,077.
  // `mirror` dá a segunda orientação sem um segundo desenho — a mesma torneira
  // que já dava as cadeiras viradas para a direita.
  mobileHome: { w: 80, h: 74, draw: drawMobileHome },
  mobileHomeFlip: { w: 80, h: 74, draw: drawMobileHome, mirror: true },
  // Os outros dois modelos do parque, pela mesma regra e com a mesma proporção
  // dos PNGs que substituem (288×100 → 96×33 dá 2,88 contra 2,909; 224×160 →
  // 80×57 dá 1,40 contra 1,404). Trocar um número aqui não muda o tamanho no
  // ecrã — a normalização repõe-no pela largura — mas distorce a casa.
  mobileHomeSide: { w: 96, h: 33, draw: drawMobileHomeSide },
  mobileHomeSideFlip: { w: 96, h: 33, draw: drawMobileHomeSide, mirror: true },
  mobileHomeArch: { w: 80, h: 57, draw: drawMobileHomeArch },
  mobileHomeArchFlip: { w: 80, h: 57, draw: drawMobileHomeArch, mirror: true },
  // ── As quatro casas novas. Cada medida é EXACTAMENTE metade do PNG assado
  // (o canvas do `texture()` é sempre 2× as dimensões declaradas), portanto o
  // plano B ocupa o mesmo rectângulo que o plano A e o parque não muda de forma
  // se um ficheiro faltar. Nenhuma tem `mirror`: são quatro casas DIFERENTES, e
  // espelhar uma não dava variedade nenhuma — dava a mesma casa com a porta
  // trocada de lado. O `topo` é o que preserva a silhueta de cada uma.
  homeDome: { w: 64, h: 61, draw: (b) => drawShowHome(b, 64, 61, PAL_CASAS.dome, "domo") },
  homeCapsuleClosed: { w: 80, h: 48, draw: (b) => drawShowHome(b, 80, 48, PAL_CASAS.capsula, "arco") },
  homeModular: { w: 96, h: 58, draw: (b) => drawShowHome(b, 96, 58, PAL_CASAS.modular, "plano") },
  homeCapsule: { w: 112, h: 58, draw: (b) => drawShowHome(b, 112, 58, PAL_CASAS.capsulaAlta, "arco") },
  // A casa do lago, pela mesma regra: 96×49 é metade do `floating-house.png`
  // (192×98), para o plano B ocupar exactamente a água que o plano A ocupa.
  floatingHouse: { w: 96, h: 49, draw: drawFloatingHouse },
  // 32×64 dá 64×128 px de ecrã (o `texture` duplica), que é a pegada 2×4 ao
  // pixel; `detail` 2 põe a célula do pincel a 1 px, logo as coordenadas do
  // `drawCar` são px de ecrã.
  carSedan: { w: 32, h: 64, draw: drawCarSedan, detail: 2 },
  carSport: { w: 32, h: 64, draw: drawCarSport, detail: 2 },
  carSuper: { w: 32, h: 64, draw: drawCarSuper, detail: 2 },
  carSuv: { w: 32, h: 64, draw: drawCarSuv, detail: 2 },
  streetLamp: { w: 16, h: 56, draw: drawStreetLamp, detail: 2 },
  // 48×53 a detail=2 → 96×106. A LARGURA não mexeu (3 tiles, ao pixel); só a
  // altura, porque agora os postes vêm inteiros de cima a baixo.
  parkSign: { w: 48, h: 53, draw: drawParkSign, detail: 2 },
  dumpster: { w: 32, h: 28, draw: drawDumpster, detail: 2 },
  trashCan: { w: 16, h: 24, draw: drawTrashCan, detail: 2 },
  cone: { w: 16, h: 20, draw: drawCone, detail: 2 },
  // 32×22 a detail=2 → 64×44 de ecrã sobre uma pegada de 2×1 tiles (64×32). Era
  // 16×16 (32×32, um tile) enquanto isto era só o plano B de um PNG — deixou de
  // ser plano B (ver `drawFlowerBox`), portanto passa a ter a medida a sério.
  flowerBox: { w: 32, h: 22, draw: drawFlowerBox, detail: 2 },
  fence: { w: 64, h: 24, draw: drawFence, detail: 2 },
  stoneWall: { w: 64, h: 22, draw: drawStoneWall, detail: 2 },
  parkingLines: { w: 96, h: 64, draw: drawParkingLines, detail: 2 },
  // ── Bunker de automações. As três primeiras medidas são as da arte do pacote
  // (`officeFurniture.ts`), de propósito: quando o PNG carrega é ele que fica com
  // a chave, e como a medida é a mesma a sala fica igual em qualquer dos casos.
  serverRack: { w: 16, h: 35, draw: drawServerRack, detail: 2 }, // 32×70
  aiConsole: { w: 80, h: 48, draw: drawAIConsole, detail: 2 }, // 160×96
  analyticsScreen: { w: 48, h: 24, draw: drawAnalyticsScreen, detail: 2 }, // 96×48
  bunkerHatch: { w: 48, h: 32, draw: drawBunkerHatch, detail: 2 }, // 96×64, = a pegada
  stairs: { w: 48, h: 64, draw: drawStairs, detail: 2 }, // 96×128, = a pegada 3×4
  stairwell: { w: 48, h: 64, draw: drawStairwell, detail: 2 },
};

/** Este móvel desenha-se em duas camadas à volta de quem lá está sentado? */
export function isLayeredSeat(kind: PropKind): boolean {
  return PROP_DRAWERS[kind].views != null;
}

/**
 * Em quantas imagens se pinta este móvel — uma por linha de chão que ocupa.
 * 0 para a esmagadora maioria, que é uma imagem só e não precisa de saber disto.
 */
export function propSlices(kind: PropKind): number {
  return PROP_DRAWERS[kind].fatias ?? 0;
}

/** Chave da textura da fatia `i` (a de cima é a 0). */
export function propSliceKey(kind: PropKind, i: number): string {
  return `prop:${kind}:${i}`;
}

// ── Jardim / exterior (tira o ar de prisão ao prédio) ───────

export const GRASS = "world:grass";
export const GRASS_ALT = "world:grass:b";
/**
 * Quatro frames de água — o cliente cicla-os para a superfície do lago ONDULAR.
 *
 * Deixaram de ser o lago normal e passaram a ser o PLANO B dele: quando o sprite
 * pintado (`LAKE_ART`) está lá, o lago é uma imagem única e estes tiles não se
 * desenham. Ficam porque um 404 no Phaser é silencioso, e a alternativa a este
 * plano B era um buraco de relva com uma casa a flutuar em cima.
 */
export const WATER_FRAMES = ["world:water:0", "world:water:1", "world:water:2", "world:water:3"] as const;
/**
 * O lago inteiro numa só imagem, 448×224, posta pelo `officeFurniture.ts`.
 *
 * Não tem desenho em código associado, ao contrário de tudo o resto neste
 * ficheiro, e é de propósito: a moldura tem 100 352 px, e pintá-la à mão daria
 * uma mancha chapada muito pior do que os tiles de água que já existem. O plano B
 * de um lago pintado é o lago em tiles, não um segundo lago pintado.
 */
export const LAKE_ART = "world:lake";
export const TREE_SMALL = "world:tree:s";
export const TREE = "world:tree"; // conífera média (nome curto = o default)
export const TREE_BIG = "world:tree:l";
export const BUSH = "world:bush";
export const BUSH_SMALL = "world:bush:s";
export const HEDGE = "world:hedge"; // sebe-vedação da borda do mapa
export const LEAF = "world:leaf"; // partícula de folha que cai das árvores
export const ROOF = "world:roof"; // a tela do telhado, vista do 1.º andar
export const ROOF_ALT = "world:roof:b";
export const ROOF_SOLAR = "world:roof:solar"; // painel solar, 64×40
export const ROOF_AC = "world:roof:ac"; // ar condicionado, 40×36
export const ROOF_HELI = "world:roof:heli"; // heliponto, 208×208
/**
 * Carreiro de pedra e pedrisco (Recepção → parque).
 *
 * Pinta-se POR CIMA da relva, nunca no lugar dela: o tile recorta-se nos lados
 * que dão para o jardim e é o verde de baixo que aparece no recorte. Daí serem
 * 32 chaves — 16 recortes possíveis × as 2 variantes do xadrez. O traçado é do
 * `space.ts` (`GARDEN_PATH`), o recorte sai do `pathMask` e o desenho é todo do
 * `officePath.ts`.
 */
export function pathKey(mask: number, alt: boolean): string {
  return `world:path:${alt ? "b" : "a"}:${mask}`;
}

/** Tile de relva 16×16 com mosqueado e tufos de erva (2 variantes = xadrez vivo). */
function drawGrass(b: Brush, alt: boolean): void {
  const base = alt ? "#3f7a3e" : "#448241";
  const dk = shade(base, 0.86);
  const hi = shade(base, 1.12);
  b.r(0, 0, 16, 16, base);
  b.dither(0, 0, 16, 16, hi, base);
  const blades: [number, number][] = alt
    ? [
        [2, 4],
        [6, 9],
        [9, 3],
        [12, 11],
        [4, 13],
        [14, 6],
      ]
    : [
        [3, 7],
        [7, 2],
        [10, 10],
        [13, 4],
        [5, 12],
        [1, 9],
      ];
  for (const [x, y] of blades) {
    b.r(x, y, 1, 3, dk);
    b.r(x + 1, y + 1, 1, 2, base);
    b.r(x, y, 1, 1, hi);
  }
}

/**
 * Tile de água 16×16 — um dos quatro frames que o cliente cicla para a superfície
 * ONDULAR. Paleta funda e rica (não o "azul seco" de antes): degradê de profundidade
 * (topo a apanhar luz do céu, fundo escuro) + mosqueado + cristas de onda que mudam
 * de sítio a cada frame → o lago ganha vida e movimento.
 */
function drawWater(b: Brush, frame: number): void {
  // Degradê vertical subtil: superfície mais clara em cima (luz), funda em baixo.
  for (let y = 0; y < 16; y += 1) b.r(0, y, 16, 1, mix("#1c5e76", "#123f59", y / 15));
  b.dither(0, 0, 16, 16, "#175169", "#123f59"); // mosqueado (textura da água)
  b.r(0, 0, 16, 1, "#2f7f94"); // fio de luz na aresta de cima (reflexo do céu)
  // Cristas de onda que "andam": cada frame desloca-as → cintilação animada.
  const glint = "#3f93b4";
  const glintHi = "#66bcd6";
  const trough = "#0c2c42";
  const sets: [number, number, number][][] = [
    [
      [2, 3, 5],
      [9, 6, 4],
      [5, 11, 4],
      [12, 13, 3],
    ],
    [
      [3, 4, 4],
      [10, 7, 4],
      [6, 12, 4],
      [1, 2, 3],
    ],
    [
      [4, 3, 5],
      [8, 8, 4],
      [4, 12, 4],
      [11, 14, 3],
    ],
    [
      [2, 5, 4],
      [11, 6, 3],
      [7, 11, 5],
      [13, 3, 3],
    ],
  ];
  const set = sets[frame % 4] ?? sets[0]!;
  for (const [x, y, w] of set) {
    b.r(x, y, w, 1, glint);
    b.r(x + 1, y + 1, Math.max(1, w - 2), 1, trough); // sombra sob a crista
    b.r(x + 1, y, 1, 1, glintHi); // brilho da crista
  }
}

// ── Conífera de jardim: camadas triangulares, alto contraste (estilo referência).
const CONIFER = {
  bark: "#5b3d22",
  barkHi: "#7c5330",
  barkSh: "#38240f",
  deep: "#0f3a20", // sombra mais funda (separação de camadas)
  dk: "#195029", // corpo sombreado (lado direito)
  md: "#2b7a3c", // corpo
  lt: "#49a457", // lado iluminado (esquerda)
  rim: "#83d67e", // rebordo de luz
} as const;

/**
 * Uma "saia" triangular da conífera: ponta em cima, alarga em baixo. Luz de lado
 * (esquerda clara, direita sombreada) + agulhas mosqueadas + rebordo inferior bem
 * escuro → a camada assenta sobre a seguinte com contraste (o degrau clássico das
 * coníferas de pixel-art). Coordenadas em pixels de canvas.
 */
function coniferTier(b: Brush, cx: number, top: number, h: number, half: number): void {
  for (let i = 0; i < h; i += 1) {
    const t = h > 1 ? i / (h - 1) : 1;
    const hw = Math.max(1, Math.round(1 + (half - 1) * t));
    const y = top + i;
    const left = cx - hw;
    b.r(left, y, hw * 2, 1, CONIFER.md); // corpo
    b.r(cx + Math.round(hw * 0.25), y, Math.ceil(hw * 0.75), 1, CONIFER.dk); // sombra (direita)
    b.r(left, y, Math.max(1, Math.round(hw * 0.5)), 1, CONIFER.lt); // luz (esquerda)
    if (i % 2 === 0) b.r(left, y, Math.max(1, Math.round(hw * 0.2)), 1, CONIFER.rim); // rebordo
    if (hw > 3) {
      b.r(cx - Math.round(hw * 0.3), y, 1, 1, CONIFER.deep); // falha entre agulhas
      if (i % 3 === 0) b.r(cx + Math.round(hw * 0.15), y, 1, 1, CONIFER.lt); // ponta iluminada
    }
  }
  b.r(cx - half, top + h - 1, half * 2, 1, CONIFER.deep); // rebordo inferior escuro
  b.r(cx - half - 1, top + h - 2, 1, 1, CONIFER.md); // agulhas a espreitar
  b.r(cx + half, top + h - 2, 1, 1, CONIFER.dk);
}

/**
 * Conífera completa numa grelha W×H (pixels de canvas): tronco curto + N saias
 * triangulares sobrepostas. `tiers` controla a densidade da folhagem; W/H a silhueta
 * — é assim que nascem tamanhos diferentes (pequena/média/grande) da MESMA função.
 */
function drawConifer(b: Brush, W: number, H: number, tiers: number): void {
  const cx = Math.floor(W / 2);
  const tw = Math.max(2, Math.round(W * 0.13));
  const th = Math.max(3, Math.round(H * 0.14));
  const ty = H - th;
  const tx = cx - Math.floor(tw / 2);
  b.r(tx, ty, tw, th, CONIFER.bark);
  b.r(tx, ty, 1, th, CONIFER.barkHi);
  b.r(tx + tw - 1, ty, 1, th, CONIFER.barkSh);
  b.r(tx - 1, H - 2, 2, 2, CONIFER.barkSh); // raízes
  b.r(tx + tw - 1, H - 2, 2, 2, CONIFER.barkSh);

  const folTop = 1;
  const folBot = ty + 2; // sobrepõe um pouco ao topo do tronco
  const folH = folBot - folTop;
  const maxHalf = Math.floor(W / 2) - 1;
  const minHalf = Math.max(2, Math.round(maxHalf * 0.34));
  const tierH = Math.max(3, Math.round(folH * 0.52));
  for (let idx = 0; idx < tiers; idx += 1) {
    const p = tiers > 1 ? idx / (tiers - 1) : 0;
    const top = Math.round(folTop + (folH - tierH) * p);
    const half = Math.round(minHalf + (maxHalf - minHalf) * p);
    coniferTier(b, cx, top, tierH, half);
  }
  b.r(cx, folTop - 1, 1, 3, CONIFER.md); // ponta do topo
  b.r(cx - 1, folTop, 1, 2, CONIFER.lt);
  b.r(cx, folTop - 1, 1, 1, CONIFER.rim);
}

// ── Arbustos / sebes: folhagem densa de alto contraste. Paleta partilhada.
const SHRUB = {
  g: "#2f7d3e",
  gMid: "#3f9a50",
  gHi: "#69c777",
  gRim: "#94ec8f",
  gSh: "#1d5a2e",
  gDk: "#123f21",
} as const;

/** Sebe densa e frondosa, alto contraste (estilo referência). detail=2 → 64×40. */
function drawBush(b: Brush): void {
  const clusters: [number, number, number, number, string][] = [
    [4, 16, 56, 22, SHRUB.gSh], // massa de fundo (sombra larga)
    [2, 12, 26, 24, SHRUB.g], // monte esquerdo
    [30, 11, 30, 25, SHRUB.g], // monte direito
    [14, 5, 24, 20, SHRUB.gMid], // topo central
    [7, 18, 22, 18, SHRUB.gMid],
    [34, 17, 24, 18, SHRUB.gMid],
  ];
  for (const [x, y, w, h, col] of clusters) leafBlob(b, x, y, w, h, col);
  b.r(6, 35, 52, 4, SHRUB.gDk); // sombra de contacto no chão
  b.r(30, 6, 1, 30, SHRUB.gSh); // separações de folhas
  b.r(16, 17, 10, 1, SHRUB.gSh);
  b.r(38, 19, 10, 1, SHRUB.gSh);
  b.r(14, 4, 12, 1, SHRUB.gRim); // rebordo de luz + destaques
  b.r(6, 12, 1, 12, SHRUB.gHi);
  b.r(16, 7, 6, 2, SHRUB.gHi);
  b.r(36, 12, 5, 2, SHRUB.gHi);
  b.dither(20, 11, 18, 8, SHRUB.gHi, SHRUB.gMid); // cintilação de folhas iluminadas
  b.r(46, 20, 6, 6, SHRUB.gDk); // sombra interior baixo-direita
  b.r(18, 15, 2, 2, "#f0d24a"); // bagas / florzinhas para dar vida
  b.r(41, 17, 2, 2, "#f0d24a");
  b.r(28, 23, 2, 2, "#ec7aa8");
  b.r(49, 22, 2, 2, "#e8574f");
}

/** Arbusto redondo pequeno (variedade de tamanho). detail=2 → 40×32. */
function drawBushSmall(b: Brush): void {
  const clusters: [number, number, number, number, string][] = [
    [4, 13, 32, 17, SHRUB.gSh],
    [3, 8, 18, 18, SHRUB.g],
    [19, 9, 17, 17, SHRUB.g],
    [10, 3, 20, 16, SHRUB.gMid],
  ];
  for (const [x, y, w, h, col] of clusters) leafBlob(b, x, y, w, h, col);
  b.r(6, 27, 28, 3, SHRUB.gDk);
  b.r(11, 2, 10, 1, SHRUB.gRim);
  b.r(6, 9, 1, 8, SHRUB.gHi);
  b.dither(13, 7, 14, 7, SHRUB.gHi, SHRUB.gMid);
  b.r(26, 18, 5, 5, SHRUB.gDk);
  b.r(16, 12, 2, 2, "#f0d24a");
  b.r(25, 15, 2, 2, "#ec7aa8");
}

/** Sebe-vedação da borda do mapa: parede de folhagem com altura. detail=2 → 32×44. */
function drawHedge(b: Brush): void {
  b.r(0, 22, 32, 22, SHRUB.gDk); // face sombreada (dá altura à sebe)
  b.dither(0, 26, 32, 16, SHRUB.gSh, SHRUB.gDk);
  b.r(0, 42, 32, 2, "#08240f"); // contacto no chão
  const clusters: [number, number, number, number, string][] = [
    [3, 10, 26, 16, SHRUB.gSh], // coroa de folhagem arredondada
    [1, 6, 16, 18, SHRUB.g],
    [15, 5, 16, 19, SHRUB.g],
    [8, 1, 18, 14, SHRUB.gMid],
    [4, 12, 12, 12, SHRUB.gMid],
    [18, 12, 12, 12, SHRUB.gMid],
  ];
  for (const [x, y, w, h, col] of clusters) leafBlob(b, x, y, w, h, col);
  b.r(8, 0, 12, 1, SHRUB.gRim);
  b.r(3, 6, 1, 12, SHRUB.gHi);
  b.dither(10, 5, 12, 8, SHRUB.gHi, SHRUB.gMid);
  b.r(20, 16, 6, 6, SHRUB.gDk);
}

/** Folha solta (partícula que cai das árvores). detail=2 → 8×8. */
function drawLeaf(b: Brush): void {
  const g = "#4fa055";
  const gHi = "#78cf82";
  const gDk = "#2e6d3a";
  const vein = "#255c31";
  b.r(3, 0, 2, 1, gDk);
  b.r(2, 1, 4, 1, g);
  b.r(1, 2, 6, 2, g);
  b.r(1, 4, 6, 2, g);
  b.r(2, 6, 4, 1, g);
  b.r(3, 7, 2, 1, gDk);
  b.r(1, 2, 3, 1, gHi); // meia iluminada (canto sup-esq)
  b.r(2, 3, 2, 1, gHi);
  b.r(3, 1, 1, 6, vein); // nervura central
  b.r(4, 4, 2, 1, gDk); // sombra (baixo-direita)
}

/** Regista todas as texturas estáticas do mundo, uma vez por cena. */
export function ensureWorldTextures(scene: PhaserT.Scene): void {
  const floors: FloorKind[] = ["wood", "carpet", "lobby", "lounge", "tile", "asphalt", "concrete"];
  for (const kind of floors) {
    texture(scene, floorKey(kind, false), 16, 16, (b) => drawFloor(b, kind, false));
    texture(scene, floorKey(kind, true), 16, 16, (b) => drawFloor(b, kind, true));
  }
  texture(scene, WALL_TOP, 16, 16, drawWallTop);
  texture(scene, WALL_TOP_SLIM, 16, 16, drawWallTopSlim);
  texture(scene, WALL_FACE, 16, 20, drawWallFace);
  texture(scene, WALL_WINDOW, 16, 20, drawWallWindow);
  texture(scene, WALL_DOOR, 16, 16, drawWallDoor, 2); // batente da porta exterior (detail=2)
  texture(scene, DOOR_EXT_SLIDE, 14, 13, drawDoorExtSlide, 2); // meia-folha 28×26 px (detail=2)
  texture(scene, DOOR_HATCH_SLIDE, 14, 16, drawHatchLeaf, 2); // meia-tampa 28×32 px (detail=2)
  texture(scene, DOOR_FRAME, 16, 20, drawDoorFrame, 2); // moldura fixa do vão (detail=2)
  texture(scene, DOOR_SLIDE, 16, 16, drawDoorSlide, 2); // folha de correr, um tile (detail=2)
  texture(scene, DOOR_SIGN, 8, 10, drawDoorSign, 2); // cadeado de sala trancada (detail=2)
  texture(scene, WALL_LAMP, 12, 18, drawWallLamp, 2); // aplique a detail=2
  // Jardim (tiles 16×16 + adereços de cenário).
  texture(scene, GRASS, 16, 16, (b) => drawGrass(b, false));
  texture(scene, GRASS_ALT, 16, 16, (b) => drawGrass(b, true));
  for (let mask = 0; mask < EDGE_COUNT; mask += 1)
    for (const alt of [false, true]) texture(scene, pathKey(mask, alt), 16, 16, (b) => drawGravel(b, alt, mask));
  for (let f = 0; f < WATER_FRAMES.length; f += 1) {
    const key = WATER_FRAMES[f]!;
    texture(scene, key, 16, 16, (b) => drawWater(b, f));
  }
  // Coníferas de três tamanhos (mesma pegada no ecrã que W×0.5, detail=2).
  texture(scene, TREE_SMALL, 24, 40, (b) => drawConifer(b, 48, 80, 4), 2);
  texture(scene, TREE, 32, 52, (b) => drawConifer(b, 64, 104, 5), 2);
  texture(scene, TREE_BIG, 40, 66, (b) => drawConifer(b, 80, 132, 6), 2);
  texture(scene, BUSH, 32, 20, drawBush, 2);
  texture(scene, BUSH_SMALL, 20, 16, drawBushSmall, 2);
  texture(scene, HEDGE, 16, 22, drawHedge, 2);
  texture(scene, LEAF, 4, 4, drawLeaf, 2);
  texture(scene, ROOF, 16, 16, (b) => drawRoof(b, false));
  texture(scene, ROOF_ALT, 16, 16, (b) => drawRoof(b, true));
  texture(scene, ROOF_SOLAR, 32, 20, drawSolarPanel, 2);
  texture(scene, ROOF_AC, 20, 18, drawRoofAc, 2);
  texture(scene, ROOF_HELI, 104, 104, drawHeliponto, 2);
  for (const kind of Object.keys(PROP_DRAWERS) as PropKind[]) {
    const spec = PROP_DRAWERS[kind];
    if (spec.views) {
      // Assento: 4 direcções × 2 camadas. A direita é a esquerda ao espelho.
      const views = spec.views;
      for (const facing of DIRS) {
        for (const layer of SEAT_LAYERS) {
          const drawn: Facing = facing === "right" ? "left" : facing;
          texture(
            scene,
            seatPropKey(kind, facing, layer),
            spec.w,
            spec.h,
            (b) => views(b, drawn, layer),
            spec.detail,
            facing === "right",
          );
        }
      }
      continue;
    }
    // `!== undefined` e não a verdade simples: `fatias` é um número, portanto o
    // ramo falso de um `if (spec.fatias)` continuaria a admitir a variante fatiada
    // com zero fatias — e o `texture` de baixo ficava sem `draw` nenhum.
    if (spec.fatias !== undefined) {
      // Peça funda: uma textura por linha de chão, para cada uma poder entrar na
      // cena com a profundidade da SUA linha (ver `PropArt.fatias`).
      const slice = spec.slice;
      for (let i = 0; i < spec.fatias; i++) {
        texture(scene, propSliceKey(kind, i), spec.w, spec.h, (b) => slice(b, i), spec.detail);
      }
      continue;
    }
    texture(scene, propKey(kind), spec.w, spec.h, spec.draw, spec.detail, spec.mirror);
  }
}

// ── Personagem ──────────────────────────────────────────────

export interface CharSprite {
  key: string;
  shirt: string;
  hair: string;
  skin: string;
  pants: string;
  /** Escolhido no registo — só muda o cabelo (comprido nas 3 cabeças). */
  female: boolean;
}

interface CharColors {
  skin: string;
  skinSh: string;
  skinHi: string;
  hair: string;
  hairSh: string;
  hairHi: string;
  shirt: string;
  shirtSh: string;
  shirtHi: string;
  pants: string;
  pantsSh: string;
  shoe: string;
  shoeHi: string;
  eye: string;
  ol: string; // outline escuro
  /** Cabelo comprido nas cabeças partilhadas por todas as 9 poses. */
  female: boolean;
}

function colorsFor(sp: CharSprite): CharColors {
  return {
    skin: sp.skin,
    skinSh: shade(sp.skin, 0.82),
    skinHi: shade(sp.skin, 1.08),
    hair: sp.hair,
    hairSh: shade(sp.hair, 0.75),
    hairHi: shade(sp.hair, 1.3),
    shirt: sp.shirt,
    shirtSh: shade(sp.shirt, 0.74),
    shirtHi: shade(sp.shirt, 1.18),
    pants: sp.pants,
    pantsSh: shade(sp.pants, 0.78),
    shoe: "#20242b",
    shoeHi: "#333944",
    eye: "#15181d",
    ol: "#191b21",
    female: sp.female,
  };
}

// Os bonecos são autorados a detail=2 (grelha 32×48, o dobro da densidade) para
// deixarem de ser um "blob padrão": ganham cara desenhada (sobrancelhas, olhos com
// brilho, nariz, boca, bochechas coradas), gola/botões na camisa, mangas com
// antebraço e mão, calças com vinco e sapatos com sola. Luz fixa no canto sup-esq.
const EYE_W = "#f4f6f8"; // branco do olho
const BLUSH = "#e8746a"; // rubor da bochecha

/** Boneco de frente (vira a câmara). `step`: -1 pé esq. à frente, 0 parado, 1 dir. */
function drawDown(b: Brush, step: number, c: CharColors): void {
  const lUp = step < 0 ? 2 : 0;
  const rUp = step > 0 ? 2 : 0;
  b.r(9, 45, 14, 1, c.ol); // sombra de contacto no chão
  // pernas + calças (vinco de luz + entreperna escura)
  b.r(10, 36 - lUp, 5, 9 + lUp, c.pants);
  b.r(10, 36 - lUp, 2, 9 + lUp, c.pantsSh);
  b.r(14, 36 - lUp, 1, 9 + lUp, shade(c.pants, 1.1));
  b.r(17, 36 - rUp, 5, 9 + rUp, c.pants);
  b.r(20, 36 - rUp, 2, 9 + rUp, shade(c.pants, 0.86));
  b.r(17, 36 - rUp, 1, 9 + rUp, shade(c.pants, 1.06));
  b.r(15, 36, 2, 8, shade(c.pants, 0.7)); // entreperna
  // sapatos (biqueira lustrosa + sola)
  b.r(9, 44, 6, 3, c.shoe);
  b.r(17, 44, 6, 3, c.shoe);
  b.r(9, 44, 6, 1, c.shoeHi);
  b.r(17, 44, 6, 1, c.shoeHi);
  b.r(9, 46, 6, 1, shade(c.shoe, 0.6));
  b.r(17, 46, 6, 1, shade(c.shoe, 0.6));
  // tronco: camisa com relevo lateral e bainha
  b.r(8, 19, 16, 18, c.shirt);
  b.r(8, 19, 16, 2, c.shirtHi); // ombros
  b.r(8, 19, 2, 16, c.shirtHi); // flanco esq
  b.r(22, 21, 2, 15, c.shirtSh); // flanco dir
  b.r(8, 34, 16, 3, c.shirtSh); // bainha
  b.dither(9, 30, 14, 4, c.shirt, c.shirtSh); // dobra na barriga
  // gola + decote em V + botões
  b.r(13, 19, 6, 3, c.skinSh); // pescoço
  b.r(12, 19, 8, 1, shade(c.shirt, 0.6)); // linha da gola
  b.r(14, 20, 4, 2, c.skin); // decote (pele)
  b.r(13, 21, 1, 4, c.shirtHi); // aba esq
  b.r(18, 21, 1, 4, c.shirtSh); // aba dir
  b.r(15, 24, 1, 1, shade(c.shirt, 1.4));
  b.r(15, 28, 1, 1, shade(c.shirt, 1.4));
  b.r(15, 32, 1, 1, shade(c.shirt, 1.4));
  // braços: manga + antebraço + mão (baloiçam ao contrário das pernas)
  const laY = 20 + rUp;
  const raY = 20 + lUp;
  b.r(5, laY, 4, 8, c.shirtHi);
  b.r(5, laY, 4, 1, shade(c.shirt, 1.3));
  b.r(5, laY + 8, 4, 4, c.skin);
  b.r(5, laY + 12, 4, 3, c.skinSh); // mão esq
  b.r(23, raY, 4, 8, c.shirtSh);
  b.r(23, raY + 8, 4, 4, c.skinSh);
  b.r(23, raY + 12, 4, 3, shade(c.skin, 0.72)); // mão dir
  drawFaceDown(b, 2, c);
}

/**
 * A cabeça de frente — cabelo, cara e rosto — desenhada a partir de `top` (a
 * linha do cocuruto). Sai daqui em vez de estar colada ao boneco de pé porque o
 * boneco SENTADO tem a mesma cara mais abaixo: uma só fonte de verdade, senão a
 * pessoa mudava de cara ao sentar-se.
 */
function drawFaceDown(b: Brush, top: number, c: CharColors): void {
  const y = top;
  // cabeça
  b.r(9, y + 3, 14, 13, c.skin); // cara
  b.r(9, y + 14, 14, 2, c.skinSh); // mandíbula na sombra
  b.r(21, y + 4, 2, 11, c.skinSh); // face dir sombreada
  b.r(9, y + 3, 2, 11, c.skinHi); // face esq iluminada
  b.r(8, y + 8, 1, 3, c.skinSh); // orelha esq
  b.r(23, y + 8, 1, 3, shade(c.skin, 0.7)); // orelha dir
  // cabelo (calota + franja + patilhas)
  b.r(8, y, 16, 6, c.hair);
  b.r(8, y, 16, 1, c.hairHi); // brilho no cocuruto
  b.r(9, y + 1, 6, 1, c.hairHi); // mecha
  b.r(8, y + 3, 1, 6, c.hair); // patilha esq
  b.r(23, y + 3, 1, 6, c.hairSh); // patilha dir
  b.r(9, y + 4, 14, 2, c.hairSh); // franja sombreia a testa
  b.r(10, y + 3, 2, 1, c.hair);
  b.r(14, y + 3, 2, 1, c.hair);
  b.r(18, y + 3, 2, 1, c.hair);
  // rosto: sobrancelhas, olhos com brilho, nariz, boca, bochechas
  b.r(11, y + 7, 3, 1, c.hairSh);
  b.r(18, y + 7, 3, 1, c.hairSh);
  b.r(11, y + 8, 3, 3, EYE_W);
  b.r(18, y + 8, 3, 3, EYE_W);
  b.r(12, y + 9, 2, 2, c.eye);
  b.r(19, y + 9, 2, 2, c.eye);
  b.r(12, y + 9, 1, 1, "#ffffff");
  b.r(19, y + 9, 1, 1, "#ffffff");
  b.r(15, y + 10, 2, 3, c.skinSh); // nariz
  b.r(15, y + 10, 1, 3, c.skinHi); // aresta do nariz
  b.r(13, y + 13, 6, 1, shade(c.skin, 0.62)); // boca
  b.r(14, y + 14, 4, 1, shade(c.skin, 0.8)); // lábio inferior
  b.r(10, y + 11, 2, 2, mix(c.skin, BLUSH, 0.5)); // bochecha esq
  b.r(20, y + 11, 2, 2, mix(c.skin, BLUSH, 0.4)); // bochecha dir
  if (c.female) {
    // Madeixas compridas a emoldurar a cara até ao queixo, por fora dos olhos.
    b.r(7, y + 3, 2, 13, c.hair); // esq
    b.r(7, y + 3, 1, 13, c.hairHi); // brilho na madeixa esq
    b.r(23, y + 3, 2, 13, c.hairSh); // dir na sombra
    b.r(7, y + 15, 2, 1, c.hairSh); // pontas
    b.r(23, y + 15, 2, 1, shade(c.hair, 0.68));
  }
}

/**
 * A nuca — cabelo, volume e orelhas — a partir de `top`. Mesma razão de existir
 * que `drawFaceDown`: a pose SENTADA de costas usa esta cabeça uns pixels abaixo.
 */
function drawHeadUp(b: Brush, top: number, c: CharColors): void {
  const y = top;
  b.r(13, y + 15, 6, 2, c.skinSh); // pescoço
  b.r(8, y, 16, 15, c.hair);
  b.r(8, y, 16, 1, c.hairHi);
  b.r(8, y, 2, 14, shade(c.hair, 1.12)); // lado esq iluminado
  b.r(22, y + 1, 2, 13, c.hairSh); // lado dir sombreado
  b.r(11, y + 3, 10, 6, c.hairSh); // coroa/remoinho
  b.r(13, y + 4, 6, 3, shade(c.hair, 0.82));
  b.r(10, y + 11, 12, 3, shade(c.hair, 0.9)); // pontas a cair
  b.r(8, y + 8, 1, 3, c.skinSh); // orelha esq
  b.r(23, y + 8, 1, 3, shade(c.skin, 0.7)); // orelha dir
  if (c.female) {
    // Cabelo comprido a cair pelas costas, tapando o pescoço visto de trás.
    b.r(9, y + 15, 14, 4, c.hairSh);
    b.r(9, y + 15, 14, 1, c.hair);
    b.r(9, y + 15, 2, 4, shade(c.hair, 1.06)); // lado esq iluminado
    b.r(11, y + 18, 10, 1, shade(c.hair, 0.72)); // pontas
  }
}

/**
 * A cabeça de perfil virada à ESQUERDA — nariz, olho, nuca — a partir de `top`.
 * A pose SENTADA de lado reaproveita-a: a mesma pessoa, à mesma altura relativa.
 */
function drawHeadSide(b: Brush, top: number, c: CharColors): void {
  const y = top;
  b.r(12, y + 16, 6, 2, c.skinSh); // pescoço
  b.r(9, y + 3, 13, 13, c.skin);
  b.r(9, y + 14, 13, 2, c.skinSh); // queixo
  b.r(9, y + 3, 2, 11, c.skinHi); // frente da cara iluminada
  b.r(7, y + 7, 2, 3, c.skin); // nariz saliente
  b.r(7, y + 9, 1, 1, c.skinSh); // narina
  b.r(8, y + 11, 2, 1, shade(c.skin, 0.62)); // boca
  b.r(9, y + 13, 1, 1, c.skinSh); // queixo
  // cabelo de perfil
  b.r(9, y, 14, 6, c.hair);
  b.r(9, y, 14, 1, c.hairHi);
  b.r(19, y + 2, 4, 12, c.hair); // nuca comprida
  b.r(19, y + 2, 1, 12, c.hairSh);
  b.r(9, y + 4, 12, 2, c.hairSh); // franja
  b.r(10, y + 3, 2, 1, c.hairHi);
  // orelha
  b.r(15, y + 8, 2, 3, c.skinSh);
  b.r(15, y + 8, 2, 1, c.skinHi);
  // olho de perfil + sobrancelha
  b.r(11, y + 7, 3, 1, c.hairSh);
  b.r(11, y + 8, 3, 3, EYE_W);
  b.r(11, y + 9, 2, 2, c.eye);
  b.r(11, y + 9, 1, 1, "#ffffff");
  if (c.female) {
    // Nuca de perfil ainda mais comprida a cair até aos ombros.
    b.r(19, y + 14, 4, 5, c.hair);
    b.r(19, y + 14, 1, 5, c.hairSh);
    b.r(20, y + 18, 3, 1, shade(c.hair, 0.72)); // pontas
  }
}

/** Boneco de costas — a mesma silhueta, mas o cabelo tapa a cabeça toda. */
function drawUp(b: Brush, step: number, c: CharColors): void {
  const lUp = step < 0 ? 2 : 0;
  const rUp = step > 0 ? 2 : 0;
  b.r(9, 45, 14, 1, c.ol);
  b.r(10, 36 - lUp, 5, 9 + lUp, c.pants);
  b.r(10, 36 - lUp, 2, 9 + lUp, c.pantsSh);
  b.r(17, 36 - rUp, 5, 9 + rUp, c.pants);
  b.r(20, 36 - rUp, 2, 9 + rUp, shade(c.pants, 0.86));
  b.r(15, 36, 2, 8, shade(c.pants, 0.7));
  b.r(9, 44, 6, 3, c.shoe);
  b.r(17, 44, 6, 3, c.shoe);
  b.r(9, 44, 6, 1, c.shoeHi);
  b.r(17, 44, 6, 1, c.shoeHi);
  // tronco de costas (costura central)
  b.r(8, 19, 16, 18, c.shirt);
  b.r(8, 19, 16, 2, c.shirtHi);
  b.r(8, 19, 2, 16, c.shirtHi);
  b.r(22, 21, 2, 15, c.shirtSh);
  b.r(8, 34, 16, 3, c.shirtSh);
  b.r(15, 20, 2, 16, shade(c.shirt, 0.9)); // costura das costas
  b.dither(9, 30, 14, 4, c.shirt, c.shirtSh);
  b.r(12, 19, 8, 2, shade(c.shirt, 1.15)); // gola vista de trás
  const laY = 20 + rUp;
  const raY = 20 + lUp;
  b.r(5, laY, 4, 8, c.shirtHi);
  b.r(5, laY + 8, 4, 4, c.skin);
  b.r(5, laY + 12, 4, 3, c.skinSh);
  b.r(23, raY, 4, 8, c.shirtSh);
  b.r(23, raY + 8, 4, 4, c.skinSh);
  b.r(23, raY + 12, 4, 3, shade(c.skin, 0.72));
  drawHeadUp(b, 3, c);
}

/** Boneco de perfil, virado à ESQUERDA (o direito é este espelhado). */
function drawSide(b: Brush, step: number, c: CharColors): void {
  const front = step < 0 ? 2 : 0; // perna da frente avança
  const back = step > 0 ? 2 : 0;
  b.r(8, 45, 14, 1, c.ol);
  // pernas (frente clara, trás sombreada)
  b.r(11 - front, 36, 5, 9 + front, c.pants);
  b.r(11 - front, 36, 2, 9 + front, shade(c.pants, 1.06));
  b.r(15 + back, 36, 5, 9 + back, c.pantsSh);
  // sapatos (biqueira à esquerda)
  b.r(7 - front, 44, 7, 3, c.shoe);
  b.r(7 - front, 44, 7, 1, c.shoeHi);
  b.r(15 + back, 44, 7, 3, shade(c.shoe, 0.82));
  // tronco de perfil
  b.r(10, 19, 12, 18, c.shirt);
  b.r(10, 19, 12, 2, c.shirtHi);
  b.r(10, 19, 2, 17, c.shirtHi); // peito (frente) iluminado
  b.r(20, 21, 2, 15, c.shirtSh); // costas na sombra
  b.r(10, 34, 12, 3, c.shirtSh);
  b.dither(11, 30, 10, 4, c.shirt, c.shirtSh);
  // braço que baloiça à frente do peito
  const aY = 21 + back;
  b.r(9, aY, 4, 9, shade(c.shirt, 0.96));
  b.r(9, aY, 4, 1, shade(c.shirt, 1.2));
  b.r(9, aY + 9, 4, 4, c.skin); // mão
  drawHeadSide(b, 2, c);
}

/** Linha das ancas na grelha 32×48 do boneco sentado (o resto é conta). */
const SIT_HIP = 39;

/**
 * Boneco SENTADO, DE FRENTE — a pose de quem se senta a olhar para a câmara.
 *
 * Há uma pose por direcção, e não uma só, porque uma cadeira encostada à mesa
 * diz para onde a pessoa olha: com uma única pose virada a sul, quem se sentava
 * do lado NORTE da mesa de reunião ficava de costas para ela. As quatro poses
 * partilham a mesma linha de ancas (`SIT_HIP`) e a mesma cabeça das poses de pé
 * — sentar-se muda a postura, não a pessoa.
 *
 * A leitura de "sentado" faz-se pela silhueta, não por truques: a cabeça desce
 * (o corpo encolhe ~6px), o tronco encurta contra o encosto, as coxas viram uma
 * faixa HORIZONTAL encurtada pela perspectiva e as mãos pousam nelas. A linha
 * das ancas fica a `SIT_HIP` do fundo da imagem — é esse o número que encaixa o
 * rabo na almofada (ver `seatDropFor`).
 */
function drawSit(b: Brush, c: CharColors): void {
  // coxas na horizontal (a perspectiva encurta-as) — é isto que "assenta" o corpo
  b.r(8, SIT_HIP, 16, 5, c.pants);
  b.r(8, SIT_HIP, 16, 1, shade(c.pants, 1.12)); // luz no topo das coxas
  b.r(15, SIT_HIP, 2, 5, shade(c.pants, 0.7)); // entreperna
  b.r(8, SIT_HIP + 4, 16, 1, c.pantsSh); // dobra do joelho
  // canelas a cair do joelho + sapatos
  b.r(10, 43, 5, 4, c.pantsSh);
  b.r(17, 43, 5, 4, c.pantsSh);
  b.r(9, 45, 6, 3, c.shoe);
  b.r(17, 45, 6, 3, c.shoe);
  b.r(9, 45, 6, 1, c.shoeHi);
  b.r(17, 45, 6, 1, c.shoeHi);
  // tronco encostado: mais curto que de pé, bainha comprimida contra o assento
  b.r(8, 25, 16, 16, c.shirt);
  b.r(8, 25, 16, 2, c.shirtHi); // ombros
  b.r(8, 25, 2, 14, c.shirtHi); // flanco esq
  b.r(22, 27, 2, 13, c.shirtSh); // flanco dir
  b.r(8, 38, 16, 3, c.shirtSh); // bainha
  b.dither(9, 34, 14, 4, c.shirt, c.shirtSh);
  // gola + decote em V + botões (os mesmos da pose de pé, comprimidos)
  b.r(13, 25, 6, 3, c.skinSh); // pescoço
  b.r(12, 25, 8, 1, shade(c.shirt, 0.6)); // linha da gola
  b.r(14, 26, 4, 2, c.skin); // decote
  b.r(13, 27, 1, 4, c.shirtHi);
  b.r(18, 27, 1, 4, c.shirtSh);
  b.r(15, 30, 1, 1, shade(c.shirt, 1.4));
  b.r(15, 34, 1, 1, shade(c.shirt, 1.4));
  // braços a descer e as mãos pousadas nas coxas (por cima do colo já pintado)
  b.r(5, 26, 4, 9, c.shirtHi);
  b.r(5, 26, 4, 1, shade(c.shirt, 1.3));
  b.r(5, 35, 4, 3, c.skin); // antebraço esq
  b.r(6, 38, 5, 3, c.skinSh); // mão esq na coxa
  b.r(23, 26, 4, 9, c.shirtSh);
  b.r(23, 35, 4, 3, c.skinSh); // antebraço dir
  b.r(21, 38, 5, 3, shade(c.skin, 0.72)); // mão dir na coxa
  drawFaceDown(b, 8, c); // mesma cara, 6px mais abaixo
}

/**
 * Boneco SENTADO de costas — quem está do lado de cá da mesa, a olhar para lá.
 *
 * Não há pés nesta pose, e é de propósito: as pernas seguem para LONGE da câmara
 * e desaparecem atrás do próprio corpo. Desenhar canelas aqui era o erro clássico
 * de espelhar a pose de frente — ficava uma pessoa dobrada ao contrário.
 */
function drawSitUp(b: Brush, c: CharColors): void {
  // assento das calças: a única parte das pernas que se vê de trás
  b.r(8, SIT_HIP - 1, 16, 6, c.pants);
  b.r(8, SIT_HIP - 1, 16, 1, shade(c.pants, 1.1));
  b.r(15, SIT_HIP - 1, 2, 6, shade(c.pants, 0.7)); // costura do fundilho
  b.r(8, SIT_HIP + 4, 16, 1, c.pantsSh);
  // tronco de costas, encostado (costura central, como na pose de pé)
  b.r(8, 25, 16, 16, c.shirt);
  b.r(8, 25, 16, 2, c.shirtHi); // ombros
  b.r(8, 25, 2, 14, c.shirtHi);
  b.r(22, 27, 2, 13, c.shirtSh);
  b.r(8, 38, 16, 3, c.shirtSh); // bainha comprimida contra o assento
  b.r(15, 26, 2, 14, shade(c.shirt, 0.9)); // costura das costas
  b.dither(9, 34, 14, 4, c.shirt, c.shirtSh);
  b.r(12, 25, 8, 2, shade(c.shirt, 1.15)); // gola vista de trás
  // braços caídos, antebraços a apontar para a frente (fora de vista)
  b.r(5, 26, 4, 9, c.shirtHi);
  b.r(5, 26, 4, 1, shade(c.shirt, 1.3));
  b.r(5, 35, 4, 4, c.skin);
  b.r(23, 26, 4, 9, c.shirtSh);
  b.r(23, 35, 4, 4, c.skinSh);
  drawHeadUp(b, 9, c); // mesma nuca, 6px mais abaixo
}

/**
 * Boneco SENTADO de PERFIL, virado à ESQUERDA (o direito é este espelhado).
 *
 * É a pose que mais depende do ângulo: aqui a perna dobra mesmo em L — coxa na
 * horizontal a sair para a frente, canela a cair no joelho — e é essa quebra que
 * se lê como "sentado" de lado. A perna de trás vai primeiro e mais escura, para
 * dar profundidade sem contorno.
 */
function drawSitSide(b: Brush, c: CharColors): void {
  // perna de trás (mais escura, ligeiramente recuada)
  b.r(8, SIT_HIP + 1, 13, 4, shade(c.pants, 0.74));
  b.r(8, 43, 4, 4, shade(c.pants, 0.66));
  b.r(6, 45, 7, 3, shade(c.shoe, 0.72));
  // coxa da frente na horizontal + canela a cair do joelho
  b.r(6, SIT_HIP, 15, 5, c.pants);
  b.r(6, SIT_HIP, 15, 1, shade(c.pants, 1.12)); // luz no topo da coxa
  b.r(6, SIT_HIP + 4, 15, 1, c.pantsSh); // dobra do joelho
  b.r(6, 43, 5, 4, c.pantsSh);
  b.r(4, 45, 8, 3, c.shoe); // biqueira à esquerda, como de pé
  b.r(4, 45, 8, 1, c.shoeHi);
  // tronco de perfil, encostado
  b.r(10, 25, 12, 16, c.shirt);
  b.r(10, 25, 12, 2, c.shirtHi); // ombro
  b.r(10, 25, 2, 14, c.shirtHi); // peito (frente) iluminado
  b.r(20, 27, 2, 13, c.shirtSh); // costas contra o encosto
  b.r(10, 38, 12, 3, c.shirtSh); // bainha
  b.dither(11, 34, 10, 4, c.shirt, c.shirtSh);
  // braço a descer e a mão pousada na coxa
  b.r(11, 27, 4, 9, shade(c.shirt, 0.96));
  b.r(11, 27, 4, 1, shade(c.shirt, 1.2));
  b.r(11, 36, 4, 3, c.skin); // antebraço
  b.r(9, 38, 5, 3, c.skinSh); // mão na coxa
  drawHeadSide(b, 8, c); // mesmo perfil, 6px mais abaixo
}

/**
 * Quantos pixels o sprite sentado desce para o rabo assentar na almofada de cada
 * móvel. Conta, não gosto: o contentor do avatar fica no CENTRO do tile (16px
 * acima da base do móvel), as ancas estão a `48 - SIT_HIP` do fundo da imagem, e
 * cada almofada tem a sua altura própria — medida no desenho do próprio móvel.
 */
const CUSHION_TOP: Partial<Record<PropKind, number>> = {
  officeChair: 13, // `drawOfficeChair`: almofada em y=27 na grelha 32×40
  meetingChair: 15, // `drawMeetingChair`: y=21 em 32×36
  diningChair: 14, // `drawDiningChair`: y=22 em 32×36
  // 96×46 desde que o sofá passou a vir do render do pacote. A almofada do
  // assento começa em y=20 → 46−20 = 26. Não é um ajuste de gosto: mediu-se o
  // perfil de luminância do próprio PNG, onde a costura escura entre encosto e
  // assento cai em y=19 e a almofada clara ocupa y=20..28.
  couch: 26,
  // O mesmo sofá, logo a MESMA altura de assento — quem se senta no de perfil não
  // pode ficar um dedo mais alto do que quem se senta no de frente, é a mesma
  // peça. Na fatia de 60 px o valor cai em y=34, e as três almofadas do composto
  // (13,8..45 · 45..77 · 77..108,6) recebem-no a 0,65 / 0,66 / 0,66 da sua altura:
  // os três lugares assentam à mesma profundidade da almofada, não em degraus.
  // Foi esta conta que fixou a fatia em 40×60 — ver `PROP_DRAWERS.couchSide`.
  couchSide: 26,
};

/**
 * Correcção só para a cadeira de escritório: o encosto de rede é ALTO.
 *
 * As cadeiras viradas a norte partem-se em duas texturas — `base` (assento+rodas,
 * atrás do avatar) e `front` (encosto+braços, POR CIMA). No PNG de 32×40 da
 * `officeChair` o `front` é opaco em toda a faixa y=0..23: um encosto que sobe até
 * ao topo da moldura. Com o `seatDropFor` só a contar a almofada (13), o sentado
 * descia 12px e o encosto engolia-lhe o tronco e a cabeça — via-se só o cocuruto
 * (bug reportado). As outras cadeiras (meeting/dining) têm encosto mais baixo e a
 * cabeça já saía; por isso o lift é ZERO para elas.
 *
 * Subimos o sentado 8px: o fundo das calças (frame y=44) assenta exactamente no
 * topo da almofada visível (worldY −16) e a cabeça inteira passa a limpar o topo
 * do encosto — sentado, não escondido. O tronco continua atrás da rede, custo
 * visual nulo. Medido no próprio PNG (bandas opacas front y=0..23, base y=24..39).
 */
const BACKREST_LIFT: Partial<Record<PropKind, number>> = {
  officeChair: 8,
};

/**
 * A linha das ancas na ARTE sentada — a do pacote, não a do boneco desenhado.
 *
 * Medida nas seis folhas de perfil (as de frente e de costas não têm queda de
 * silhueta para medir): 32 nele, 32 nela. O boneco DESENHADO tem-nas em
 * `SIT_HIP` = 39, porque a pose dele recolhe as pernas; a arte tem-nas
 * penduradas, e por isso o rabo fica sete pixels mais acima no mesmo quadro.
 *
 * Enquanto o `seatDropFor` usou 39 para as duas, quem tinha arte sentava-se SETE
 * PIXELS ACIMA da almofada — com os pés em cima do sofá. Via-se sobretudo nos
 * sofás, onde não há encosto a tapar o engano.
 */
export const SIT_HIP_ARTE = 32;

/**
 * Os assentos onde a anca da ARTE conta — só os sofás.
 *
 * A almofada dos sofás (`CUSHION_TOP.couch`/`couchSide` = 26) foi MEDIDA no
 * PNG, por isso pousar lá a arte exige a anca da arte. As cadeiras não: a altura
 * delas (`CUSHION_TOP` + `BACKREST_LIFT`) foi afinada a olho em agosto, com a
 * arte sentada do rapaz já no ecrã e o `seatDropFor` a contar 39 — a diferença
 * de sete pixels já lá está dentro. Dar-lhes também a anca da arte descia-as
 * SETE PIXELS outra vez, e o boneco afundava abaixo do assento, de lado e de
 * costas (visto a 2026-09-15, nos dois personagens).
 */
const ANCA_DA_ARTE_EM = new Set<PropKind>(["couch", "couchSide"]);

/** A anca com que se pousa uma pose sentada neste móvel. */
export function ancaSentada(kind: PropKind, deArte: boolean): number {
  return deArte && ANCA_DA_ARTE_EM.has(kind) ? SIT_HIP_ARTE : SIT_HIP;
}

/** As chaves sentadas que foram preenchidas com ARTE. O desenho em código e a
 *  arte têm ancas em linhas diferentes, e é preciso saber qual está na chave
 *  para a pousar na almofada — a mesma conta serve as duas com o número certo. */
const sentadasDeArte = new Set<string>();

/** Chamado por quem reclama uma chave sentada com uma folha do pacote. */
export function marcarSentadoDeArte(tex: string): void {
  sentadasDeArte.add(tex);
}

/** Esta chave sentada veio de arte (`true`) ou do boneco desenhado (`false`)? */
export function sentadoEDeArte(tex: string): boolean {
  return sentadasDeArte.has(tex);
}

export function seatDropFor(kind: PropKind, anca: number = SIT_HIP): number {
  return TILE / 2 + (48 - anca) - (CUSHION_TOP[kind] ?? TILE / 2) - (BACKREST_LIFT[kind] ?? 0);
}

/** Chave da textura do boneco sentado, por direcção — quem se senta olha a mesa. */
export function sitKey(key: string, facing: Facing): string {
  return `char:${key}:sit:${facing}`;
}

/** Chave da textura de um frame do boneco. `frame`: 0 esq · 1 parado · 2 dir. */
export function frameKey(key: string, dir: string, frame: number): string {
  return `char:${key}:${dir}:${frame}`;
}

/**
 * Deriva o visual de uma pessoa a partir do id — determinístico e estável:
 * a mesma pessoa tem sempre a mesma cara, camisola e cabelo, aqui e no chat.
 */
export function spriteFor(id: string, gender: Gender): CharSprite {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  const si = hash % SHIRTS.length;
  const hi = (hash >>> 3) % HAIRS.length;
  const ki = (hash >>> 6) % SKINS.length;
  const pi = (hash >>> 9) % PANTS.length;
  // O género entra na chave da textura: sem isto, um "João" masc. e uma "Joana"
  // fem. com os mesmos índices de cor partilhavam a mesma cache e o segundo a
  // entrar via com o cabelo do primeiro.
  return {
    key: `${gender}_${si}_${hi}_${ki}_${pi}`,
    shirt: SHIRTS[si] ?? SHIRTS[0],
    hair: HAIRS[hi] ?? HAIRS[0],
    skin: SKINS[ki] ?? SKINS[0],
    pants: PANTS[pi] ?? PANTS[0],
    female: gender === "FEMALE",
  };
}

/** Regista os 16 frames (4 direcções × 3 passos + 4 sentado) de um boneco, uma vez por visual. */
export function ensureCharTextures(scene: PhaserT.Scene, sp: CharSprite): void {
  const c = colorsFor(sp);
  const steps = [-1, 0, 1];
  for (const dir of DIRS) {
    for (let fi = 0; fi < steps.length; fi += 1) {
      const tex = frameKey(sp.key, dir, fi);
      if (scene.textures.exists(tex)) continue;
      const canvas = document.createElement("canvas");
      canvas.width = 16 * S;
      canvas.height = 24 * S;
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      ctx.imageSmoothingEnabled = false;
      if (dir === "right") {
        ctx.translate(16 * S, 0);
        ctx.scale(-1, 1);
      }
      // detail=2: pinta em células S/2 numa grelha 32×48 → mesma pegada, dobro do
      // detalhe. O espelho fica em 16*S (a largura do canvas) — invariante à densidade.
      const brush = makeBrush(ctx, S / 2);
      const step = steps[fi] ?? 0;
      if (dir === "down") drawDown(brush, step, c);
      else if (dir === "up") drawUp(brush, step, c);
      else drawSide(brush, step, c); // left e right (right vem espelhado pelo ctx)
      scene.textures.addCanvas(tex, canvas);
    }
  }
  // As quatro poses sentadas — uma por direcção, pelo mesmo processo (e `right`
  // continua a ser `left` espelhado pelo contexto, sem segundo desenho a manter).
  for (const dir of DIRS) {
    const tex = sitKey(sp.key, dir);
    if (scene.textures.exists(tex)) continue;
    const canvas = document.createElement("canvas");
    canvas.width = 16 * S;
    canvas.height = 24 * S;
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;
    ctx.imageSmoothingEnabled = false;
    if (dir === "right") {
      ctx.translate(16 * S, 0);
      ctx.scale(-1, 1);
    }
    const brush = makeBrush(ctx, S / 2);
    if (dir === "down") drawSit(brush, c);
    else if (dir === "up") drawSitUp(brush, c);
    else drawSitSide(brush, c);
    scene.textures.addCanvas(tex, canvas);
  }
}
